

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import imageCompression from 'browser-image-compression';
import heic2any from 'heic2any';
import { Document, Page, Text, View, StyleSheet, pdf, Image, Svg, Path, Circle, Rect, Ellipse } from '@react-pdf/renderer';

// ==================================================================================
// IMPORTAÇÃO DA API CENTRALIZADA
// ==================================================================================
import api from '../../services/api';
import labolensLogoPng from './labolens-logo.png';

// ==================================================================================
// CONSTANTES & TEMA
// ==================================================================================
const STATUS_PERMITIDOS = ['Pedido da lente', 'Pedido realizado', 'No laboratório', 'Pronto na loja', 'Entregue'];
const FORMATOS_ARMACAO = ['Redondo', 'Quadrado', 'Retangular', 'Oval', 'Aviador', 'Gatinho'];

const Icons = {
  Calendar: () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>,
  Download: () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>,
  TrendingUp: () => <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>,
  Users: () => <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle></svg>,
  Receipt: () => <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line></svg>,
  Search: () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>,
  ChevronDown: () => <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"></polyline></svg>,
  Camera: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>,
  Edit: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>,
  Save: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>,
  Close: () => <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>,
  Eye: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>,
  Dollar: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>,
  Alert: () => <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>,
  List: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>,
  Trophy: () => <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path d="M8 21h8m-4-9v9m-8-3h16M4 4h16c1.1 0 2 .9 2 2v1c0 2.2-1.8 4-4 4h-2l-2 6-2-6H6c-2.2 0-4-1.8-4-4V6c0-1.1.9-2 2-2z"/></svg>,
  MoreVertical: () => <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><circle cx="12" cy="12" r="1"></circle><circle cx="12" cy="5" r="1"></circle><circle cx="12" cy="19" r="1"></circle></svg>,
  FileText: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>,
  Loader: () => <span className="mini-loader" aria-hidden="true" />,
  Xml: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><path d="M14 2v6h6"></path><path d="m9 13-2 2 2 2"></path><path d="m15 13 2 2-2 2"></path><path d="m13 12-2 6"></path></svg>,
  CheckCircle: () => <svg width="14" height="14" fill="none" stroke="#10b981" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>,
  Store: () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 21h18v-8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><path d="M7 8V6a5 5 0 0 1 10 0v2"></path></svg>,
  CheckSquare: () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>,
  Square: () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect></svg>,
  User: () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>,
  Wallet: () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"></path><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"></path><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"></path></svg>,
  Trash: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>,
  UploadCloud: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="16 16 12 12 8 16"></polyline><line x1="12" y1="12" x2="12" y2="21"></line><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"></path><polyline points="16 16 12 12 8 16"></polyline></svg>,
  Refresh: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.59-9.21L21.5 8"></path></svg>,
  FilterOff: () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>,
  Plus: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>,
  MessageCircle: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>,
  Send: () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>,
  
  // Ícones dos Formatos (Web)
  ShapeRound: () => <svg width="20" height="10" viewBox="0 0 40 20" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="10" cy="10" r="7"/><circle cx="30" cy="10" r="7"/><path d="M17 10h6"/></svg>,
  ShapeSquare: () => <svg width="20" height="10" viewBox="0 0 40 20" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="3" width="14" height="14" rx="2"/><rect x="23" y="3" width="14" height="14" rx="2"/><path d="M17 10h6"/></svg>,
  ShapeRect: () => <svg width="20" height="10" viewBox="0 0 40 20" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="2" y="5" width="16" height="10" rx="2"/><rect x="22" y="5" width="16" height="10" rx="2"/><path d="M18 10h4"/></svg>,
  ShapeOval: () => <svg width="20" height="10" viewBox="0 0 40 20" fill="none" stroke="currentColor" strokeWidth="2.5"><ellipse cx="10" cy="10" rx="8" ry="6"/><ellipse cx="30" cy="10" rx="8" ry="6"/><path d="M18 10h4"/></svg>,
  ShapeAviator: () => <svg width="20" height="10" viewBox="0 0 40 20" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 8c0-3 3-5 7-5 5 0 7 5 7 9 0 4-4 5-7 5-4 0-7-2-7-9z"/><path d="M23 8c0-3 3-5 7-5 5 0 7 5 7 9 0 4-4 5-7 5-4 0-7-2-7-9z"/><path d="M17 6h6"/></svg>,
  ShapeCat: () => <svg width="20" height="10" viewBox="0 0 40 20" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M2 6l5-4c4 0 9 4 9 9 0 4-4 5-7 5-4 0-7-3-7-10z"/><path d="M22 6l5-4c4 0 9 4 9 9 0 4-4 5-7 5-4 0-7-3-7-10z"/><path d="M16 8h8"/></svg>
};

// ==================================================================================
// HELPERS, FORMATADORES E INTELIGÊNCIA DE MAPEAMENTO
// ==================================================================================
const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);

const formatDateLocal = (dateString) => {
  if (!dateString) return 'Não informada';
  try {
    const [year, month, day] = dateString.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
  } catch (e) { return dateString; }
};

const getDateISOForFilter = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) return '';

    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

const safeRenderName = (objOrStr) => {
  if (!objOrStr) return '';
  if (typeof objOrStr === 'string') return objOrStr;
  if (typeof objOrStr === 'object' && objOrStr.nome) return objOrStr.nome;
  return '';
};

const formatImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('data:image') || url.startsWith('blob:')) return url;
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${api.defaults.baseURL || window.location.origin}${cleanUrl}`;
};

const isImagemProtegidaOS = (url = '') => {
  const valor = String(url || '').trim().replace(/\\/g, '/').replace(/^\/+/, '');
  return valor.startsWith('pilotos/anexos/os/');
};

const criarUrlImagemProtegida = async (src) => {
  if (!src) return '';

  if (
    String(src).startsWith('data:image') ||
    String(src).startsWith('blob:') ||
    String(src).startsWith('http://') ||
    String(src).startsWith('https://') ||
    !isImagemProtegidaOS(src)
  ) {
    return formatImageUrl(src);
  }

  const response = await api.get('/ordens-servico/anexo-imagem', {
    params: { path: src },
    responseType: 'blob'
  });

  return URL.createObjectURL(response.data);
};

function ProtectedImage({ src, alt = 'Imagem protegida', className = '', style = {}, onClick }) {
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let ativo = true;
    let objectUrlCriada = '';

    async function carregar() {
      try {
        setLoading(true);
        setError(false);
        setImageUrl('');

        if (!src) {
          setLoading(false);
          return;
        }

        const url = await criarUrlImagemProtegida(src);

        if (isImagemProtegidaOS(src) && url?.startsWith('blob:')) {
          objectUrlCriada = url;
        }

        if (ativo) {
          setImageUrl(url);
        }
      } catch (err) {
        console.error('[ProtectedImage] Erro ao carregar imagem protegida:', err);
        if (ativo) {
          setError(true);
        }
      } finally {
        if (ativo) {
          setLoading(false);
        }
      }
    }

    carregar();

    return () => {
      ativo = false;
      if (objectUrlCriada) {
        URL.revokeObjectURL(objectUrlCriada);
      }
    };
  }, [src]);

  if (loading) {
    return (
      <div className={className} style={{ ...style, display: 'grid', placeItems: 'center', background: '#f8fafc', color: '#64748b', fontSize: 11, fontWeight: 700 }}>
        Carregando...
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div className={className} style={{ ...style, display: 'grid', placeItems: 'center', background: '#fff7ed', color: '#c2410c', fontSize: 11, fontWeight: 800, textAlign: 'center', padding: 8 }}>
        Não foi possível carregar
      </div>
    );
  }

  return <img src={imageUrl} alt={alt} className={className} style={style} onClick={onClick} />;
}

const formatPhone = (phoneStr) => {
  if (!phoneStr) return '';
  return phoneStr.replace(/\D/g, ''); 
};

// --- Multiempresa: resolução oficial da empresa da OS ---
const normalizarId = (valor) => {
  if (!valor) return '';
  if (typeof valor === 'object') return String(valor._id || valor.id || '');
  return String(valor);
};

const resolverArrayApi = (data, fallbackKey = '') => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (fallbackKey && Array.isArray(data?.[fallbackKey])) return data[fallbackKey];
  if (Array.isArray(data?.empresas)) return data.empresas;
  if (Array.isArray(data?.ordens)) return data.ordens;
  if (Array.isArray(data?.ordensServico)) return data.ordensServico;
  if (Array.isArray(data?.users)) return data.users;
  if (Array.isArray(data?.usuarios)) return data.usuarios;
  return [];
};

const resolverPaginacaoApi = (data, paginaAtual = 1, limiteAtual = 10, quantidadeRecebida = 0) => {
  const respostaEhArray = Array.isArray(data);
  const meta = data?.pagination || data?.paginacao || data?.meta || (respostaEhArray ? {} : data) || {};
  const possuiTotalExplicito = [meta.total, meta.totalItems, meta.totalRegistros, meta.count, data?.total]
    .some((valor) => valor !== undefined && valor !== null && valor !== '');
  const pagina = Number(meta.page ?? meta.pagina ?? meta.currentPage ?? paginaAtual) || paginaAtual;
  const limite = Number(meta.limit ?? meta.limite ?? meta.perPage ?? meta.pageSize ?? limiteAtual) || limiteAtual;
  const totalExplicito = Number(meta.total ?? meta.totalItems ?? meta.totalRegistros ?? meta.count ?? data?.total);
  const temMaisPorTamanho = quantidadeRecebida >= limite;
  const total = possuiTotalExplicito
    ? totalExplicito
    : Math.max(quantidadeRecebida, ((pagina - 1) * limite) + quantidadeRecebida + (temMaisPorTamanho ? 1 : 0));
  const totalPaginasInformado = Number(meta.totalPages ?? meta.totalPaginas ?? meta.pages);
  const totalPaginas = totalPaginasInformado || Math.max(1, Math.ceil(total / limite));
  const temProximaInformado = meta.hasNextPage ?? meta.temProximaPagina ?? meta.hasNext;
  const temAnteriorInformado = meta.hasPreviousPage ?? meta.temPaginaAnterior ?? meta.hasPrev;

  return {
    total,
    pagina,
    limite,
    totalPaginas,
    totalConhecido: possuiTotalExplicito || totalPaginasInformado > 0,
    temProxima: typeof temProximaInformado === 'boolean'
      ? temProximaInformado
      : (possuiTotalExplicito || totalPaginasInformado ? pagina < totalPaginas : temMaisPorTamanho),
    temAnterior: typeof temAnteriorInformado === 'boolean' ? temAnteriorInformado : pagina > 1
  };
};

const montarParametrosRelatorio = ({ filtros, pagina, limite }) => {
  const params = {
    page: pagina,
    pagina,
    limit: limite,
    limite,
    porPagina: limite,
    sort: '-dataCompra',
    ordenarPor: 'dataCompra',
    ordem: 'desc'
  };

  const temBuscaDireta = Boolean(filtros.busca?.trim());

  if (temBuscaDireta) {
    /*
      Busca global:
      quando o usuário digita cliente, paciente, número da OS, CV ou telefone,
      não limitamos a consulta ao mês exibido nos atalhos.
      Assim uma OS antiga continua aparecendo imediatamente.
    */
    params.busca = filtros.busca.trim();
    params.buscaGlobal = 'true';
    params.ignorarPeriodo = 'true';
  } else {
    if (filtros.dataInicio) params.dataInicio = filtros.dataInicio;
    if (filtros.dataFim) params.dataFim = filtros.dataFim;
  }

  if (filtros.loja && filtros.loja !== 'Todas') params.empresaId = filtros.loja;
  if (filtros.status && filtros.status !== 'Todos') params.status = filtros.status;
  if (filtros.vendedor && filtros.vendedor !== 'Todos') params.vendedor = filtros.vendedor;
  if (filtros.financeiro && filtros.financeiro !== 'Todos') params.financeiro = filtros.financeiro;

  return params;
};

const normalizarTextoBusca = (valor = '') =>
  String(valor || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();

const getEmpresaIdOS = (os = {}) => normalizarId(
  os.empresaId ||
    os.empresa?._id ||
    os.empresa?.id ||
    os.empresa ||
    ''
);

const getEmpresaCodigo = (empresa = {}) =>
  String(empresa.codigoLoja || empresa.loja || empresa.slug || empresa._id || empresa.id || '').trim();

const getEmpresaLabel = (empresa = {}) => {
  const nome = empresa.nome || empresa.razaoSocial || 'Empresa';
  const unidade = empresa.unidade && empresa.unidade !== 'Matriz' ? ` · ${empresa.unidade}` : '';
  return `${nome}${unidade}`;
};

const empresaFiscalPronta = (empresa = {}) => {
  const fiscal = empresa.fiscal || {};

  return Boolean(
    fiscal.nfeioCompanyId &&
      (fiscal.nfeioApiKeyConfigurada || fiscal.nfeioApiKey) &&
      fiscal.cscId &&
      fiscal.cscCodigo &&
      fiscal.regimeTributario &&
      fiscal.certificadoA1Configurado
  );
};

const getEmpresaFiscalPendencias = (empresa = {}) => {
  const fiscal = empresa.fiscal || {};
  const pendencias = [];

  if (!empresa.cnpj) pendencias.push('CNPJ');
  if (!fiscal.nfeioCompanyId) pendencias.push('Company ID');
  if (!fiscal.nfeioApiKeyConfigurada && !fiscal.nfeioApiKey) pendencias.push('API Key');
  if (!fiscal.regimeTributario) pendencias.push('Regime tributário');
  if (!fiscal.cscId) pendencias.push('CSC ID');
  if (!fiscal.cscCodigo) pendencias.push('Código CSC');
  if (!fiscal.certificadoA1Configurado) pendencias.push('Certificado A1');

  return pendencias;
};

const descobrirEmpresaOficial = (osOuLoja, empresasCadastradas = []) => {
  if (!empresasCadastradas.length) return null;

  const os = typeof osOuLoja === 'object' && osOuLoja !== null ? osOuLoja : null;
  const osEmpresaId = os ? getEmpresaIdOS(os) : '';
  const osLojaString = os ? os.loja : osOuLoja;

  if (osEmpresaId) {
    const porId = empresasCadastradas.find((emp) => {
      return normalizarId(emp._id) === osEmpresaId || normalizarId(emp.id) === osEmpresaId;
    });

    if (porId) return porId;
  }

  if (!osLojaString) return null;

  const busca = normalizarTextoBusca(osLojaString);

  const porCodigo = empresasCadastradas.find((emp) => normalizarTextoBusca(emp.codigoLoja) === busca);
  if (porCodigo) return porCodigo;

  const porUnidade = empresasCadastradas.find((emp) => normalizarTextoBusca(emp.unidade) === busca);
  if (porUnidade) return porUnidade;

  const porNome = empresasCadastradas.find((emp) => normalizarTextoBusca(emp.nome) === busca);
  if (porNome) return porNome;

  return (
    empresasCadastradas.find((emp) => {
      const alvo = [
        emp.codigoLoja,
        emp.unidade,
        emp.nome
      ].map(normalizarTextoBusca).join(' ');

      return alvo.includes(busca) || busca.includes(alvo);
    }) || null
  );
};

// --- Mapeamento Inteligente de Vendedores ---
const normalizarNomeVendedor = (nomeBruto) => {
  if (!nomeBruto) return 'Não Informado';
  const n = nomeBruto.toUpperCase().trim();
  if (n.includes('VITOR AFONSO') || n === 'VITOR') return 'Vitor Afonso';
  if (n.includes('ANA BEATRIZ') || n.includes('ANNA BEATRIZ')) return 'Anna Beatriz';
  if (n.includes('ROSILENE')) return 'Rosilene';
  return nomeBruto.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const calcularTotalPago = (os) => {
  if (!os) return 0;

  return Array.isArray(os.pagamentos)
    ? os.pagamentos.reduce((acc, p) => acc + (Number(p.valor) || 0), 0)
    : 0;
};

const calcularRestante = (os) => {
  if (!os) return 0;

  const total = Number(os.valorTotal) || 0;
  const pago = calcularTotalPago(os);
  const restante = total - pago;

  return restante > 0.009 ? Number(restante.toFixed(2)) : 0;
};

const normalizarNumeroEnvio = (phoneStr) => {
  const clean = String(phoneStr || '').replace(/\D/g, '');

  if (!clean) return '';
  if (clean.startsWith('55')) return clean;

  return `55${clean}`;
};

const getEmpresaEnderecoFormatado = (empresa = {}) => {
  const endereco = empresa.endereco || empresa.address || {};

  if (typeof endereco === 'string') {
    return endereco.trim();
  }

  const logradouro = endereco.logradouro || endereco.rua || endereco.endereco || '';
  const numero = endereco.numero || endereco.number || '';
  const bairro = endereco.bairro || '';
  const cidade = endereco.cidade || '';
  const uf = endereco.uf || endereco.estado || '';

  const linha1 = [logradouro, numero].filter(Boolean).join(', ');
  const linha2 = [bairro, cidade, uf].filter(Boolean).join(' - ');

  return [linha1, linha2].filter(Boolean).join(' | ');
};

const getEmpresaContatoWhatsApp = (empresa = {}) => {
  const contato =
    empresa.whatsapp ||
    empresa.telefone ||
    empresa.celular ||
    empresa.contato?.whatsapp ||
    empresa.contato?.telefone ||
    empresa.configWhatsApp?.numero ||
    '';

  if (typeof contato === 'object') {
    return String(contato.numero || contato.phone || contato.telefone || '').trim();
  }

  return String(contato || '').trim();
};

const getCanalWhatsappId = (canal = {}) => normalizarId(
  canal._id ||
    canal.id ||
    canal.canalWhatsappId ||
    canal.whatsappCanalId ||
    canal.canalId ||
    ''
);

const getCanalEmpresaId = (canal = {}) => normalizarId(
  canal.empresaId ||
    canal.empresa?._id ||
    canal.empresa?.id ||
    canal.empresa ||
    canal.unidadeId ||
    canal.lojaId ||
    ''
);

const getCanalCodigoLoja = (canal = {}) => String(
  canal.codigoLoja ||
    canal.loja ||
    canal.empresaCodigo ||
    canal.codigoEmpresa ||
    canal.empresa?.codigoLoja ||
    canal.empresa?.loja ||
    canal.empresa?.slug ||
    ''
).trim();

const getCanalStatus = (canal = {}) => String(
  canal.status ||
    canal.sessionStatus ||
    canal.estado ||
    canal.statusConexao ||
    canal.conexaoStatus ||
    ''
).trim().toUpperCase();

const isCanalPrincipal = (canal = {}) => (
  canal.principal === true ||
  canal.isPrincipal === true ||
  canal.padrao === true ||
  canal.default === true
);

const isCanalConectado = (canal = {}) => {
  const status = getCanalStatus(canal);

  return (
    canal.conectado === true ||
    canal.connected === true ||
    canal.isConnected === true ||
    status === 'CONNECTED' ||
    status === 'OPEN' ||
    status === 'ABERTO'
  );
};

const ordenarCanaisPreferenciais = (a, b) => {
  const conectadoA = isCanalConectado(a) ? 1 : 0;
  const conectadoB = isCanalConectado(b) ? 1 : 0;

  if (conectadoA !== conectadoB) return conectadoB - conectadoA;

  const principalA = isCanalPrincipal(a) ? 1 : 0;
  const principalB = isCanalPrincipal(b) ? 1 : 0;

  if (principalA !== principalB) return principalB - principalA;

  return String(a.nome || '').localeCompare(String(b.nome || ''));
};

const resolverCanalWhatsAppDaOS = (os = {}, empresasCadastradas = [], canaisWhatsapp = []) => {
  const empresa = descobrirEmpresaOficial(os, empresasCadastradas);

  const empresaIdOficial = empresa ? normalizarId(empresa._id || empresa.id) : '';
  const empresaIdDaOS = getEmpresaIdOS(os);
  const empresaId = empresaIdOficial || empresaIdDaOS || '';

  const lojaCodigo = empresa ? getEmpresaCodigo(empresa) : String(os.loja || '').trim();
  const lojaLabel = empresa ? getEmpresaLabel(empresa) : String(os.loja || 'Empresa não identificada').trim();
  const buscaLoja = normalizarTextoBusca(lojaCodigo || lojaLabel);

  const canaisAtivos = (Array.isArray(canaisWhatsapp) ? canaisWhatsapp : [])
    .filter((canal) => canal && canal.ativo !== false && canal.deleted !== true && canal.removido !== true);

  const canaisDaEmpresa = canaisAtivos.filter((canal) => {
    const canalEmpresaId = getCanalEmpresaId(canal);
    const canalCodigoLoja = normalizarTextoBusca(getCanalCodigoLoja(canal));
    const canalEmpresaNome = normalizarTextoBusca(canal.empresa?.nome || canal.empresa?.razaoSocial || '');
    const canalEmpresaUnidade = normalizarTextoBusca(canal.empresa?.unidade || '');

    if (empresaId && canalEmpresaId && canalEmpresaId === empresaId) return true;
    if (buscaLoja && canalCodigoLoja && canalCodigoLoja === buscaLoja) return true;
    if (buscaLoja && canalEmpresaUnidade && canalEmpresaUnidade === buscaLoja) return true;
    if (buscaLoja && canalEmpresaNome && canalEmpresaNome === buscaLoja) return true;

    return false;
  }).sort(ordenarCanaisPreferenciais);

  const canalSelecionado = canaisDaEmpresa[0] || null;
  const canalWhatsappId = getCanalWhatsappId(canalSelecionado || {});

  return {
    empresa,
    empresaId,
    canal: canalSelecionado,
    canalWhatsappId,
    canalNome: canalSelecionado?.nome || canalSelecionado?.name || '',
    canalStatus: canalSelecionado ? getCanalStatus(canalSelecionado) : '',
    canalConectado: canalSelecionado ? isCanalConectado(canalSelecionado) : false,
    lojaCodigo,
    lojaLabel,
    empresaNome: empresa?.nome || empresa?.razaoSocial || lojaLabel,
    empresaUnidade: empresa?.unidade || '',
    contatoEmpresa: getEmpresaContatoWhatsApp(empresa || {}),
    enderecoEmpresa: getEmpresaEnderecoFormatado(empresa || {})
  };
};

const criarMensagemProntoNaLoja = (os = {}, empresa = {}) => {
  const nomeCliente = safeRenderName(os.cliente).split(' ')[0] || 'Cliente';
  const temEmpresa = empresa && Object.keys(empresa).length > 0;
  const nomeEmpresa = temEmpresa ? getEmpresaLabel(empresa) : (os.loja || 'nossa ótica');
  const telefone = getEmpresaContatoWhatsApp(empresa || {});
  const endereco = getEmpresaEnderecoFormatado(empresa || {});

  return `Olá, ${nomeCliente}! 😊\n\nTemos uma ótima notícia: seus óculos já estão *prontos* e aguardando retirada. 👓✨\n\n📍 *${nomeEmpresa}*${endereco ? `\n${endereco}` : ''}${telefone ? `\n📲 Contato: ${telefone}` : ''}\n\nPode passar aqui quando ficar melhor para você. Será um prazer te atender!`;
};

const criarPayloadAvisoProntoNaLoja = ({ os = {}, phone = '', message = '' }) => {
  const payload = {
    number: phone,
    message,
    osId: os._id,
    tipoMensagem: 'aviso_pronto_loja'
  };

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );
};

const getNotaFiscal = (os) => os?.notaFiscal || null;


const getNomeArquivoNfce = (os, extensao = 'pdf') => {
  const numero = os?.numeroOS || os?._id || 'sem-numero';
  return `nfce-os-${numero}.${extensao}`;
};

const abrirBlobOuBaixar = (blob, nomeArquivo, baixar = false) => {
  const fileURL = URL.createObjectURL(blob);

  if (baixar) {
    const link = document.createElement('a');
    link.href = fileURL;
    link.download = nomeArquivo;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } else {
    window.open(fileURL, '_blank', 'noopener,noreferrer');
  }

  setTimeout(() => URL.revokeObjectURL(fileURL), 30000);
};

const baixarPdfNfce = async (os, options = {}) => {
  const { baixar = false } = options;

  try {
    const osId = os?._id;

    if (!osId) {
      alert('ID da OS não encontrado.');
      return false;
    }

    const token = localStorage.getItem('vizumToken');

    const response = await api.get(`/ordens-servico/${osId}/nfce/pdf`, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const file = new Blob([response.data], { type: 'application/pdf' });
    abrirBlobOuBaixar(file, getNomeArquivoNfce(os, 'pdf'), baixar);

    return true;
  } catch (error) {
    console.error('Erro ao abrir PDF:', error);
    alert(baixar ? 'Erro ao baixar o PDF da nota fiscal.' : 'Erro ao abrir o PDF da nota fiscal.');
    return false;
  }
};

const baixarXmlNfce = async (os, options = {}) => {
  const { baixar = true } = options;

  try {
    const osId = os?._id;

    if (!osId) {
      alert('ID da OS não encontrado.');
      return false;
    }

    if (!isNotaFiscalEmitida(os) && !os?.notaFiscal?.invoiceId) {
      alert('XML ainda não disponível nesta OS. Consulte a NFC-e novamente após a autorização.');
      return false;
    }

    const token = localStorage.getItem('vizumToken');

    const response = await api.get(`/ordens-servico/${osId}/nfce/xml`, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const contentType =
      response.headers?.['content-type'] ||
      'application/xml';

    const file = new Blob([response.data], { type: contentType });
    abrirBlobOuBaixar(file, getNomeArquivoNfce(os, 'xml'), baixar);

    return true;
  } catch (error) {
    console.error('Erro ao abrir XML:', error);
    alert('Erro ao abrir/baixar XML da nota fiscal.');
    return false;
  }
};


const isNotaFiscalEmitida = (os) => {
  const nota = getNotaFiscal(os);
  const status = String(nota?.status || '').toLowerCase();

  return Boolean(
    nota?.pdfUrl ||
    nota?.xmlUrl ||
    nota?.chave ||
    status.includes('issued') ||
    status.includes('authorized') ||
    status.includes('autoriz')
  );
};

const isNotaFiscalProcessando = (os) => {
  const nota = getNotaFiscal(os);
  const status = String(nota?.status || '').toLowerCase();

  return Boolean(
    nota?.invoiceId &&
    !isNotaFiscalEmitida(os) &&
    !status.includes('rejeit') &&
    !status.includes('error') &&
    !status.includes('erro')
  );
};

const isNotaFiscalRejeitada = (os) => {
  const nota = getNotaFiscal(os);
  const status = String(nota?.status || '').toLowerCase();

  return Boolean(
    status.includes('rejeit') ||
    status.includes('error') ||
    status.includes('erro') ||
    nota?.mensagemErro
  );
};

const osEstaEntregue = (os = {}) => String(os?.status || '').trim() === 'Entregue';

const podeEmitirNovaNfce = (os = {}) => {
  /*
    Regra comercial:
    - Só emite uma NFC-e nova quando a OS estiver Entregue.
    - Se a nota já foi emitida ou está processando, pode visualizar/consultar/baixar.
  */
  return osEstaEntregue(os) || isNotaFiscalEmitida(os) || isNotaFiscalProcessando(os);
};

const getNotaFiscalLabel = (os) => {
  if (isNotaFiscalEmitida(os)) return 'NFC-e autorizada';
  if (isNotaFiscalRejeitada(os)) return 'NFC-e rejeitada';
  if (isNotaFiscalProcessando(os)) return 'NFC-e processando';
  return 'Sem NFC-e';
};

const getNotaFiscalBadgeStyle = (os) => {
  if (isNotaFiscalEmitida(os)) {
    return { background: '#ECFDF5', color: '#059669', border: '1px solid #A7F3D0' };
  }

  if (isNotaFiscalRejeitada(os)) {
    return { background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' };
  }

  if (isNotaFiscalProcessando(os)) {
    return { background: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A' };
  }

  return { background: '#F8FAFC', color: '#64748B', border: '1px solid #E2E8F0' };
};


const getOsItems = (os) => {
  if (!os) return [];
  if (os.itens && Array.isArray(os.itens) && os.itens.length > 0) {
    return os.itens;
  }
  if (os.produto || os.receita || os.paciente) {
    return [{
      paciente: { nome: safeRenderName(os.paciente) || safeRenderName(os.cliente) },
      produto: os.produto || {},
      receita: os.receita || { od: {}, oe: {} }
    }];
  }
  return [];
};

const getQuickDates = (preset) => {
  const today = new Date();
  let start, end;
  
  switch(preset) {
    case 'hoje':
      start = new Date(); end = new Date(); 
      break;
    case 'ontem':
      start = new Date(); start.setDate(start.getDate() - 1);
      end = new Date(); end.setDate(end.getDate() - 1);
      break;
    case 'semana':
      start = new Date(); start.setDate(start.getDate() - start.getDay()); 
      end = new Date(); end.setDate(start.getDate() + 6); 
      break;
    case 'mes':
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      break;
    case 'mes_passado':
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    default: 
      return null;
  }
  return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
};

const prepararArquivoImagemOS = async (file) => {
  if (!file) return null;

  let fileToProcess = file;
  const fileExtension = file.name.split('.').pop().toLowerCase();
  const isHeic =
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    fileExtension === 'heic' ||
    fileExtension === 'heif';

  if (isHeic) {
    const convertedBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.8
    });

    const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

    fileToProcess = new File(
      [blob],
      file.name.replace(/\.(heic|heif)$/i, '.jpg'),
      { type: 'image/jpeg' }
    );
  }

  return imageCompression(fileToProcess, {
    maxSizeMB: 0.35,
    maxWidthOrHeight: 1280,
    useWebWorker: true,
    initialQuality: 0.72
  });
};

const extractObservations = (os) => String(
  os?.observacaoVenda ||
  os?.observacaoDaVenda ||
  os?.observacao ||
  os?.observacoes ||
  ''
).trim();
// ==================================================================================
// INTEGRAÇÃO LABOLENS — VIA API PRINCIPAL DA LUMIX
// ==================================================================================
// O React NÃO conhece URL, segredo ou servidor do laboratório.
// Fluxo:
// Front -> API Lumix -> API Labolens
//
// A API Lumix é a fonte de verdade da integração e retorna PartnerOrder.
const LABOLENS_LUMIX_PEDIDOS_ENDPOINT = '/api/integracoes/labolens/pedidos';
const LABOLENS_POLL_INTERVAL_MS = 4000;

const montarPedidoLabolens = ({ os = {}, item = {}, itemIndex = 0 }) => {
  const produto = item?.produto || {};
  const receita = item?.receita || {};
  const medidas = produto?.medidasArmacao || {};
  const osId = normalizarId(os?._id || os?.id);
  const numeroOS = os?.numeroOS || '';
  const entregaMarcadaCliente = item?.dataEntrega || produto?.dataEntrega || os?.dataEntrega || '';
  const previsaoEntregaLabolens = subtrairUmDiaISO(entregaMarcadaCliente);

  return {
    origem: 'lumix_oticas',
    parceiro: 'labolens',
    tipo: 'pedido_lente',
    versaoPayload: '1.0',
    referencia: `OS-${numeroOS || osId || 'SEM-NUMERO'}-ITEM-${itemIndex + 1}`,
    os: {
      id: osId,
      numero: numeroOS,
      status: os?.status || '',
      empresaId: getEmpresaIdOS(os),
      loja: os?.lojaOficial || os?.loja || '',
      vendedor: normalizarNomeVendedor(os?.vendedor),
      dataCompra: os?.dataCompra || '',
      entregaMarcadaCliente,
      previsaoEntrega: previsaoEntregaLabolens,
      previsaoEntregaLabolens
    },
    cliente: {
      nome: safeRenderName(os?.cliente) || '',
      whatsapp: os?.whatsapp || ''
    },
    paciente: {
      nome: safeRenderName(item?.paciente) || safeRenderName(os?.cliente) || ''
    },
    lente: {
      nome: produto?.lente || '',
      codigoCV: produto?.numeroCV || produto?.cv || produto?.codigoCV || '',
      armacao: produto?.armacao || '',
      origemArmacao: produto?.origemArmacao || '',
      envioArmacao: produto?.envioArmacao || '',
      formatoArmacao: produto?.formatoArmacao || '',
      medidas: {
        aro: medidas?.aro || '',
        ponte: medidas?.ponte || '',
        altura: medidas?.altura || '',
        diagonalMaior: medidas?.diagonalMaior || ''
      }
    },
    receita: {
      od: {
        esferico: receita?.od?.esf || '',
        cilindrico: receita?.od?.cil || '',
        eixo: receita?.od?.eixo || '',
        dnp: receita?.od?.dnp || '',
        altura: receita?.od?.aco || receita?.od?.altura || ''
      },
      oe: {
        esferico: receita?.oe?.esf || '',
        cilindrico: receita?.oe?.cil || '',
        eixo: receita?.oe?.eixo || '',
        dnp: receita?.oe?.dnp || '',
        altura: receita?.oe?.aco || receita?.oe?.altura || ''
      },
      adicao: receita?.adicao || ''
    },
    enviadoEm: new Date().toISOString()
  };
};

const formatarCampoLabolens = (valor, fallback = '-') => {
  const texto = String(valor ?? '').trim();
  return texto || fallback;
};

const subtrairUmDiaISO = (dateString = '') => {
  const raw = String(dateString || '').trim();
  if (!raw) return '';

  const onlyDate = raw.includes('T') ? raw.split('T')[0] : raw;
  const [year, month, day] = onlyDate.split('-').map(Number);

  if (!year || !month || !day) return onlyDate;

  const date = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(date.getTime())) return onlyDate;

  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().split('T')[0];
};

const LABOLENS_STATUS_LABELS = Object.freeze({
  CRIADO: 'Pedido criado',
  ENVIANDO: 'Enviando pedido',
  RECEBIDO: 'Pedido recebido',
  CONFIRMADO: 'Pedido confirmado',
  EM_FABRICACAO: 'Pedido em fabricação',
  PRONTO: 'Pedido pronto',
  CANCELADO: 'Pedido cancelado',
  ERRO: 'Erro na integração'
});

const normalizarStatusLabolens = (status = '') => {
  const value = String(status || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

  const aliases = {
    PEDIDO_RECEBIDO: 'RECEBIDO',
    PEDIDO_CONFIRMADO: 'CONFIRMADO',
    PEDIDO_EM_FABRICACAO: 'EM_FABRICACAO',
    FABRICACAO: 'EM_FABRICACAO',
    EM_PRODUCAO: 'EM_FABRICACAO',
    PRODUCAO: 'EM_FABRICACAO',
    PEDIDO_PRONTO: 'PRONTO',
    PEDIDO_REALIZADO: 'RECEBIDO'
  };

  return aliases[value] || value;
};

const getLabolensStatusLabel = (status = '') => {
  const normalized = normalizarStatusLabolens(status);
  return LABOLENS_STATUS_LABELS[normalized] || String(status || 'Pedido Labolens');
};

const parceiroParaIntegracaoOS = (pedido = {}) => ({
  parceiro: 'LABOLENS',
  partnerOrderId: normalizarId(pedido?._id || pedido?.id),
  pedidoId: pedido?.pedidoExternoId || pedido?.pedidoId || '',
  itemIndex: Number(pedido?.itemIndex ?? 0),
  status: normalizarStatusLabolens(pedido?.status || 'RECEBIDO'),
  statusLabel: getLabolensStatusLabel(pedido?.status || 'RECEBIDO'),
  previsaoEntrega:
    pedido?.previsaoLaboratorio ||
    pedido?.previsaoEntrega ||
    pedido?.previsaoEntregaLabolens ||
    '',
  previsaoEntregaLabolens:
    pedido?.previsaoLaboratorio ||
    pedido?.previsaoEntrega ||
    pedido?.previsaoEntregaLabolens ||
    '',
  recebidoEm: pedido?.recebidoEm || null,
  confirmadoEm: pedido?.confirmadoEm || null,
  fabricacaoIniciadaEm: pedido?.fabricacaoIniciadaEm || null,
  prontoEm: pedido?.prontoEm || null,
  atualizadoEm: pedido?.updatedAt || new Date().toISOString()
});

const getLabolensPedidoItem = (os = {}, itemIndex = 0, pedidos = []) => {
  const index = Number(itemIndex);

  if (Array.isArray(pedidos) && pedidos.length) {
    const encontrados = pedidos
      .filter((pedido) => Number(pedido?.itemIndex ?? 0) === index)
      .slice()
      .sort((a, b) => {
        const da = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
        const db = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
        return db - da;
      });

    if (encontrados.length) return encontrados[0];
  }

  const integracao =
    os?.integracaoLabolens ||
    os?.labolens ||
    os?.pedidoLabolens ||
    null;

  if (
    integracao &&
    Number(integracao?.itemIndex ?? 0) === index
  ) {
    return integracao;
  }

  return null;
};

const getLabolensInfoOS = (os = {}, pedidos = []) => {
  const registros = Array.isArray(os?.registros) ? os.registros : [];

  const registroLabolens = registros
    .slice()
    .reverse()
    .find((registro) => {
      const texto = String(registro?.texto || '');
      return /LABOLENS/i.test(texto);
    });

  const textoRegistro = String(registroLabolens?.texto || '');
  const previsaoRegistro =
    textoRegistro.match(/previsaoLabolens=(\d{4}-\d{2}-\d{2})/i)?.[1] || '';

  const integracaoFallback =
    os?.integracaoLabolens ||
    os?.labolens ||
    os?.pedidoLabolens ||
    null;

  const lista = Array.isArray(pedidos)
    ? pedidos.filter(Boolean)
    : [];

  let atual = null;

  if (lista.length) {
    /*
      Se uma OS tem mais de uma lente, mostramos o estágio menos avançado
      entre os pedidos ativos. Assim a linha só fica "Pronto" quando todos
      os pedidos ativos realmente chegaram a PRONTO.
    */
    const ordem = {
      ERRO: 0,
      CRIADO: 1,
      ENVIANDO: 2,
      RECEBIDO: 3,
      CONFIRMADO: 4,
      EM_FABRICACAO: 5,
      PRONTO: 6,
      CANCELADO: 7
    };

    const ativos = lista.filter(
      (pedido) => normalizarStatusLabolens(pedido?.status) !== 'CANCELADO'
    );

    const fonte = ativos.length ? ativos : lista;

    atual = fonte
      .slice()
      .sort((a, b) => {
        const sa = ordem[normalizarStatusLabolens(a?.status)] ?? 99;
        const sb = ordem[normalizarStatusLabolens(b?.status)] ?? 99;

        if (sa !== sb) return sa - sb;

        const da = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
        const db = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
        return db - da;
      })[0];
  } else {
    atual = integracaoFallback;
  }

  const status = normalizarStatusLabolens(
    atual?.status ||
    (textoRegistro.includes('fabrica') ? 'EM_FABRICACAO' : '') ||
    ''
  );

  const ativo = Boolean(
    atual ||
    registroLabolens
  );

  const previsao =
    atual?.previsaoLaboratorio ||
    atual?.previsaoEntrega ||
    atual?.previsaoEntregaLabolens ||
    previsaoRegistro ||
    '';

  return {
    ativo,
    status,
    statusLabel: getLabolensStatusLabel(status || 'RECEBIDO'),
    pedidoId:
      atual?.pedidoExternoId ||
      atual?.pedidoId ||
      atual?.id ||
      '',
    previsao,
    totalPedidos: lista.length || (ativo ? 1 : 0)
  };
};

function LabolensPedidoModal({
  pedido,
  onClose,
  onConfirm,
  enviando,
  erro,
  sucesso,
  conferido,
  onToggleConferido
}) {
  const modalRef = useRef(null);

  useEffect(() => {
    if (!pedido || typeof document === 'undefined') return undefined;

    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !enviando) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    const frame = window.requestAnimationFrame(() => {
      modalRef.current?.focus?.();
    });

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = overflowAnterior;
    };
  }, [pedido, enviando, onClose]);

  if (!pedido || typeof document === 'undefined') return null;

  const payload = pedido.payload || {};
  const receita = payload.receita || {};
  const medidas = payload.lente?.medidas || {};
  const entregaCliente = payload.os?.entregaMarcadaCliente || '';
  const previsaoLabolens =
    payload.os?.previsaoEntregaLabolens ||
    payload.os?.previsaoEntrega ||
    '';

  const modal = (
    <div
      className="labolens-portal-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !enviando) {
          onClose();
        }
      }}
    >
      <div
        ref={modalRef}
        className="labolens-order-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Pedido Labolens da OS ${formatarCampoLabolens(payload.os?.numero)}`}
        tabIndex={-1}
      >
        <div className="labolens-modal-top">
          <div className="labolens-modal-brand-box">
            <span className="labolens-partner-label">Parceria</span>
            <img src={labolensLogoPng} alt="Labolens" />
          </div>

          <button
            type="button"
            className="btn-acao labolens-modal-close"
            onClick={onClose}
            aria-label="Fechar pedido Labolens"
            title="Fechar"
            disabled={enviando}
          >
            <Icons.Close />
          </button>
        </div>

        <div className="labolens-modal-scroll">
          <div className="labolens-modal-heading">
            <div>
              <span className="labolens-modal-kicker">Pedido integrado</span>
              <h3>Fazer Pedido da Lente</h3>
            </div>

            <strong>OS #{formatarCampoLabolens(payload.os?.numero)}</strong>
          </div>

          <div className="labolens-modal-summary">
            <div>
              <span>Paciente</span>
              <strong>{formatarCampoLabolens(payload.paciente?.nome)}</strong>
            </div>

            <div>
              <span>Lente</span>
              <strong>{formatarCampoLabolens(payload.lente?.nome)}</strong>
              <small>CV {formatarCampoLabolens(payload.lente?.codigoCV)}</small>
            </div>

            <div>
              <span>Entrega marcada</span>
              <strong>
                {entregaCliente
                  ? formatDateLocal(entregaCliente)
                  : 'Não informada'}
              </strong>
            </div>

            <div className="labolens-forecast-box">
              <span>Previsão Labolens</span>
              <strong>
                {previsaoLabolens
                  ? formatDateLocal(previsaoLabolens)
                  : 'Não informada'}
              </strong>
              <small>1 dia antes da entrega marcada</small>
            </div>
          </div>

          <div className="labolens-modal-body-grid">
            <div className="labolens-modal-section">
              <div className="labolens-modal-section-title">Grau da lente</div>

              <div className="labolens-prescription-table-wrap">
                <table className="labolens-prescription-table">
                  <thead>
                    <tr>
                      <th>Olho</th>
                      <th>Esf.</th>
                      <th>Cil.</th>
                      <th>Eixo</th>
                      <th>DNP</th>
                      <th>Alt.</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td>OD</td>
                      <td>{formatarCampoLabolens(receita.od?.esferico)}</td>
                      <td>{formatarCampoLabolens(receita.od?.cilindrico)}</td>
                      <td>{formatarCampoLabolens(receita.od?.eixo)}</td>
                      <td>{formatarCampoLabolens(receita.od?.dnp)}</td>
                      <td>{formatarCampoLabolens(receita.od?.altura)}</td>
                    </tr>

                    <tr>
                      <td>OE</td>
                      <td>{formatarCampoLabolens(receita.oe?.esferico)}</td>
                      <td>{formatarCampoLabolens(receita.oe?.cilindrico)}</td>
                      <td>{formatarCampoLabolens(receita.oe?.eixo)}</td>
                      <td>{formatarCampoLabolens(receita.oe?.dnp)}</td>
                      <td>{formatarCampoLabolens(receita.oe?.altura)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="labolens-addition">
                <span>Adição</span>
                <strong>{formatarCampoLabolens(receita.adicao)}</strong>
              </div>
            </div>

            <div className="labolens-modal-section">
              <div className="labolens-modal-section-title">
                Medidas da armação
              </div>

              <div className="labolens-measures">
                <span>
                  Aro <strong>{formatarCampoLabolens(medidas.aro)}</strong>
                </span>
                <span>
                  Ponte <strong>{formatarCampoLabolens(medidas.ponte)}</strong>
                </span>
                <span>
                  Altura <strong>{formatarCampoLabolens(medidas.altura)}</strong>
                </span>
                <span>
                  Diagonal{' '}
                  <strong>{formatarCampoLabolens(medidas.diagonalMaior)}</strong>
                </span>
              </div>
            </div>
          </div>

          <div className="labolens-delivery-note">
            <span>Integração</span>
            <code>
              API Lumix → Labolens · envio protegido pelo servidor
            </code>
          </div>

          {erro && (
            <div className="labolens-feedback error">
              {erro}
            </div>
          )}

          {sucesso && (
            <div className="labolens-feedback success">
              {sucesso}
            </div>
          )}

          <button
            type="button"
            className={`labolens-confirm-check ${conferido ? 'checked' : ''}`}
            onClick={() => {
              if (!enviando && !sucesso) {
                onToggleConferido(!conferido);
              }
            }}
            disabled={enviando || Boolean(sucesso)}
            aria-pressed={Boolean(conferido)}
          >
            <span
              className="labolens-confirm-check-box"
              aria-hidden="true"
            >
              {conferido ? '✓' : ''}
            </span>

            <span className="labolens-confirm-check-copy">
              <strong>Pedido conferido</strong>
              <small>
                Confirme lente, grau, medidas e previsão antes de enviar.
              </small>
            </span>
          </button>
        </div>

        <div className="labolens-modal-actions">
          <button
            type="button"
            className="btn-sm btn-cancel"
            onClick={onClose}
            disabled={enviando}
          >
            Cancelar
          </button>

          <button
            type="button"
            className={`labolens-confirm-order ${conferido ? 'is-ready' : 'is-locked'}`}
            onClick={onConfirm}
            disabled={enviando || Boolean(sucesso) || !conferido}
            aria-disabled={enviando || Boolean(sucesso) || !conferido}
            title={
              conferido
                ? 'Enviar pedido para a Labolens'
                : 'Marque "Pedido conferido" para liberar'
            }
          >
            {enviando
              ? <><Icons.Loader /> Enviando pedido...</>
              : <><Icons.Send /> Fazer Pedido</>}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

// ==================================================================================
// TELA PRINCIPAL
// ==================================================================================
export default function OSReports({ onBack, initialOsId }) {
  const [servicos, setServicos] = useState([]);
  const [empresasApi, setEmpresasApi] = useState([]);
  const [canaisWhatsapp, setCanaisWhatsapp] = useState([]);
  const [vendedoresApi, setVendedoresApi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAuxiliares, setLoadingAuxiliares] = useState(true);
  const [erroCarregamento, setErroCarregamento] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);
  const [paginacao, setPaginacao] = useState({
    total: 0,
    pagina: 1,
    limite: 10,
    totalPaginas: 1,
    temProxima: false,
    temAnterior: false
  });
  
  const [expandedRow, setExpandedRow] = useState(null);
  const [osDetalhada, setOsDetalhada] = useState(null);
  const [loadingDetalhes, setLoadingDetalhes] = useState(false);

  const [fotoExpandida, setFotoExpandida] = useState(null);
  const [menuAberto, setMenuAberto] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [detailSections, setDetailSections] = useState({ cliente: true, produtos: true, financeiro: true });
  const [expandedItems, setExpandedItems] = useState({});
  const [erroDetalhes, setErroDetalhes] = useState('');
  const [searchPinnedOsId, setSearchPinnedOsId] = useState('');
  const detailRequestSeqRef = useRef(0);
  const pinnedSearchQueryRef = useRef('');
  const rowRefs = useRef(new Map());

  const [labolensPedido, setLabolensPedido] = useState(null);
  const [labolensEnviando, setLabolensEnviando] = useState(false);
  const [labolensErro, setLabolensErro] = useState('');
  const [labolensSucesso, setLabolensSucesso] = useState('');
  const [labolensPedidoConferido, setLabolensPedidoConferido] = useState(false);
  const [labolensPedidosPorOs, setLabolensPedidosPorOs] = useState({});
  const labolensPollInFlightRef = useRef(false);
  const initialOsHandledRef = useRef('');
  const [loadingPdfId, setLoadingPdfId] = useState(null);
  const [loadingFiscalAction, setLoadingFiscalAction] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const [printModalOs, setPrintModalOs] = useState(null);
  const [whatsappModalOs, setWhatsappModalOs] = useState(null);
  const [statusDecisionModal, setStatusDecisionModal] = useState(null);
  const [textoWhatsApp, setTextoWhatsApp] = useState('');

  const [printOptions, setPrintOptions] = useState({ otica: true, lab: true, cliente: true });

  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingFotos, setUploadingFotos] = useState(false);
  const [uploadFotosProgress, setUploadFotosProgress] = useState(0);
  const [uploadFotosMensagem, setUploadFotosMensagem] = useState('');
  const [adicionandoPagamento, setAdicionandoPagamento] = useState(false);
  const [novoPagamento, setNovoPagamento] = useState({
    metodo: 'PIX',
    valor: '',
    parcelas: 1
  });

  const initDates = getQuickDates('mes');
  const [activeQuickFilter, setActiveQuickFilter] = useState('mes');
  const [atalhosAbertos, setAtalhosAbertos] = useState(true);
  const [filtrosAvancadosAbertos, setFiltrosAvancadosAbertos] = useState(true);
  const [indicadoresAbertos, setIndicadoresAbertos] = useState(false);
  const [filtros, setFiltros] = useState({ 
    dataInicio: initDates.start, 
    dataFim: initDates.end, 
    busca: '', loja: 'Todas', status: 'Todos', financeiro: 'Todos', vendedor: 'Todos'
  });
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtros);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFiltrosAplicados(filtros);
      setPaginaAtual(1);

      // Busca ativa não fecha a OS após o clique.
      if (!initialOsId && !String(filtros.busca || '').trim()) {
        detailRequestSeqRef.current += 1;
        setExpandedRow(null);
        setOsDetalhada(null);
        setErroDetalhes('');
        setSearchPinnedOsId('');
        pinnedSearchQueryRef.current = '';
      }
    }, filtros.busca !== filtrosAplicados.busca ? 450 : 0);

    return () => window.clearTimeout(timer);
  }, [filtros, filtrosAplicados.busca, initialOsId]);

  useEffect(() => {
    const handleClickFora = (event) => {
      if (
        event.target.closest?.('.dropdown-menu') ||
        event.target.closest?.('.btn-acao')
      ) {
        return;
      }

      setMenuAberto(null);
    };

    const fecharMenuFlutuante = () => setMenuAberto(null);
    window.addEventListener('click', handleClickFora);
    window.addEventListener('scroll', fecharMenuFlutuante, true);
    window.addEventListener('resize', fecharMenuFlutuante);
    return () => {
      window.removeEventListener('click', handleClickFora);
      window.removeEventListener('scroll', fecharMenuFlutuante, true);
      window.removeEventListener('resize', fecharMenuFlutuante);
    };
  },[]);

  const fetchAuxiliares = useCallback(async () => {
    setLoadingAuxiliares(true);

    try {
      const [empResult, userResult, canaisResult] = await Promise.allSettled([
        api.get('/empresas', { params: { comResumo: true } }),
        api.get('/users', { params: { ativos: true, resumo: true } }),
        api.get('/api/whatsapp/canais')
      ]);

      const empresas = empResult.status === 'fulfilled'
        ? resolverArrayApi(empResult.value.data, 'empresas').filter((e) => e.ativo !== false)
        : [];

      const vendedores = userResult.status === 'fulfilled'
        ? resolverArrayApi(userResult.value.data, 'users').filter((u) => {
            return u.ativo !== false && (!u.tipo || u.tipo === 'vendedor' || u.tipo === 'admin');
          })
        : [];

      const canaisCarregados = canaisResult.status === 'fulfilled'
        ? resolverArrayApi(canaisResult.value.data, 'canais').filter((canal) => {
            return canal && canal.ativo !== false && canal.deleted !== true && canal.removido !== true;
          })
        : [];

      setEmpresasApi(empresas);
      setVendedoresApi(vendedores);
      setCanaisWhatsapp(canaisCarregados);
    } catch (err) {
      console.error('[OSReports] Erro ao carregar dados auxiliares:', err);
    } finally {
      setLoadingAuxiliares(false);
    }
  }, []);

  const fetchOrdens = useCallback(async ({ manterConteudo = false } = {}) => {
    if (!manterConteudo) setLoading(true);
    setErroCarregamento('');

    try {
      const params = montarParametrosRelatorio({
        filtros: filtrosAplicados,
        pagina: paginaAtual,
        limite: itensPorPagina
      });

      const response = await api.get('/ordens-servico', { params });
      const ordensRecebidas = resolverArrayApi(response.data, 'ordens');
      const respostaSemEnvelope = Array.isArray(response.data);
      const apiIgnorouPaginacao = respostaSemEnvelope && ordensRecebidas.length > itensPorPagina;
      const inicioLocal = apiIgnorouPaginacao ? (paginaAtual - 1) * itensPorPagina : 0;
      const loteLimitado = ordensRecebidas.slice(inicioLocal, inicioLocal + itensPorPagina);
      const fonteMeta = apiIgnorouPaginacao
        ? { total: ordensRecebidas.length, page: paginaAtual, limit: itensPorPagina }
        : response.data;
      const meta = resolverPaginacaoApi(fonteMeta, paginaAtual, itensPorPagina, loteLimitado.length);

      if (apiIgnorouPaginacao) {
        console.warn('[OSReports] A API ignorou page/limit. A interface paginou localmente, mas o backend deve aplicar limit/skip para eliminar a sobrecarga de rede.');
      }

      setServicos(loteLimitado);
      setPaginacao(meta);
    } catch (err) {
      console.error('[OSReports] Erro ao carregar ordens paginadas:', err);
      setErroCarregamento('Não foi possível carregar as ordens de serviço. Verifique a conexão e tente novamente.');
      setServicos([]);
      setPaginacao((prev) => ({ ...prev, total: 0, totalPaginas: 1, temProxima: false }));
    } finally {
      setLoading(false);
    }
  }, [filtrosAplicados, paginaAtual, itensPorPagina]);

  const fetchDados = useCallback(async () => {
    await Promise.all([fetchAuxiliares(), fetchOrdens()]);
  }, [fetchAuxiliares, fetchOrdens]);

  useEffect(() => { fetchAuxiliares(); }, [fetchAuxiliares]);
  useEffect(() => { fetchOrdens(); }, [fetchOrdens]);

  // Lista oficial de empresas para filtro e edição
  const listaEmpresasFiltro = useMemo(() => {
    return empresasApi
      .map((empresa) => ({
        id: normalizarId(empresa._id || empresa.id),
        codigo: getEmpresaCodigo(empresa),
        label: getEmpresaLabel(empresa),
        fiscalPronta: empresaFiscalPronta(empresa)
      }))
      .filter((empresa) => empresa.id || empresa.codigo)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [empresasApi]);

  // Fallback para OS antigas sem empresa cadastrada
  const listaLojas = useMemo(() => {
    const names = servicos
      .map((os) => String(os.loja || '').trim())
      .filter(Boolean);

    return Array.from(new Set(names)).sort();
  }, [servicos]);

  // Lista de Vendedores normalizados
  const listaVendedores = useMemo(() => {
    const namesApi = vendedoresApi.map(v => normalizarNomeVendedor(v.nomeCompleto));
    const namesOS = servicos.map(s => normalizarNomeVendedor(s.vendedor));
    const unicos = new Set([...namesApi, ...namesOS]);
    return ['Todos', ...Array.from(unicos).sort()];
  }, [vendedoresApi, servicos]);
  
  const listaVendedoresEdit = useMemo(() => listaVendedores.filter(v => v !== 'Todos'), [listaVendedores]);

  const scrollOsToTop = useCallback((osId) => {
    const id = normalizarId(osId);
    if (!id) return;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => rowRefs.current.get(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    });
  }, []);

  useEffect(() => {
    if (!searchPinnedOsId || !pinnedSearchQueryRef.current) return;
    const consultaAtual = normalizarTextoBusca(filtros.busca);
    if (consultaAtual === pinnedSearchQueryRef.current) return;
    detailRequestSeqRef.current += 1;
    setSearchPinnedOsId('');
    setExpandedRow(null);
    setOsDetalhada(null);
    setErroDetalhes('');
    setExpandedItems({});
    pinnedSearchQueryRef.current = '';
  }, [filtros.busca, searchPinnedOsId]);
  const handleRowClick = useCallback(async (osId, forceOpen = false) => {
    const id = normalizarId(osId);
    if (!id) return;

    if (expandedRow === id && !forceOpen) {
      detailRequestSeqRef.current += 1;
      setExpandedRow(null);
      setOsDetalhada(null);
      setErroDetalhes('');
      setEditMode(false);
      setDetailSections({ cliente: true, produtos: true, financeiro: true });
      setExpandedItems({});
      setSearchPinnedOsId('');
      pinnedSearchQueryRef.current = '';
      return;
    }

    const requestSeq = ++detailRequestSeqRef.current;
    const buscaAtiva = Boolean(String(filtros.busca || '').trim());

    setExpandedRow(id);
    setLoadingDetalhes(true);
    setErroDetalhes('');
    setEditMode(false);
    setDetailSections({ cliente: true, produtos: true, financeiro: true });
    setExpandedItems({});

    if (buscaAtiva) {
      setSearchPinnedOsId(id);
      pinnedSearchQueryRef.current = normalizarTextoBusca(filtros.busca);
      scrollOsToTop(id);
    }

    try {
      const response = await api.get(`/ordens-servico/${id}`);
      if (requestSeq !== detailRequestSeqRef.current) return;

      const osCompleta = response?.data || {};
      const idCompleto = normalizarId(osCompleta?._id || osCompleta?.id);
      if (!idCompleto) throw new Error('A API não retornou uma OS válida.');

      const itens = getOsItems(osCompleta);
      setExpandedRow(idCompleto);
      setOsDetalhada(osCompleta);
      setExpandedItems(itens.length > 0 ? { 0: true } : {});

      if (buscaAtiva) {
        setSearchPinnedOsId(idCompleto);
        scrollOsToTop(idCompleto);
      }
    } catch (err) {
      if (requestSeq !== detailRequestSeqRef.current) return;
      console.error('[OSReports] Erro ao abrir detalhes da OS:', err);
      setOsDetalhada(null);
      setErroDetalhes(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Não foi possível carregar os detalhes desta O.S.'
      );
    } finally {
      if (requestSeq === detailRequestSeqRef.current) setLoadingDetalhes(false);
    }
  }, [expandedRow, filtros.busca, scrollOsToTop]);

  useEffect(() => {
    const osId = normalizarId(initialOsId);
    if (!osId || initialOsHandledRef.current === osId) return undefined;

    initialOsHandledRef.current = osId;
    let ativo = true;

    const abrirOsDiretamente = async () => {
      const requestSeq = ++detailRequestSeqRef.current;
      setExpandedRow(osId);
      setLoadingDetalhes(true);
      setErroDetalhes('');
      setEditMode(false);
      setDetailSections({ cliente: true, produtos: true, financeiro: true });
      setExpandedItems({});

      try {
        const response = await api.get(`/ordens-servico/${osId}`);
        const osCompleta = response?.data || {};
        if (!ativo || requestSeq !== detailRequestSeqRef.current || !normalizarId(osCompleta?._id || osCompleta?.id)) return;

        const idCompleto = normalizarId(osCompleta._id || osCompleta.id);
        const itens = getOsItems(osCompleta);
        setExpandedRow(idCompleto);
        setOsDetalhada(osCompleta);
        setExpandedItems(itens.length > 0 ? { 0: true } : {});

        setServicos((prev) => {
          const semDuplicar = prev.filter((item) => normalizarId(item?._id || item?.id) !== idCompleto);
          return [osCompleta, ...semDuplicar].slice(0, itensPorPagina);
        });

        scrollOsToTop(idCompleto);
      } catch (error) {
        if (!ativo || requestSeq !== detailRequestSeqRef.current) return;
        initialOsHandledRef.current = '';
        setOsDetalhada(null);
        setErroDetalhes(
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Não foi possível abrir a O.S. solicitada.'
        );
        console.error('[OSReports] Não foi possível abrir a OS solicitada:', error);
      } finally {
        if (ativo && requestSeq === detailRequestSeqRef.current) setLoadingDetalhes(false);
      }
    };

    abrirOsDiretamente();
    return () => { ativo = false; };
  }, [initialOsId, itensPorPagina, scrollOsToTop]);


  const handleQuickFilterClick = (preset) => {
    const dates = getQuickDates(preset);
    if(dates) {
      setFiltros(prev => ({ ...prev, dataInicio: dates.start, dataFim: dates.end }));
      setActiveQuickFilter(preset);
    }
  };

  const handleCustomDateChange = (field, value) => {
    setFiltros(prev => ({ ...prev, [field]: value }));
    setActiveQuickFilter('custom');
  };

  const handleClearFilters = () => {
    const init = getQuickDates('mes');
    detailRequestSeqRef.current += 1;
    setFiltros({
      dataInicio: init.start, dataFim: init.end,
      busca: '', loja: 'Todas', status: 'Todos', financeiro: 'Todos', vendedor: 'Todos'
    });
    setActiveQuickFilter('mes');
    setPaginaAtual(1);
    setExpandedRow(null);
    setOsDetalhada(null);
    setErroDetalhes('');
    setExpandedItems({});
    setSearchPinnedOsId('');
    pinnedSearchQueryRef.current = '';
  };

  const handleDeleteOS = async (id) => {
    if (!window.confirm(`⚠️ ATENÇÃO: Tem certeza absoluta que deseja excluir a OS #${osDetalhada.numeroOS}? Essa ação é permanente.`)) return;
    try {
      await api.delete(`/ordens-servico/${id}`);
      alert("Ordem de serviço excluída com sucesso!");
      setExpandedRow(null);
      setOsDetalhada(null);
      setServicos((prev) => prev.filter((item) => item._id !== id));
    } catch (error) {
      console.error(error);
      alert("Erro ao tentar excluir a OS.");
    }
  };

  const handleDeleteFoto = async (e, indexToRemove) => {
    e.stopPropagation(); 
    if (!window.confirm("Deseja realmente apagar esta foto? Esta ação não pode ser desfeita.")) return;
    try {
      const novosAnexos = osDetalhada.anexos.filter((_, idx) => idx !== indexToRemove);
      await api.put(`/ordens-servico/${osDetalhada._id}`, { anexos: novosAnexos });
      setOsDetalhada(prev => ({ ...prev, anexos: novosAnexos }));
      alert("Foto removida com sucesso.");
    } catch (error) {
      console.error(error);
      alert("Erro ao tentar remover a foto.");
    }
  };

  const atualizarOSLocal = useCallback((osAtualizada) => {
    if (!osAtualizada?._id) return;

    setOsDetalhada((prev) => {
      if (!prev || prev._id !== osAtualizada._id) return osAtualizada;
      return { ...prev, ...osAtualizada };
    });

    setServicos((prev) => prev.map((item) => (
      item._id === osAtualizada._id ? { ...item, ...osAtualizada } : item
    )));
  }, []);

  const recarregarOsDetalhada = async (osId = osDetalhada?._id) => {
    if (!osId) return null;

    const response = await api.get(`/ordens-servico/${osId}`);
    atualizarOSLocal(response.data);

    return response.data;
  };

  const handleReenviarBoasVindas = async () => {
    if (!osDetalhada?.whatsapp) {
      alert('Sem número de WhatsApp cadastrado nesta OS.');
      return;
    }

    if (!window.confirm('Deseja reenviar a mensagem de boas-vindas para este cliente?')) {
      return;
    }

    setSaving(true);

    try {
      await api.post(`/ordens-servico/${osDetalhada._id}/boas-vindas`);

      alert('Mensagem de boas-vindas reenviada com sucesso!');
      await recarregarOsDetalhada(osDetalhada._id);
    } catch (error) {
      console.error(error);
      alert('Erro ao reenviar boas-vindas.');
    } finally {
      setSaving(false);
    }
  };

  const handleAdicionarPagamento = async () => {
    if (!osDetalhada) return;

    const valor = Number(String(novoPagamento.valor || '').replace(',', '.'));
    const restanteAtual = calcularRestante(osDetalhada);

    if (!valor || valor <= 0) {
      alert('Informe um valor de entrada válido.');
      return;
    }

    if (restanteAtual <= 0) {
      alert('Esta OS já está quitada.');
      return;
    }

    if (valor > restanteAtual + 0.009) {
      alert(`O valor informado é maior que o saldo a receber (${formatCurrency(restanteAtual)}).`);
      return;
    }

    const pagamento = {
      metodo: novoPagamento.metodo || 'PIX',
      valor: Number(valor.toFixed(2)),
      parcelas: Number(novoPagamento.parcelas || 1) || 1,
      data: new Date().toISOString()
    };

    const pagamentosAtualizados = [
      ...(Array.isArray(osDetalhada.pagamentos) ? osDetalhada.pagamentos : []),
      pagamento
    ];

    setAdicionandoPagamento(true);

    try {
      await api.put(`/ordens-servico/${osDetalhada._id}`, {
        pagamentos: pagamentosAtualizados,
        registroTexto: `💰 Nova entrada adicionada: ${pagamento.metodo} - ${formatCurrency(pagamento.valor)}.`
      });

      alert('Entrada adicionada com sucesso!');
      setNovoPagamento({
        metodo: 'PIX',
        valor: '',
        parcelas: 1
      });

      await recarregarOsDetalhada(osDetalhada._id);
    } catch (error) {
      console.error(error);
      alert('Erro ao adicionar entrada.');
    } finally {
      setAdicionandoPagamento(false);
    }
  };

  const handleMarcarProntoNaLoja = async (decisaoAviso = null) => {
    if (!osDetalhada) return;

    const temWhatsapp = Boolean(osDetalhada.whatsapp);

    if (!decisaoAviso) {
      setStatusDecisionModal({ os: osDetalhada, temWhatsapp });
      return;
    }

    if (decisaoAviso === 'cancelar') {
      setStatusDecisionModal(null);
      return;
    }

    const enviarAviso = decisaoAviso === 'enviar';

    if (enviarAviso && !temWhatsapp) {
      alert('Esta OS não tem WhatsApp cadastrado. Corrija o número antes de enviar o aviso.');
      return;
    }

    setStatusDecisionModal(null);
    setSaving(true);

    try {
      if (enviarAviso) {
        const canal = resolverCanalWhatsAppDaOS(osDetalhada, empresasApi, canaisWhatsapp);
        const phone = normalizarNumeroEnvio(osDetalhada.whatsapp);

        if (!phone || phone.length < 12) {
          alert('O WhatsApp desta OS está vazio ou inválido. Corrija o número antes de enviar o aviso.');
          return;
        }

        if (!canal.empresaId && !canal.lojaCodigo) {
          alert('Não foi possível identificar a empresa desta OS. Abra a OS, selecione a empresa correta e salve antes de enviar o aviso.');
          return;
        }

        if (!canal.canalWhatsappId) {
          alert(`Não existe canal de atendimento cadastrado para esta empresa/unidade.\n\nEmpresa: ${canal.lojaLabel || 'Não identificada'}\n\nCrie ou conecte o canal em Canais antes de enviar o aviso.`);
          return;
        }

        const message = criarMensagemProntoNaLoja(osDetalhada, canal.empresa || {});
        const payload = criarPayloadAvisoProntoNaLoja({
          os: osDetalhada,
          phone,
          message
        });

        await api.post(`/api/whatsapp/canais/${canal.canalWhatsappId}/send`, payload);
      }

      await api.put(`/ordens-servico/${osDetalhada._id}`, {
        status: 'Pronto na loja',
        registroTexto: enviarAviso
          ? 'Aviso de retirada enviado ao cliente. Status alterado para Pronto na loja.'
          : 'Status alterado para Pronto na loja sem envio de aviso.'
      });

      setOsDetalhada((prev) => ({ ...prev, status: 'Pronto na loja' }));
      await recarregarOsDetalhada(osDetalhada._id);
      alert(enviarAviso ? 'Aviso enviado e status atualizado.' : 'Status atualizado para Pronto na loja.');
    } catch (error) {
      console.error('[OSReports] Erro ao atualizar status ou enviar aviso:', error);
      const detalhe =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Erro desconhecido.';

      alert(`Erro ao atualizar status ou enviar aviso.\n\nDetalhe: ${detalhe}`);
    } finally {
      setSaving(false);
    }
  };


  const abrirPedidoLabolens = useCallback((os, item, itemIndex) => {
    if (!os || !item) return;

    const osId = normalizarId(os?._id || os?.id);
    if (!osId) return;

    setMenuAberto(null);
    setLabolensErro('');
    setLabolensSucesso('');
    setLabolensPedidoConferido(false);

    setLabolensPedido({
      osId,
      itemIndex: Number(itemIndex || 0),
      payload: montarPedidoLabolens({ os, item, itemIndex })
    });
  }, []);

  const fecharPedidoLabolens = useCallback(() => {
    if (labolensEnviando) return;
    setLabolensPedido(null);
    setLabolensErro('');
    setLabolensSucesso('');
    setLabolensPedidoConferido(false);
  }, [labolensEnviando]);

  const salvarPedidosLabolensNoMapa = useCallback((osId, pedidos = []) => {
    const id = normalizarId(osId);
    if (!id) return;

    const lista = Array.isArray(pedidos)
      ? pedidos.filter(Boolean)
      : [];

    setLabolensPedidosPorOs((prev) => ({
      ...prev,
      [id]: lista
    }));
  }, []);

  const mesclarPedidoLabolensNoMapa = useCallback((osId, pedido) => {
    const id = normalizarId(osId);
    if (!id || !pedido) return;

    const pedidoId = normalizarId(pedido?._id || pedido?.id);
    const itemIndex = Number(pedido?.itemIndex ?? 0);

    setLabolensPedidosPorOs((prev) => {
      const atual = Array.isArray(prev[id]) ? prev[id] : [];

      const indice = atual.findIndex((item) => {
        const atualId = normalizarId(item?._id || item?.id);

        if (pedidoId && atualId) {
          return pedidoId === atualId;
        }

        return Number(item?.itemIndex ?? 0) === itemIndex;
      });

      const proxima = atual.slice();

      if (indice >= 0) {
        proxima[indice] = {
          ...proxima[indice],
          ...pedido
        };
      } else {
        proxima.unshift(pedido);
      }

      return {
        ...prev,
        [id]: proxima
      };
    });
  }, []);

  const buscarPedidosLabolensDaOs = useCallback(async (osId) => {
    const id = normalizarId(osId);
    if (!id) return [];

    try {
      const response = await api.get(
        `/api/integracoes/labolens/pedidos/os/${id}`
      );

      const pedidos =
        response?.data?.pedidos ||
        response?.data?.data ||
        [];

      const lista = Array.isArray(pedidos) ? pedidos : [];

      salvarPedidosLabolensNoMapa(id, lista);

      return lista;
    } catch (error) {
      /*
        A consulta de acompanhamento é silenciosa:
        não derruba a tela principal se a integração estiver
        momentaneamente indisponível.
      */
      console.warn(
        `[Labolens] Não foi possível sincronizar a OS ${id}:`,
        error?.response?.data?.message ||
        error?.message ||
        error
      );

      return [];
    }
  }, [salvarPedidosLabolensNoMapa]);

  const enviarPedidoLabolens = useCallback(async () => {
    if (!labolensPedido?.payload || labolensEnviando) return;

    if (!labolensPedidoConferido) {
      setLabolensErro(
        'Marque "Pedido conferido" antes de enviar para a Labolens.'
      );
      return;
    }

    const osId =
      normalizarId(labolensPedido?.osId) ||
      normalizarId(labolensPedido?.payload?.os?.id);

    const itemIndex = Number(labolensPedido?.itemIndex ?? 0);

    if (!osId) {
      setLabolensErro(
        'Não foi possível identificar a OS deste pedido.'
      );
      return;
    }

    setLabolensEnviando(true);
    setLabolensErro('');
    setLabolensSucesso('');

    try {
      /*
        IMPORTANTE:
        O front envia APENAS a referência da OS/item.
        A API Lumix busca a OS no próprio banco, monta o payload oficial,
        cria PartnerOrder, assina e conversa com a API Labolens.
      */
      const response = await api.post(
        LABOLENS_LUMIX_PEDIDOS_ENDPOINT,
        {
          osId,
          itemIndex
        }
      );

      const data = response?.data || {};
      const pedidoServidor =
        data?.pedido ||
        data?.data?.pedido ||
        data?.data ||
        null;

      if (!pedidoServidor) {
        throw new Error(
          'A API Lumix confirmou a requisição, mas não retornou o registro da integração.'
        );
      }

      mesclarPedidoLabolensNoMapa(
        osId,
        pedidoServidor
      );

      const integracaoLocal =
        parceiroParaIntegracaoOS(
          pedidoServidor
        );

      /*
        Resposta RECEBIDO da API do laboratório:
        a OS comercial já aparece como Pedido realizado.
        O selo passa a usar o status REAL do PartnerOrder.
      */
      setServicos((prev) => prev.map((os) => (
        normalizarId(os?._id || os?.id) === osId
          ? {
              ...os,
              status: 'Pedido realizado',
              integracaoLabolens: integracaoLocal
            }
          : os
      )));

      setOsDetalhada((prev) => (
        normalizarId(prev?._id || prev?.id) === osId
          ? {
              ...prev,
              status: 'Pedido realizado',
              integracaoLabolens: integracaoLocal
            }
          : prev
      ));

      const pedidoExternoId =
        pedidoServidor?.pedidoExternoId ||
        pedidoServidor?.pedidoId ||
        '';

      setLabolensSucesso(
        data?.message ||
        `Pedido recebido pela Labolens${pedidoExternoId ? ` · ${pedidoExternoId}` : ''}.`
      );

      /*
        Recarrega a OS e a integração sem duplicar gravação no front.
        Toda persistência agora pertence aos controllers das APIs.
      */
      await Promise.allSettled([
        buscarPedidosLabolensDaOs(osId),

        api.get(`/ordens-servico/${osId}`)
          .then((detalheResponse) => {
            if (detalheResponse?.data) {
              atualizarOSLocal(
                detalheResponse.data
              );
            }
          })
      ]);
    } catch (error) {
      console.error(
        '[Labolens] Erro ao criar pedido pela API Lumix:',
        error
      );

      setLabolensErro(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Não foi possível enviar o pedido para a Labolens.'
      );
    } finally {
      setLabolensEnviando(false);
    }
  }, [
    labolensPedido,
    labolensEnviando,
    labolensPedidoConferido,
    mesclarPedidoLabolensNoMapa,
    buscarPedidosLabolensDaOs,
    atualizarOSLocal
  ]);

  /*
    A API Lumix recebe os webhooks da Labolens e atualiza PartnerOrder.
    Este componente acompanha esses registros em intervalos curtos,
    sem precisar expor o servidor do laboratório no navegador.
  */
  useEffect(() => {
    const ids = new Set();

    servicos.forEach((os) => {
      const id = normalizarId(os?._id || os?.id);
      if (!id) return;

      /*
        Para decidir QUAIS OS precisam de acompanhamento,
        usamos o estado persistido na própria OS.
        Não usamos labolensPedidosPorOs aqui para evitar
        reiniciar o timer a cada resposta do polling.
      */
      const info = getLabolensInfoOS(os);

      if (info.ativo) {
        ids.add(id);
      }
    });

    if (osDetalhada) {
      const detalheId = normalizarId(
        osDetalhada?._id ||
        osDetalhada?.id
      );

      if (detalheId) {
        const info = getLabolensInfoOS(
          osDetalhada
        );

        if (info.ativo) {
          ids.add(detalheId);
        }
      }
    }

    const listaIds = Array.from(ids);

    if (!listaIds.length) {
      return undefined;
    }

    let cancelado = false;

    const sincronizar = async () => {
      if (
        cancelado ||
        labolensPollInFlightRef.current ||
        (typeof document !== 'undefined' && document.hidden)
      ) {
        return;
      }

      labolensPollInFlightRef.current = true;

      try {
        await Promise.allSettled(
          listaIds.map((id) =>
            buscarPedidosLabolensDaOs(id)
          )
        );
      } finally {
        labolensPollInFlightRef.current = false;
      }
    };

    sincronizar();

    const timer = window.setInterval(
      sincronizar,
      LABOLENS_POLL_INTERVAL_MS
    );

    const handleVisibility = () => {
      if (!document.hidden) {
        sincronizar();
      }
    };

    document.addEventListener(
      'visibilitychange',
      handleVisibility
    );

    return () => {
      cancelado = true;
      window.clearInterval(timer);
      document.removeEventListener(
        'visibilitychange',
        handleVisibility
      );
    };
  }, [
    servicos,
    osDetalhada,
    buscarPedidosLabolensDaOs
  ]);

  const handleQuickStatusUpdate = async (novoStatus) => {
    if (!osDetalhada) return;

    if (novoStatus === osDetalhada.status) {
      return;
    }

    if (novoStatus === 'Entregue' && calcularRestante(osDetalhada) > 0) {
      alert(`Não é possível marcar como Entregue. Ainda existe ${formatCurrency(calcularRestante(osDetalhada))} a receber.`);
      return;
    }

    if (novoStatus === 'Pronto na loja') {
      await handleMarcarProntoNaLoja();
      return;
    }

    if (!window.confirm(`Deseja alterar o status da OS para: "${novoStatus}"?`)) return;

    setSaving(true);

    try {
      await api.put(`/ordens-servico/${osDetalhada._id}`, {
        status: novoStatus,
        registroTexto: `⚙️ Status da O.S. atualizado rapidamente para: ${novoStatus}`
      });

      alert(`Status atualizado para ${novoStatus}!`);
      setOsDetalhada(prev => ({ ...prev, status: novoStatus }));
      await recarregarOsDetalhada(osDetalhada._id);
    } catch (e) {
      console.error(e);
      alert("Erro ao mudar o status.");
    } finally {
      setSaving(false);
    }
  };

  const handleSelectWhatsAppTemplate = (mensagem) => {
    const nome = safeRenderName(whatsappModalOs.cliente).split(' ')[0] || 'Cliente'; 
    const mensagemFinal = mensagem.replace('{{nome}}', nome);
    setTextoWhatsApp(mensagemFinal);
  };

  const handleConfirmSendWhatsApp = async () => {
    if (!textoWhatsApp) return alert('Por favor, digite ou selecione uma mensagem antes de enviar.');
    const numLimpo = formatPhone(whatsappModalOs.whatsapp);
    if (!numLimpo || numLimpo.length < 10) {
       alert("O número de WhatsApp cadastrado parece inválido ou está vazio.");
       return;
    }

    try {
      await api.put(`/ordens-servico/${whatsappModalOs._id}`, {
        registroTexto: `📱 WhatsApp enviado para o cliente: "${textoWhatsApp.substring(0, 50)}..."`
      });
      if (whatsappModalOs?._id === osDetalhada?._id) {
        await recarregarOsDetalhada(whatsappModalOs._id);
      }
    } catch (error) {
      console.error("Erro ao registrar envio na API:", error);
    }

    const link = `https://wa.me/55${numLimpo}?text=${encodeURIComponent(textoWhatsApp)}`;
    window.open(link, '_blank');
    setWhatsappModalOs(null);
    setTextoWhatsApp('');
  };

  const toggleEditMode = () => {
    if (!editMode) {
      const currentItems = getOsItems(osDetalhada);
      // Preenche os selects de edição com a empresa oficial da OS
      const emp = descobrirEmpresaOficial(osDetalhada, empresasApi);
      const empresaIdOficial = emp ? normalizarId(emp._id || emp.id) : '';
      const lojaOficial = emp ? getEmpresaCodigo(emp) : (osDetalhada.loja || listaLojas[0] || '');
      const vendedorOficial = normalizarNomeVendedor(osDetalhada.vendedor);

      setEditForm({
        empresaId: empresaIdOficial,
        loja: lojaOficial,
        vendedor: vendedorOficial,
        status: osDetalhada.status || 'Pedido da lente',
        cliente: safeRenderName(osDetalhada.cliente) || '',
        cpf: osDetalhada.cliente?.cpf || '',
        dataNascimento: osDetalhada.cliente?.dataNascimento ? osDetalhada.cliente.dataNascimento.split('T')[0] : '',
        whatsapp: osDetalhada.whatsapp || '',
        cep: osDetalhada.endereco?.cep || '',
        endereco: osDetalhada.endereco?.logradouro || '',
        numero: osDetalhada.endereco?.numero || '',
        complemento: osDetalhada.endereco?.complemento || '',
        bairro: osDetalhada.endereco?.bairro || '',
        cidade: osDetalhada.endereco?.cidade || '',
        uf: osDetalhada.endereco?.uf || '',
        dataCompra: osDetalhada.dataCompra ? osDetalhada.dataCompra.split('T')[0] : '',
        dataEntrega: osDetalhada.dataEntrega ? osDetalhada.dataEntrega.split('T')[0] : '',
        valorTotal: osDetalhada.valorTotal || 0,
        itens: currentItems.map(item => ({
          paciente: safeRenderName(item.paciente) || '',
          lente: item.produto?.lente || '', 
          armacao: item.produto?.armacao || '',
          numeroCV: item.produto?.numeroCV || item.produto?.cv || item.produto?.codigoCV || '', 
          origemArmacao: item.produto?.origemArmacao || 'Ótica',
          envioArmacao: item.produto?.envioArmacao || 'Vai no malote',
          formatoArmacao: item.produto?.formatoArmacao || '',
          medidasArmacao: {
            aro: item.produto?.medidasArmacao?.aro || '',
            ponte: item.produto?.medidasArmacao?.ponte || '',
            altura: item.produto?.medidasArmacao?.altura || '',
            diagonalMaior: item.produto?.medidasArmacao?.diagonalMaior || ''
          },
          receita: {
            od: { esf: item.receita?.od?.esf || '', cil: item.receita?.od?.cil || '', eixo: item.receita?.od?.eixo || '', dnp: item.receita?.od?.dnp || '', aco: item.receita?.od?.aco || '' },
            oe: { esf: item.receita?.oe?.esf || '', cil: item.receita?.oe?.cil || '', eixo: item.receita?.oe?.eixo || '', dnp: item.receita?.oe?.dnp || '', aco: item.receita?.oe?.aco || '' },
            adicao: item.receita?.adicao || ''
          }
        }))
      });
    }
    setEditMode(!editMode);
  };

  const handleClearForm = () => {
    if (!window.confirm("Isso irá APAGAR todos os campos digitados. Deseja continuar?")) return;
    setEditForm(prev => ({
        ...prev, cliente: '', cpf: '', dataNascimento: '', whatsapp: '', cep: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '', dataCompra: '', dataEntrega: '', valorTotal: 0,
        itens: [{ paciente: '', lente: '', armacao: '', numeroCV: '', origemArmacao: 'Ótica', envioArmacao: 'Vai no malote', formatoArmacao: '', medidasArmacao: {aro:'', ponte:'', altura:'', diagonalMaior:''}, receita: { od: {}, oe: {}, adicao: '' } }]
    }));
  };

  const handleAddItem = () => setEditForm(prev => ({ ...prev, itens: [...prev.itens, { paciente: '', lente: '', armacao: '', numeroCV: '', origemArmacao: 'Ótica', envioArmacao: 'Vai no malote', formatoArmacao: '', medidasArmacao: {aro:'', ponte:'', altura:'', diagonalMaior:''}, receita: { od: {}, oe: {}, adicao: '' } }] }));
  const handleRemoveItem = (index) => {
    if (editForm.itens.length === 1) return alert("A O.S. precisa ter pelo menos 1 item.");
    setEditForm(prev => { const novosItens = [...prev.itens]; novosItens.splice(index, 1); return { ...prev, itens: novosItens }; });
  };
  const updateItemField = (index, field, value) => setEditForm(prev => ({ ...prev, itens: prev.itens.map((it, i) => i === index ? { ...it, [field]: value } : it) }));
  const updateMedidaField = (index, field, value) => setEditForm(prev => ({ ...prev, itens: prev.itens.map((it, i) => i === index ? { ...it, medidasArmacao: { ...it.medidasArmacao, [field]: value } } : it) }));
  const updateReceita = (index, olho, field, value) => setEditForm(prev => ({ ...prev, itens: prev.itens.map((it, i) => { if (i === index) { return { ...it, receita: { ...it.receita, [olho]: { ...(it.receita[olho] || {}), [field]: value } } }; } return it; }) }));
  const updateAdicao = (index, value) => setEditForm(prev => ({ ...prev, itens: prev.itens.map((it, i) => { if (i === index) { return { ...it, receita: { ...it.receita, adicao: value } }; } return it; }) }));

  const handleEmpresaEditChange = (empresaIdSelecionada) => {
    const empresa = empresasApi.find((emp) => {
      return (
        normalizarId(emp._id) === empresaIdSelecionada ||
        normalizarId(emp.id) === empresaIdSelecionada ||
        getEmpresaCodigo(emp) === empresaIdSelecionada
      );
    });

    setEditForm((prev) => ({
      ...prev,
      empresaId: empresa ? normalizarId(empresa._id || empresa.id) : '',
      loja: empresa ? getEmpresaCodigo(empresa) : empresaIdSelecionada
    }));
  };

  const handleSaveEdit = async () => {
    const pagamentosAtuais = Array.isArray(osDetalhada?.pagamentos) ? osDetalhada.pagamentos : [];
    const totalPagoAtual = pagamentosAtuais.reduce((acc, p) => acc + (Number(p.valor) || 0), 0);
    const restanteEditado = Math.max(0, (Number(editForm?.valorTotal || 0) || 0) - totalPagoAtual);

    if (editForm?.status === 'Entregue' && restanteEditado > 0.009) {
      alert(`Não é possível salvar como Entregue. Ainda existe ${formatCurrency(restanteEditado)} a receber.`);
      return;
    }

    setSaving(true);
    try {
      const itensFormatados = editForm.itens.map(item => ({
        paciente: { nome: item.paciente },
        produto: { 
          lente: item.lente, armacao: item.armacao, numeroCV: item.numeroCV, cv: item.numeroCV, codigoCV: item.numeroCV,
          origemArmacao: item.origemArmacao, envioArmacao: item.envioArmacao, formatoArmacao: item.formatoArmacao, medidasArmacao: item.medidasArmacao 
        },
        receita: item.receita
      }));

      const payload = {
        empresaId: editForm.empresaId || undefined,
        loja: editForm.loja,
        vendedor: editForm.vendedor,
        status: editForm.status,
        whatsapp: editForm.whatsapp,
        dataCompra: editForm.dataCompra || new Date().toISOString(), dataEntrega: editForm.dataEntrega || undefined,
        valorTotal: Number(editForm.valorTotal) || 0,
        cliente: { nome: editForm.cliente, cpf: editForm.cpf, dataNascimento: editForm.dataNascimento || undefined },
        endereco: { cep: editForm.cep, logradouro: editForm.endereco, numero: editForm.numero, complemento: editForm.complemento, bairro: editForm.bairro, cidade: editForm.cidade, uf: editForm.uf },
        itens: itensFormatados,
        registroTexto: "📝 Dados da OS editados via Painel de Gestão."
      };

      await api.put(`/ordens-servico/${osDetalhada._id}`, payload);
      alert("Dados salvos com sucesso!");
      setEditMode(false);
      await recarregarOsDetalhada(osDetalhada._id);
    } catch (e) { alert("Erro ao salvar. Verifique se as datas e valores estão corretos."); console.error(e); } 
    finally { setSaving(false); }
  };

  const handleUploadFotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !osDetalhada?._id) return;

    setUploadingFotos(true);
    setUploadFotosProgress(0);
    setUploadFotosMensagem('Preparando imagens...');

    try {
      const urlsEnviadas = [];
      const empresaIdDaOS = getEmpresaIdOS(osDetalhada);

      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const etapaBase = Math.round((i / files.length) * 100);

        setUploadFotosMensagem(`Otimizando imagem ${i + 1} de ${files.length}...`);
        setUploadFotosProgress(etapaBase);

        const arquivoFinal = await prepararArquivoImagemOS(file);

        const formData = new FormData();
        formData.append('imagem', arquivoFinal);
        formData.append('empresaId', empresaIdDaOS || '');
        formData.append('osId', osDetalhada._id);
        formData.append('tipo', 'os-anexo');

        setUploadFotosMensagem(`Enviando imagem ${i + 1} de ${files.length}...`);

        const uploadResponse = await api.post('/ordens-servico/upload-anexo', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (event) => {
            if (!event.total) return;

            const progressoArquivo = event.loaded / event.total;
            const progressoTotal = Math.round(((i + progressoArquivo) / files.length) * 100);
            setUploadFotosProgress(Math.min(progressoTotal, 98));
          }
        });

        const urlSalva = uploadResponse.data?.url || uploadResponse.data?.imageUrl;

        if (!urlSalva) {
          throw new Error('Servidor não retornou o caminho da imagem.');
        }

        urlsEnviadas.push(urlSalva);
      }

      setUploadFotosMensagem('Vinculando imagens à OS...');
      setUploadFotosProgress(99);

      await api.patch(`/ordens-servico/${osDetalhada._id}/adicionar-anexos`, {
        novosAnexos: urlsEnviadas
      });

      setUploadFotosProgress(100);
      setUploadFotosMensagem('Imagens anexadas com sucesso.');

      alert(`${files.length} foto(s) anexada(s) com sucesso!`);

      await recarregarOsDetalhada(osDetalhada._id);
    } catch (error) {
      console.error('Erro no envio:', error);
      alert(error.response?.data?.error || error.message || 'Houve um erro ao enviar as imagens.');
    } finally {
      setTimeout(() => {
        setUploadingFotos(false);
        setUploadFotosProgress(0);
        setUploadFotosMensagem('');
      }, 350);

      e.target.value = null;
    }
  };

  const isFiscalActionLoading = (osId, action = null) => {
    if (!loadingFiscalAction) return false;
    if (loadingFiscalAction.osId !== osId) return false;
    return action ? loadingFiscalAction.action === action : true;
  };

  const getFiscalActionLabel = (osId, action, normalLabel, loadingLabel = 'Carregando...') => {
    return isFiscalActionLoading(osId, action) ? loadingLabel : normalLabel;
  };

  const atualizarOsDepoisDaNota = async (osId) => {
    if (!osId) return null;

    const detalhe = await api.get(`/ordens-servico/${osId}`);
    atualizarOSLocal(detalhe.data);

    return detalhe.data;
  };

  const handleOpenPrintModal = (os, e) => {
    e.stopPropagation();
    setMenuAberto(null);
    setPrintModalOs(os);
    setPrintOptions({ otica: true, lab: true, cliente: true });
  };

  const confirmGerarPDF = async () => {
    if (!printOptions.otica && !printOptions.lab && !printOptions.cliente) return alert("Selecione pelo menos uma via.");
    setLoadingPdfId(printModalOs._id);
    const viasConfig = [];
    if (printOptions.otica) viasConfig.push({ titulo: 'VIA DA ÓTICA (ARQUIVO)', signText: 'Assinatura do Cliente / Responsável' });
    if (printOptions.lab) viasConfig.push({ titulo: 'VIA DO LABORATÓRIO', signText: 'Visto de Conferência do Laboratório' });
    if (printOptions.cliente) viasConfig.push({ titulo: 'VIA DO CLIENTE (RECIBO)', signText: 'Assinatura do Vendedor / Ótica' });

    try {
      const blob = await pdf(<OsPdfDocument os={printModalOs} viasConfig={viasConfig} />).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setPrintModalOs(null);
    } catch (error) { alert("Houve um erro ao gerar as vias da OS."); console.error(error); } 
    finally { setLoadingPdfId(null); }
  };

const handleEmitirNFCe = async (os, e) => {
  e.stopPropagation();

  const jaEmitida = isNotaFiscalEmitida(os);
  const estaProcessando = isNotaFiscalProcessando(os);
  const empresaDaOS = descobrirEmpresaOficial(os, empresasApi);

  setLoadingFiscalAction({
    osId: os._id,
    action: 'visualizar'
  });

  try {
    // Se já emitiu, só abre o PDF pelo backend com token
    if (jaEmitida) {
      await baixarPdfNfce(os, { baixar: false });
      return;
    }

    if (!estaProcessando && !osEstaEntregue(os)) {
      alert('Para emitir o cupom fiscal, a OS precisa estar com status Entregue.');
      return;
    }

    if (!empresaDaOS) {
      alert('Não foi possível identificar a empresa desta OS. Abra a OS, selecione a empresa correta e salve antes de emitir.');
      return;
    }

    if (!empresaFiscalPronta(empresaDaOS)) {
      const pendencias = getEmpresaFiscalPendencias(empresaDaOS).join(', ');

      alert(`Configuração fiscal incompleta para ${getEmpresaLabel(empresaDaOS)}.\n\nPendências: ${pendencias || 'dados fiscais'}.\n\nFinalize a configuração fiscal da empresa antes de emitir NFC-e.`);
      return;
    }

    const pergunta = estaProcessando
      ? `Deseja consultar o processamento da NFC-e da OS #${os.numeroOS}?`
      : `Deseja emitir o Cupom Fiscal NFC-e da OS #${os.numeroOS} pela empresa ${getEmpresaLabel(empresaDaOS)}?`;

    if (!window.confirm(pergunta)) return;

    const response = await api.post(`/ordens-servico/${os._id}/emitir-nfce`);
    const data = response.data || {};

    const detalheAtualizado = await atualizarOsDepoisDaNota(os._id);

    if (data.pdfUrl || data.notaFiscal?.pdfUrl || (detalheAtualizado && isNotaFiscalEmitida(detalheAtualizado))) {
      await baixarPdfNfce(detalheAtualizado || os, { baixar: false });
      return;
    }

    if (response.status === 202) {
      alert(data.message || 'NFC-e enviada para processamento. Aguarde alguns segundos e clique novamente para consultar.');
      return;
    }

    alert(data.mensagem || data.message || 'Operação realizada com sucesso.');
  } catch (error) {
    const detalhe =
      error.response?.data?.detalhe ||
      error.response?.data?.message ||
      error.response?.data?.errors?.[0]?.message ||
      error.message;

    alert(`Erro na NFE.io: ${detalhe}`);
  } finally {
    setLoadingFiscalAction(null);
  }
};

  const handleBaixarPdfCupom = async (os, e) => {
    e.stopPropagation();

    if (!isNotaFiscalEmitida(os) && !os?.notaFiscal?.invoiceId) {
      alert('Ainda não existe NFC-e autorizada para baixar PDF.');
      return;
    }

    setLoadingFiscalAction({
      osId: os._id,
      action: 'pdf'
    });

    try {
      await baixarPdfNfce(os, { baixar: true });
    } finally {
      setLoadingFiscalAction(null);
    }
  };

  const handleBaixarXmlCupom = async (os, e) => {
    e.stopPropagation();

    setLoadingFiscalAction({
      osId: os._id,
      action: 'xml'
    });

    try {
      await baixarXmlNfce(os, { baixar: true });
    } finally {
      setLoadingFiscalAction(null);
    }
  };

  // O servidor já devolve apenas a página filtrada. Aqui mantemos somente o enriquecimento
  // necessário para apresentação e uma validação local de segurança.
  const { filteredData, kpis } = useMemo(() => {
    const dataFiltrada = servicos.map((os) => {
      const emp = descobrirEmpresaOficial(os, empresasApi);
      const empresaIdOficial = emp ? normalizarId(emp._id || emp.id) : getEmpresaIdOS(os);
      const lojaOficial = emp ? getEmpresaLabel(emp) : (os.loja || 'Empresa não identificada');
      const lojaCodigo = emp ? getEmpresaCodigo(emp) : (os.loja || '');
      const fiscalPronta = emp ? empresaFiscalPronta(emp) : false;
      const vendedorOficial = normalizarNomeVendedor(os.vendedor);

      return {
        ...os,
        empresaIdOficial,
        lojaOficial,
        lojaCodigo,
        empresaFiscalPronta: fiscalPronta,
        vendedorOficial
      };
    });

    let totalRevenue = 0;
    let totalReceber = 0;
    let validOrdersForTicket = 0;
    const rankingMap = {};

    dataFiltrada.forEach((os) => {
      const valor = Number(os.valorTotal) || 0;
      const faltaPagar = calcularRestante(os);
      totalRevenue += valor;
      if (faltaPagar > 0) totalReceber += faltaPagar;
      if (valor > 0) validOrdersForTicket += 1;

      const vendedor = os.vendedorOficial || 'Não informado';
      if (!rankingMap[vendedor]) rankingMap[vendedor] = { nome: vendedor, total: 0, qtd: 0 };
      rankingMap[vendedor].total += valor;
      rankingMap[vendedor].qtd += 1;
    });

    const avgTicket = validOrdersForTicket > 0 ? totalRevenue / validOrdersForTicket : 0;
    const ranking = Object.values(rankingMap).sort((a, b) => b.total - a.total).slice(0, 5);

    return {
      filteredData: dataFiltrada,
      kpis: {
        totalRevenue,
        totalReceber,
        totalOrders: paginacao.total || dataFiltrada.length,
        avgTicket,
        ranking
      }
    };
  }, [servicos, empresasApi, paginacao.total]);
  const displayData = useMemo(() => {
    const pinId = normalizarId(searchPinnedOsId);
    if (!pinId || !String(filtrosAplicados.busca || '').trim()) return filteredData;

    const indice = filteredData.findIndex((item) => normalizarId(item?._id || item?.id) === pinId);
    if (indice <= 0) return filteredData;

    return [filteredData[indice], ...filteredData.slice(0, indice), ...filteredData.slice(indice + 1)];
  }, [filteredData, searchPinnedOsId, filtrosAplicados.busca]);

  const handleSearchKeyDown = useCallback((event) => {
    if (event.key !== 'Enter') return;
    if (!String(filtros.busca || '').trim() || !displayData.length) return;
    event.preventDefault();
    const primeiroId = normalizarId(displayData[0]?._id || displayData[0]?.id);
    if (primeiroId) handleRowClick(primeiroId, true);
  }, [displayData, filtros.busca, handleRowClick]);

  const irParaPagina = useCallback((novaPagina) => {
    const paginaSegura = Math.min(Math.max(1, novaPagina), Math.max(1, paginacao.totalPaginas));
    if (paginaSegura === paginaAtual || loading) return;
    detailRequestSeqRef.current += 1;
    setPaginaAtual(paginaSegura);
    setExpandedRow(null);
    setOsDetalhada(null);
    setErroDetalhes('');
    setSearchPinnedOsId('');
    pinnedSearchQueryRef.current = '';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [paginaAtual, paginacao.totalPaginas, loading]);

  const alterarItensPorPagina = (novoLimite) => {
    detailRequestSeqRef.current += 1;
    setItensPorPagina(Number(novoLimite));
    setPaginaAtual(1);
    setExpandedRow(null);
    setOsDetalhada(null);
    setErroDetalhes('');
    setSearchPinnedOsId('');
    pinnedSearchQueryRef.current = '';
  };

  const handleExportPDF = async () => {
    if (!filteredData.length) return alert('Sem dados para exportar.');
    setIsExporting(true);
    try {
      const blob = await pdf(<ReportPdfDocument data={filteredData} kpis={kpis} periodo={`${formatDateLocal(filtros.dataInicio)} até ${formatDateLocal(filtros.dataFim)}`} />).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) { console.error(error); alert('Houve um erro ao gerar o relatório PDF.'); } 
    finally { setIsExporting(false); }
  };

  const renderShapeIcon = (shapeName) => {
     if (!shapeName) return null;
     const ShapeComponent = Icons[`Shape${shapeName === 'Retangular' ? 'Rect' : shapeName}`] || Icons[`Shape${shapeName}`];
     return ShapeComponent ? <span style={{color: 'var(--primary)', display: 'flex', alignItems: 'center'}}><ShapeComponent /></span> : null;
  };

  return (
    <div className="reports-container">
      <style>{`
        /* --- ESTILOS PREMIUM & RESPONSIVOS --- */
        .reports-container { width: 100%; max-width: 1400px; margin: 0 auto; padding: 24px; color: var(--text-main); }
        
        .reports-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; flex-wrap: wrap; gap: 16px; }
        .page-titles h2 { font-size: 22px; font-weight: 800; letter-spacing: -0.5px; margin-bottom: 4px; color: var(--text-main); }
        .page-titles p { font-size: 13px; color: var(--text-sec); }
        
        .outline-btn { border: 1px solid var(--border); background: var(--surface); padding: 10px 16px; border-radius: var(--radius-sm); cursor: pointer; display: flex; align-items: center; gap: 8px; color: var(--text-main); font-weight: 600; font-size: 13px; box-shadow: var(--shadow-sm); transition: 0.2s; }
        .outline-btn:hover:not(:disabled) { box-shadow: var(--shadow-md); border-color: var(--primary); color: var(--primary); }
        .outline-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        /* Filtros Modernizados */
        .filter-card { background: var(--surface); padding: 20px; border-radius: var(--radius-lg); border: 1px solid var(--border); margin-bottom: 24px; box-shadow: var(--shadow-sm); }
        .quick-filters { display: flex; align-items: center; gap: 8px; margin-bottom: 16px; overflow-x: auto; padding-bottom: 4px; -webkit-overflow-scrolling: touch; }
        .quick-filters::-webkit-scrollbar { height: 4px; }
        .quick-filters::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }
        
        .quick-filter-btn { background: var(--surface-hover); color: var(--text-sec); border: 1px solid var(--border); padding: 6px 14px; border-radius: 20px; font-size: 12px; font-weight: 600; cursor: pointer; transition: 0.2s; white-space: nowrap; }
        .quick-filter-btn:hover { background: var(--border); color: var(--text-main); }
        .quick-filter-btn.active { background: var(--text-main); color: var(--surface); border-color: var(--text-main); box-shadow: var(--shadow-md); }

        .clear-filter-btn { background: #FEF2F2; color: #EF4444; border: 1px dashed #FCA5A5; padding: 6px 14px; border-radius: 20px; font-size: 12px; font-weight: 700; cursor: pointer; transition: 0.2s; white-space: nowrap; display: flex; align-items: center; gap: 4px; margin-left: auto; }
        .clear-filter-btn:hover { background: #FEE2E2; border-color: #EF4444; }

        .filter-grid-top { display: grid; grid-template-columns: 2fr 1fr; gap: 16px; margin-bottom: 16px; }
        .filter-grid-bottom { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        
        .input-group { display: flex; align-items: center; gap: 10px; background: var(--surface-hover); border: 1px solid var(--border); padding: 10px 14px; border-radius: var(--radius-md); transition: 0.2s; }
        .input-group:focus-within { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.15); background: var(--surface); }
        .input-group input, .input-group select { border: none; background: transparent; outline: none; width: 100%; font-size: 13px; font-weight: 500; color: var(--text-main); }
        .input-group svg { color: var(--text-muted); flex-shrink: 0; }
        
        .date-range { display: flex; align-items: center; gap: 10px; }
        .date-range .input-group { flex: 1; }
        .date-range span { color: var(--text-muted); font-weight: bold; }

        /* KPI Dashboard Grid */
        .dash-kpi-container { display: grid; grid-template-columns: 2fr 1fr; gap: 24px; margin-bottom: 24px; }
        .kpi-cards-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; align-content: start; }
        
        .kpi-card { background: var(--surface); border-radius: var(--radius-lg); border: 1px solid var(--border); padding: 20px; display: flex; align-items: center; gap: 16px; box-shadow: var(--shadow-sm); transition: 0.2s; }
        .kpi-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
        .kpi-icon { border-radius: 12px; display: grid; place-items: center; width: 48px; height: 48px; }
        .kpi-texts { display: flex; flex-direction: column; }
        .kpi-label { font-size: 11px; font-weight: 700; color: var(--text-sec); text-transform: uppercase; letter-spacing: 0.5px; }
        .kpi-val { font-size: 22px; font-weight: 800; color: var(--text-main); margin-top: 2px; }

        .ranking-card { background: var(--surface); border-radius: var(--radius-lg); border: 1px solid var(--border); padding: 20px; display: flex; flex-direction: column; box-shadow: var(--shadow-sm); height: 100%; }
        .ranking-title { font-size: 14px; font-weight: 800; color: var(--text-main); display: flex; align-items: center; gap: 8px; margin-bottom: 16px; }
        .ranking-list { display: flex; flex-direction: column; gap: 10px; overflow-y: auto; flex: 1; }
        .ranking-item { display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; background: var(--surface-hover); border-radius: var(--radius-sm); border: 1px solid var(--border-soft); }

        /* --- TABELA RESPONSIVA / CARDS --- */
        .list-container { background: var(--surface); border-radius: var(--radius-lg); border: 1px solid var(--border); box-shadow: var(--shadow-sm); }
        .list-header { display: grid; grid-template-columns: 40px 1.5fr 1.5fr 2fr 1.5fr 1.5fr 60px; padding: 16px 20px; background: var(--surface-hover); border-bottom: 1px solid var(--border); border-radius: var(--radius-lg) var(--radius-lg) 0 0; }
        .list-th { font-size: 11px; font-weight: 800; color: var(--text-sec); text-transform: uppercase; letter-spacing: 0.5px; }
        
        .list-body { display: flex; flex-direction: column; }
        .list-row-wrapper { border-bottom: 1px solid var(--border-soft); }
        .list-row-wrapper:last-child { border-bottom: none; }
        
        .list-row { display: grid; grid-template-columns: 40px 1.5fr 1.5fr 2fr 1.5fr 1.5fr 60px; padding: 16px 20px; align-items: center; cursor: pointer; transition: 0.2s; background: transparent; }
        .list-row:hover { background: var(--surface-hover); }
        .list-row.is-expanded { background: #F8FAFC; border-left: 3px solid var(--primary); padding-left: 17px; }

        .results-toolbar { display:flex; align-items:center; justify-content:space-between; gap:16px; padding:12px 16px; border:1px solid #e8eaed; border-bottom:0; background:#fff; }
        .results-summary { display:flex; flex-direction:column; gap:2px; }
        .results-summary strong { font-size:13px; color:#202124; font-weight:700; }
        .results-summary span { font-size:11px; color:#5f6368; }
        .page-size-control { display:flex; align-items:center; gap:8px; color:#5f6368; font-size:12px; font-weight:600; }
        .page-size-control select { height:34px; border:1px solid #dadce0; border-radius:8px; background:#fff; color:#202124; padding:0 28px 0 10px; outline:none; }
        .pagination-bar { display:flex; align-items:center; justify-content:space-between; gap:16px; padding:14px 16px; border:1px solid #e8eaed; border-top:0; background:#fff; }
        .pagination-info { font-size:12px; color:#5f6368; font-weight:600; }
        .pagination-actions { display:flex; align-items:center; gap:8px; }
        .pagination-button { min-width:38px; height:36px; padding:0 12px; border:1px solid #dadce0; border-radius:9px; background:#fff; color:#202124; font-size:12px; font-weight:700; cursor:pointer; }
        .pagination-button:hover:not(:disabled) { background:#f8fafd; border-color:#1a73e8; color:#1a73e8; }
        .pagination-button:disabled { opacity:.42; cursor:not-allowed; }
        .pagination-current { min-width:88px; text-align:center; font-size:12px; color:#3c4043; font-weight:700; }
        .inline-error { padding:14px 16px; border:1px solid #fecaca; background:#fff7f7; color:#b91c1c; display:flex; align-items:center; justify-content:space-between; gap:12px; font-size:13px; font-weight:600; margin-bottom:16px; }
        .inline-error button { border:0; background:#b91c1c; color:#fff; border-radius:8px; padding:8px 12px; font-weight:700; cursor:pointer; }
        
        .col-icon { color: var(--text-muted); transition: 0.3s; display: grid; place-items: center; }
        .is-expanded .col-icon { transform: rotate(180deg); color: var(--primary); }
        
        .text-main-cell { font-size: 14px; font-weight: 700; color: var(--text-main); display: block; }
        .text-sub-cell { font-size: 11px; font-weight: 500; color: var(--text-sec); display: block; margin-top: 2px; }
        
        .badge-nfce { display: inline-flex; align-items: center; gap: 4px; background: #ECFDF5; color: #059669; padding: 2px 6px; border-radius: 4px; font-size: 9px; font-weight: 800; margin-top: 4px; }
        .cv-badge { background: #EEF2FF; color: #4F46E5; padding: 3px 6px; border-radius: 6px; font-size: 10.5px; font-weight: 700; display: inline-block; margin-top: 4px; }

        /* Painel Expandido Interno */
        .expanded-panel { padding: 24px; background: #F8FAFC; border-top: 1px solid var(--border); border-bottom: 2px solid var(--border); animation: slideDown 0.3s ease-out; }
        .expanded-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 1px solid var(--border); flex-wrap: wrap; gap: 12px;}
        .expanded-header h3 { font-size: 18px; font-weight: 800; color: var(--text-main); }
        .action-buttons { display: flex; gap: 10px; flex-wrap: wrap; }
        
        .btn-sm { padding: 8px 14px; border-radius: var(--radius-sm); font-size: 12px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 6px; border: none; transition: 0.2s; }
        .btn-wa { background: #DCFCE7; color: #166534; } .btn-wa:hover { background: #BBF7D0; }
        .btn-del { background: #FEE2E2; color: #DC2626; } .btn-del:hover { background: #FECACA; }
        .btn-edit { background: #E0E7FF; color: #4F46E5; } .btn-edit:hover { background: #C7D2FE; }
        .btn-save { background: #10B981; color: white; box-shadow: 0 4px 6px rgba(16, 185, 129, 0.2); } .btn-save:hover { background: #059669; }
        .btn-cancel { background: var(--border); color: var(--text-sec); } .btn-cancel:hover { background: #CBD5E1; color: var(--text-main); }

        .status-quick-bar { background: var(--surface); border: 1px solid var(--border); padding: 10px 14px; border-radius: var(--radius-md); margin-bottom: 20px; display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
        .status-btn { padding: 6px 12px; border-radius: 20px; font-size: 10px; font-weight: 700; cursor: pointer; border: 1px solid transparent; background: var(--surface-hover); color: var(--text-sec); transition: 0.2s; }
        .status-btn:hover { background: var(--border); }
        .status-btn.active { background: var(--text-main); color: var(--surface); box-shadow: var(--shadow-sm); }

        /* GRID LADO A LADO - OTIMIZADO PARA MAIOR CONTEÚDO */
        .details-grid { display: grid; grid-template-columns: 1fr 1.6fr 1fr; gap: 16px; align-items: start; }
        .grid-col { display: flex; flex-direction: column; gap: 16px; }

        .detail-card { background: var(--surface); padding: 16px; border-radius: var(--radius-md); border: 1px solid var(--border); box-shadow: var(--shadow-sm); }
        .detail-card-title { font-size: 11px; font-weight: 800; color: var(--text-sec); text-transform: uppercase; display: flex; align-items: center; gap: 6px; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px dashed var(--border); }
        
        .info-group { margin-bottom: 8px; }
        .info-label { display: block; font-size: 9px; font-weight: 800; color: var(--text-muted); text-transform: uppercase; margin-bottom: 2px; }
        .info-val { font-size: 12px; font-weight: 600; color: var(--text-main); word-break: break-word; }
        .info-row { display: flex; gap: 12px; } .info-row > div { flex: 1; }

        .finance-box { background: var(--surface-hover); border: 1px solid var(--border); border-radius: var(--radius-md); padding: 12px; }
        .finance-line { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
        .finance-val { font-size: 16px; font-weight: 800; }
        .pag-history-item { display: flex; justify-content: space-between; align-items: center; background: var(--surface); padding: 6px 10px; border-radius: var(--radius-sm); border: 1px solid var(--border); margin-bottom: 4px; }

        .payment-entry-box {
          margin-top: 12px;
          border-top: 1px dashed var(--border);
          padding-top: 12px;
          display: grid;
          gap: 8px;
        }

        .payment-entry-title {
          display: block;
          font-size: 9px;
          font-weight: 800;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: .04em;
        }

        .payment-entry-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 78px;
          gap: 8px;
        }

        .btn-add-payment {
          height: 34px;
          border: 1px solid #bbf7d0;
          background: #ecfdf5;
          color: #047857;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 800;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          cursor: pointer;
          transition: .2s;
        }

        .btn-add-payment:hover:not(:disabled) {
          background: #d1fae5;
          border-color: #86efac;
        }

        .btn-add-payment:disabled {
          opacity: .55;
          cursor: wait;
        }

        .status-btn.financial-locked {
          border-color: #fecaca;
          background: #fff7ed;
          color: #c2410c;
        }

        .log-container { max-height: 200px; overflow-y: auto; padding-right: 8px; }
        .log-item { border-left: 2px solid var(--border); padding-left: 10px; margin-left: 4px; padding-bottom: 12px; }
        .log-date { font-size: 9px; color: var(--primary); font-weight: 800; margin-bottom: 2px; }
        .log-text { font-size: 11px; color: var(--text-sec); font-weight: 500; line-height: 1.3; }

        .gallery-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(80px, 1fr)); gap: 8px; }
        .photo-thumb { aspect-ratio: 1; border-radius: var(--radius-sm); overflow: hidden; border: 1px solid var(--border); position: relative; cursor: zoom-in; transition: 0.2s; }
        .photo-thumb:hover { transform: scale(1.05); box-shadow: var(--shadow-md); border-color: var(--primary); }
        .photo-thumb img { width: 100%; height: 100%; object-fit: cover; }
        .btn-remove-foto { position: absolute; top: 4px; right: 4px; background: rgba(239, 68, 68, 0.9); color: white; border: none; border-radius: 50%; width: 20px; height: 20px; display: grid; place-items: center; cursor: pointer; z-index: 10; backdrop-filter: blur(4px); }

        /* Edição e Inputs */
        .edit-input { width: 100%; padding: 8px 10px; border: 1px solid var(--border); border-radius: var(--radius-sm); font-size: 12px; font-weight: 500; color: var(--text-main); background: var(--surface); transition: 0.2s; margin-top: 4px; }
        .edit-input:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.15); outline: none; }
        .edit-label { display: block; font-size: 10px; font-weight: 700; color: var(--text-sec); margin-bottom: 10px; width: 100%; }

        .grau-table { width: 100%; border-collapse: collapse; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-sm); overflow: hidden; }
        .grau-table th { font-size: 9px; text-transform: uppercase; color: var(--text-muted); font-weight: 800; padding: 6px; background: var(--surface-hover); border-bottom: 1px solid var(--border); }
        .grau-table td { padding: 4px; border-bottom: 1px solid var(--border-soft); }
        .grau-input { width: 100%; padding: 6px; border: 1px solid transparent; border-radius: 4px; font-size: 11px; text-align: center; font-weight: 600; color: var(--text-main); background: transparent; transition: 0.2s; }
        .grau-input:hover { background: var(--surface-hover); }
        .grau-input:focus { border-color: var(--primary); background: var(--surface); box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.1); outline: none; }

        /* Modais */
        .modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.5); backdrop-filter: blur(4px); z-index: 99999; display: flex; alignItems: center; justify-content: center; padding: 16px; }
        .modal-content { background: var(--surface); border-radius: var(--radius-lg); padding: 24px; width: 100%; max-width: 450px; box-shadow: var(--shadow-glass); animation: scaleIn 0.2s cubic-bezier(0.16, 1, 0.3, 1); }
        
        .checkbox-container { display: flex; align-items: center; gap: 10px; padding: 12px; background: var(--surface-hover); border: 1px solid var(--border); border-radius: var(--radius-sm); cursor: pointer; transition: 0.2s; margin-bottom: 8px; font-size: 13px; font-weight: 500;}
        .checkbox-container:hover { border-color: var(--primary); background: #EFF6FF; }
        .checkbox-container.active { border-color: var(--primary); background: #EFF6FF; color: var(--primary); font-weight: 700; }

        .btn-acao { background: transparent; border: none; cursor: pointer; padding: 6px; border-radius: 8px; color: var(--text-muted); transition: 0.2s; display: grid; place-items: center; }
        .btn-acao:hover { background: var(--surface-hover); color: var(--text-main); }
        
        .dropdown-menu { position: absolute; right: 30px; top: 30px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-md); box-shadow: var(--shadow-md); z-index: 100; width: 220px; padding: 6px; animation: popIn 0.2s ease-out; }
        .dropdown-item { display: flex; align-items: center; gap: 8px; background: transparent; border: none; padding: 10px 12px; text-align: left; width: 100%; cursor: pointer; font-size: 12px; font-weight: 600; color: var(--text-sec); border-radius: 6px; transition: 0.2s; }
        .dropdown-item:hover { background: var(--surface-hover); color: var(--text-main); }

        .status-pill { padding: 4px 10px; border-radius: 20px; font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block; text-align: center;}
        
        .medidas-box { display: flex; gap: 8px; font-size: 10px; font-weight: 700; color: var(--text-sec); background: var(--surface-hover); padding: 6px 10px; border-radius: 6px; border: 1px solid var(--border); align-items: center;}
        .medida-item { display: flex; gap: 4px; align-items: center; }
        .medida-val { color: var(--primary); font-weight: 800;}

        /* 📱 RESPONSIVIDADE MOBILE FIRST APRIMORADA */
        .desktop-only { display: grid; }
        .mobile-only { display: none; }

        @media (max-width: 1024px) {
          .dash-kpi-container { grid-template-columns: 1fr; }
          .filter-grid-top { grid-template-columns: 1fr; }
          .filter-grid-bottom { grid-template-columns: repeat(2, 1fr); }
          .details-grid { grid-template-columns: 1fr; } 
        }

        @media (max-width: 768px) {
          .results-toolbar, .pagination-bar { align-items:stretch; flex-direction:column; }
          .page-size-control, .pagination-actions { justify-content:space-between; width:100%; }
          .pagination-button { flex:1; }
          .reports-container { padding: 16px 12px; }
          .desktop-only { display: none !important; }
          .mobile-only { display: flex !important; }
          
          .reports-header { flex-direction: column; align-items: stretch; }
          .outline-btn { justify-content: center; }
          
          .filter-grid-bottom { grid-template-columns: 1fr; }
          .date-range { flex-direction: column; align-items: stretch; }
          .date-range span { display: none; }
          
          .kpi-cards-grid { grid-template-columns: 1fr; }
          .kpi-card { padding: 16px; }
          
          .list-header { display: none; }
          
          .list-row { 
            display: flex; flex-direction: column; align-items: flex-start; padding: 16px; gap: 12px; position: relative; 
            background: var(--surface); border-radius: var(--radius-md); border: 1px solid var(--border); margin-bottom: 12px; box-shadow: var(--shadow-sm);
          }
          
          .list-row.is-expanded { border-left-width: 1px; border-color: var(--primary); box-shadow: var(--shadow-md); border-bottom-left-radius: 0; border-bottom-right-radius: 0; margin-bottom: 0; }

          .list-row .col-actions { position: absolute; top: 12px; right: 12px; }
          .list-row .col-os { width: 100%; border-bottom: 1px dashed var(--border); padding-bottom: 8px; }
          .list-row .col-cliente { width: 100%; }
          .list-row .col-data { display: flex; justify-content: space-between; width: 100%; align-items: center; }
          .list-row .col-status { width: 100%; display: flex; justify-content: flex-start; margin-top: 4px; }
          .list-row .col-valor { width: 100%; display: flex; justify-content: space-between; align-items: center; background: var(--surface-hover); padding: 10px; border-radius: var(--radius-sm); margin-top: 4px;}
          .list-row .col-valor .text-main-cell { font-size: 16px; }
          .list-row .col-icon { display: none; }

          .expanded-panel { padding: 16px; border-radius: 0 0 var(--radius-md) var(--radius-md); border: 1px solid var(--border); border-top: none; margin-bottom: 16px; }
          
          .expanded-header { flex-direction: column; align-items: stretch; }
          .action-buttons { flex-direction: column; }
          .action-buttons button { justify-content: center; width: 100%; }
          
          .details-grid { grid-template-columns: 1fr; }
          .info-row { flex-direction: column; gap: 12px; }
          
          .status-quick-bar { justify-content: center; }
          .status-btn { flex: 1; text-align: center; }
        }

        @keyframes slideDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        @keyframes popIn { 0% { opacity: 0; transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* =========================================================
           LUMIX CLEAN / MULTIEMPRESA OVERRIDES
        ========================================================= */
        .reports-container {
          --primary: #1a73e8;
          --surface: #ffffff;
          --surface-hover: #f8fafd;
          --text-main: #202124;
          --text-sec: #5f6368;
          --text-muted: #80868b;
          --border: #dadce0;
          --border-soft: #edf0f3;
          --radius-sm: 10px;
          --radius-md: 12px;
          --radius-lg: 14px;
          --shadow-sm: none;
          --shadow-md: none;
          --shadow-lg: none;
          max-width: 1180px;
          padding: 28px 20px 42px;
          background: #f8fafd;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, Arial, sans-serif;
          -webkit-font-smoothing: antialiased;
          text-rendering: geometricPrecision;
        }

        .reports-header {
          margin-bottom: 20px;
          align-items: flex-end;
        }

        .page-titles h2 {
          font-size: 30px;
          line-height: 1.12;
          font-weight: 500;
          letter-spacing: -0.035em;
          color: #202124;
        }

        .page-titles p {
          color: #5f6368;
          font-size: 14px;
        }

        .outline-btn,
        .quick-filter-btn,
        .clear-filter-btn,
        .btn-sm,
        .btn-acao {
          box-shadow: none !important;
        }

        .filter-card,
        .kpi-card,
        .ranking-card,
        .list-container,
        .detail-card,
        .status-quick-bar {
          box-shadow: none !important;
          border-color: #dadce0;
        }

        .filter-card {
          padding: 14px;
          border-radius: 14px;
        }

        .quick-filter-btn {
          padding: 6px 12px;
          border-radius: 999px;
          font-weight: 600;
        }

        .quick-filter-btn.active {
          background: #111318;
          border-color: #111318;
        }

        .clear-filter-btn {
          margin-left: 0;
          background: #fff;
          border: 1px solid #dadce0;
          color: #5f6368;
        }

        .input-group {
          min-height: 40px;
          border-radius: 999px;
          background: #fff;
          padding: 0 12px;
        }

        .filter-grid-bottom {
          grid-template-columns: repeat(4, minmax(0, 1fr));
        }

        .dash-kpi-container {
          grid-template-columns: minmax(0, 1fr) 340px;
          gap: 14px;
        }

        .kpi-cards-grid {
          gap: 10px;
        }

        .kpi-card {
          padding: 14px;
          border-radius: 14px;
          gap: 12px;
        }

        .kpi-card:hover {
          transform: none;
        }

        .kpi-icon {
          width: 38px;
          height: 38px;
          border-radius: 11px;
        }

        .kpi-val {
          font-size: 20px;
          font-weight: 600;
          letter-spacing: -0.02em;
        }

        .ranking-card {
          padding: 14px;
          border-radius: 14px;
        }

        .ranking-item {
          background: #fff;
          border-color: #edf0f3;
        }

        .list-container {
          border-radius: 14px;
          overflow: hidden;
        }

        .list-header {
          padding: 12px 14px;
          border-radius: 0;
          background: #f8fafd;
        }

        .list-row {
          padding: 13px 14px;
        }

        .list-row.is-expanded {
          background: #fbfcfe;
          border-left: 2px solid #1a73e8;
          padding-left: 12px;
        }

        .badge-nfce {
          border-radius: 999px;
          padding: 3px 7px;
          font-size: 10px;
          letter-spacing: 0;
        }

        .badge-nfce-ready {
          background: #eef5ff;
          color: #1a73e8;
          border: 1px solid #d2e3fc;
        }

        .expanded-panel {
          background: #f8fafd;
          padding: 18px;
        }

        .expanded-header h3 {
          font-size: 17px;
          font-weight: 600;
        }

        .details-grid {
          grid-template-columns: minmax(220px, 1fr) minmax(360px, 1.45fr) minmax(220px, 1fr);
          gap: 12px;
        }

        .detail-card {
          padding: 14px;
          border-radius: 14px;
        }

        .btn-edit,
        .btn-wa,
        .btn-save,
        .btn-cancel,
        .btn-del {
          border-radius: 999px;
        }

        .btn-save {
          background: #111318;
        }

        .edit-input {
          border-color: #dadce0 !important;
          background: #fff !important;
          border-radius: 9px !important;
        }

        .dropdown-menu {
          border: 1px solid #dadce0 !important;
          box-shadow: 0 18px 48px rgba(32,33,36,.14) !important;
          border-radius: 12px !important;
          overflow: visible !important;
        }

        .dropdown-item {
          font-size: 12px !important;
        }

        @media (max-width: 1100px) {
          .dash-kpi-container {
            grid-template-columns: 1fr;
          }

          .details-grid {
            grid-template-columns: 1fr;
          }

          .payment-entry-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 860px) {
          .reports-container {
            padding: 20px 12px 34px;
          }

          .filter-grid-top,
          .filter-grid-bottom {
            grid-template-columns: 1fr;
          }

          .date-range {
            flex-direction: column;
            align-items: stretch;
          }

          .date-range span {
            display: none;
          }

          .dash-kpi-container,
          .kpi-cards-grid {
            grid-template-columns: 1fr;
          }

          .list-row {
            grid-template-columns: 1fr;
            gap: 10px;
            border-bottom: 1px solid #edf0f3;
          }
        }


        /* Ações fiscais / dropdown corrigido */
        .list-container,
        .list-body {
          overflow: visible !important;
        }

        .list-row-wrapper {
          position: relative;
          overflow: visible !important;
          isolation: auto;
        }

        .list-row-wrapper.menu-open {
          z-index: 9999;
        }

        .list-row {
          overflow: visible !important;
        }

        .action-cell {
          position: relative;
          display: grid;
          place-items: center;
          z-index: 5;
        }

        .dropdown-menu {
          right: 8px !important;
          top: 38px !important;
          z-index: 99999 !important;
          width: 238px !important;
          padding: 7px !important;
          background: #fff !important;
          border: 1px solid #dadce0 !important;
          border-radius: 12px !important;
          box-shadow: 0 18px 48px rgba(32,33,36,.18) !important;
          overflow: visible !important;
        }

        .dropdown-menu::before {
          content: '';
          position: absolute;
          top: -6px;
          right: 14px;
          width: 10px;
          height: 10px;
          background: #fff;
          border-left: 1px solid #dadce0;
          border-top: 1px solid #dadce0;
          transform: rotate(45deg);
        }

        .dropdown-divider {
          height: 1px;
          margin: 5px 4px;
          background: #edf0f3;
        }

        .dropdown-item {
          min-height: 36px;
          position: relative;
          z-index: 1;
        }

        .dropdown-item:disabled {
          opacity: .48;
          cursor: not-allowed;
        }

        .dropdown-item.primary-action {
          color: #111318 !important;
          font-weight: 700 !important;
          background: #f8fafd;
        }

        .dropdown-item.primary-action:hover {
          background: #eef5ff !important;
          color: #1a73e8 !important;
        }

        .mini-loader {
          width: 15px;
          height: 15px;
          border: 2px solid #dadce0;
          border-top-color: #1a73e8;
          border-radius: 50%;
          display: inline-block;
          animation: spin .75s linear infinite;
          flex-shrink: 0;
        }

        .btn-acao:disabled {
          opacity: .55;
          cursor: wait;
        }


        .badge-nfce-waiting {
          background: #f8fafd;
          color: #5f6368;
          border: 1px solid #dadce0;
        }

        .dropdown-item[disabled].primary-action {
          background: #f8fafd !important;
          color: #9aa0a6 !important;
          opacity: .72;
        }


        .detail-loading-clean {
          min-height: 220px;
          display: grid;
          place-items: center;
          align-content: center;
          gap: 9px;
          padding: 34px 18px;
          text-align: center;
          color: var(--text-sec);
        }
        .detail-loading-clean .mini-loader {
          width: 26px;
          height: 26px;
          border-width: 3px;
          margin-bottom: 3px;
        }
        .detail-loading-clean strong {
          color: var(--text-main);
          font-size: 15px;
          font-weight: 700;
        }
        .detail-loading-clean p {
          margin: 0;
          font-size: 12px;
          color: var(--text-sec);
          font-weight: 500;
        }
        .detail-loading-lines {
          width: min(320px, 100%);
          display: grid;
          gap: 7px;
          margin-top: 10px;
        }
        .detail-loading-lines i {
          height: 8px;
          border-radius: 999px;
          background: #edf0f3;
          animation: cleanPulse 1.1s ease-in-out infinite;
        }
        .detail-loading-lines i:nth-child(2) { width: 82%; margin: 0 auto; animation-delay: .12s; }
        .detail-loading-lines i:nth-child(3) { width: 62%; margin: 0 auto; animation-delay: .24s; }
        @keyframes cleanPulse { 0%,100% { opacity: .48; } 50% { opacity: 1; } }

        .os-priority-strip {
          display: grid;
          grid-template-columns: 1.3fr repeat(3, 1fr);
          gap: 8px;
          margin-bottom: 12px;
        }
        .priority-item {
          min-width: 0;
          background: #fff;
          border: 1px solid #dadce0;
          border-radius: 14px;
          padding: 12px;
        }
        .priority-item span {
          display: block;
          color: #5f6368;
          font-size: 10px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .045em;
          margin-bottom: 5px;
        }
        .priority-item strong {
          display: block;
          color: #202124;
          font-size: 15px;
          font-weight: 700;
          line-height: 1.18;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .priority-item.primary { border-left: 3px solid #1a73e8; }
        .priority-item.success { border-left: 3px solid #188038; background: #f7fcf9; }
        .priority-item.danger { border-left: 3px solid #d93025; background: #fff8f7; }
        .priority-item.danger strong { color: #b3261e; }
        .priority-item.success strong { color: #137333; }

        .nfce-guidance-panel {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          padding: 13px 14px;
          margin-bottom: 12px;
          border: 1px solid #d2e3fc;
          border-radius: 14px;
          background: #f7fbff;
        }
        .nfce-copy span {
          display: block;
          color: #1a73e8;
          font-size: 10px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .05em;
          margin-bottom: 3px;
        }
        .nfce-copy strong {
          display: block;
          color: #202124;
          font-size: 15px;
          font-weight: 700;
        }
        .nfce-copy small {
          display: block;
          color: #5f6368;
          font-size: 12px;
          font-weight: 500;
          margin-top: 3px;
        }
        .nfce-actions-inline {
          display: flex;
          align-items: center;
          gap: 7px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        .nfce-main-action,
        .nfce-secondary-action {
          border: 1px solid #dadce0;
          border-radius: 999px;
          background: #fff;
          color: #202124;
          min-height: 36px;
          padding: 0 13px;
          font-size: 12px;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          cursor: pointer;
        }
        .nfce-main-action {
          background: #1a73e8;
          border-color: #1a73e8;
          color: #fff;
        }
        .nfce-main-action:disabled,
        .nfce-secondary-action:disabled {
          opacity: .5;
          cursor: not-allowed;
        }
        .smart-confirm-modal { max-width: 520px; }
        .smart-confirm-head {
          display: grid;
          grid-template-columns: 42px 1fr 34px;
          gap: 12px;
          align-items: start;
          margin-bottom: 18px;
        }
        .smart-confirm-icon {
          width: 42px;
          height: 42px;
          display: grid;
          place-items: center;
          border-radius: 14px;
          background: #e8f0fe;
          color: #1a73e8;
        }
        .smart-confirm-head h3 {
          margin: 0 0 5px;
          font-size: 18px;
          color: var(--text-main);
          font-weight: 700;
          letter-spacing: -0.02em;
        }
        .smart-confirm-head p {
          margin: 0;
          color: var(--text-sec);
          font-size: 13px;
          line-height: 1.45;
          font-weight: 500;
        }
        .smart-confirm-client {
          padding: 12px;
          border: 1px solid #edf0f3;
          border-radius: 14px;
          background: #f8fafd;
          margin-bottom: 14px;
        }
        .smart-confirm-client span {
          display: block;
          color: #5f6368;
          font-size: 10px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .05em;
          margin-bottom: 4px;
        }
        .smart-confirm-client strong {
          display: block;
          color: #202124;
          font-size: 15px;
          font-weight: 700;
        }
        .smart-confirm-client small {
          color: #5f6368;
          font-size: 12px;
          font-weight: 500;
        }
        .smart-confirm-actions {
          display: grid;
          gap: 9px;
        }
        .smart-action {
          min-height: 44px;
          border-radius: 14px;
          border: 1px solid #dadce0;
          background: #fff;
          color: #202124;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .smart-action.primary {
          background: #111318;
          color: #fff;
          border-color: #111318;
        }

        @media (max-width: 768px) {
          .os-priority-strip { grid-template-columns: 1fr 1fr; }
          .nfce-guidance-panel { align-items: stretch; flex-direction: column; }
          .nfce-actions-inline { justify-content: stretch; }
          .nfce-main-action { width: 100%; }
          .nfce-secondary-action { flex: 1; }
        }


        /* V2: tela aberta, sem quadrado dentro de quadrado */
        .list-container { background:#fff !important; border:1px solid #e8eaed !important; border-radius:0 !important; box-shadow:none !important; overflow:visible !important; }
        .list-header { background:#fff !important; border-radius:0 !important; border-bottom:1px solid #e8eaed !important; }
        .list-row-wrapper { background:#fff !important; border-bottom:1px solid #e8eaed !important; overflow:visible !important; }
        .list-row { background:#fff !important; }
        .list-row:hover { background:#fbfcfe !important; }
        .list-row.is-expanded { background:#fff !important; border-left:0 !important; padding-left:14px !important; box-shadow:inset 3px 0 0 #1a73e8; }
        .expanded-panel { background:#fff !important; border:0 !important; border-top:1px solid #e8eaed !important; border-bottom:1px solid #e8eaed !important; padding:0 18px 22px !important; }
        .expanded-header { margin:0 !important; padding:16px 0 12px !important; border-bottom:1px solid #e8eaed !important; }
        .expanded-header h3 { font-size:18px !important; font-weight:700 !important; letter-spacing:-.02em; }
        .action-buttons { gap:8px !important; }
        .btn-sm { min-height:36px; border-radius:9px !important; box-shadow:none !important; font-weight:700; }
        .os-priority-strip { margin:0 !important; padding:12px 0 !important; border-bottom:1px solid #e8eaed; gap:0 !important; grid-template-columns:1.35fr repeat(3,1fr); }
        .priority-item { border:0 !important; border-right:1px solid #e8eaed !important; border-radius:0 !important; background:transparent !important; box-shadow:none !important; padding:5px 14px !important; }
        .priority-item:first-child { padding-left:0 !important; }
        .priority-item:last-child { border-right:0 !important; }
        .priority-item.primary,.priority-item.success,.priority-item.danger { border-left:0 !important; background:transparent !important; }
        .priority-item span { font-size:10px !important; font-weight:700 !important; margin-bottom:4px !important; }
        .priority-item strong { font-size:15px !important; font-weight:700 !important; }
        .nfce-guidance-panel { margin:0 !important; padding:14px 0 !important; background:transparent !important; border:0 !important; border-bottom:1px solid #e8eaed !important; border-radius:0 !important; box-shadow:none !important; }
        .nfce-main-action,.nfce-secondary-action { border-radius:9px !important; box-shadow:none !important; }
        .status-quick-bar { margin:0 !important; padding:12px 0 !important; border:0 !important; border-bottom:1px solid #e8eaed !important; border-radius:0 !important; background:transparent !important; box-shadow:none !important; }
        .details-grid { padding-top:16px; gap:0 !important; grid-template-columns:minmax(220px,1fr) minmax(360px,1.55fr) minmax(220px,1fr) !important; border-bottom:1px solid #e8eaed; }
        .grid-col { gap:0 !important; padding:0 16px; border-right:1px solid #e8eaed; }
        .grid-col:first-child { padding-left:0; }
        .grid-col:last-child { padding-right:0; border-right:0; }
        .detail-card { background:transparent !important; border:0 !important; border-bottom:1px solid #f0f2f5 !important; border-radius:0 !important; box-shadow:none !important; padding:0 0 16px !important; margin:0 0 16px !important; }
        .detail-card:last-child { border-bottom:0 !important; }
        .detail-card-title { border-bottom:0 !important; padding:0 !important; margin:0 0 10px !important; font-size:11px !important; font-weight:700 !important; color:#5f6368 !important; letter-spacing:.04em; }
        .info-group { margin-bottom:9px !important; }
        .info-label { font-size:9.5px !important; font-weight:700 !important; color:#80868b !important; }
        .info-val { font-size:12.5px !important; font-weight:600 !important; color:#202124 !important; }
        .finance-box { background:transparent !important; border:0 !important; border-radius:0 !important; padding:0 !important; box-shadow:none !important; }
        .finance-line { padding:7px 0; margin:0 !important; border-bottom:1px solid #f0f2f5; }
        .pag-history-item { background:transparent !important; border:0 !important; border-bottom:1px solid #f0f2f5 !important; border-radius:0 !important; padding:8px 0 !important; margin:0 !important; }
        .expanded-panel > .detail-card[style] { margin-top:16px !important; background:transparent !important; border:0 !important; border-radius:0 !important; box-shadow:none !important; }
        .mobile-os-summary { display:none; }

        @media (max-width:980px) {
          .details-grid { grid-template-columns:1fr !important; border-bottom:0; }
          .grid-col { padding:0 !important; border-right:0 !important; }
        }

        @media (max-width:768px) {
          .reports-container { padding:14px 0 28px !important; }
          .reports-header,.filter-card,.dash-kpi-container { margin-left:12px; margin-right:12px; }
          .list-container { border-left:0 !important; border-right:0 !important; }
          .list-row { padding:12px 14px !important; }
          .expanded-panel { padding:0 14px 18px !important; }
          .expanded-header { align-items:stretch !important; gap:10px !important; }
          .expanded-header h3 { font-size:16px !important; }
          .action-buttons { display:grid !important; grid-template-columns:1fr 1fr; }
          .action-buttons .btn-sm { justify-content:center; min-width:0; font-size:11.5px; }
          .os-priority-strip,.nfce-guidance-panel,.details-grid,.expanded-panel > .detail-card[style] { display:none !important; }
          .status-quick-bar { padding:10px 0 !important; overflow-x:auto; }
          .status-quick-bar > strong { width:100%; }
          .mobile-os-summary { display:block; padding:12px 0 4px; border-bottom:1px solid #e8eaed; }
          .mobile-summary-head { display:flex; align-items:center; justify-content:space-between; padding-bottom:10px; border-bottom:1px solid #f0f2f5; }
          .mobile-summary-head span { color:#5f6368; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; }
          .mobile-summary-head strong { color:#1a73e8; font-size:15px; font-weight:800; }
          .mobile-summary-main,.mobile-product-summary { display:grid; grid-template-columns:1fr 1fr; gap:0; border-bottom:1px solid #f0f2f5; }
          .mobile-summary-main > div,.mobile-product-summary > div { padding:11px 10px 11px 0; border-right:1px solid #f0f2f5; min-width:0; }
          .mobile-summary-main > div:nth-child(even),.mobile-product-summary > div:nth-child(even),.mobile-product-summary > div:last-child { border-right:0; padding-left:10px; }
          .mobile-product-summary > div:last-child { grid-column:1/-1; padding-left:0; border-top:1px solid #f0f2f5; }
          .mobile-os-summary span { display:block; color:#80868b; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; margin-bottom:4px; }
          .mobile-os-summary strong { display:block; color:#202124; font-size:13px; font-weight:700; line-height:1.25; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
          .mobile-os-summary strong.danger { color:#b3261e; }
          .mobile-os-summary strong.success { color:#137333; }
          .mobile-summary-note { margin-top:12px; padding:11px 12px; border-radius:10px; background:#f8fafd; border:1px solid #e8eaed; }
          .mobile-summary-note strong { white-space:normal; font-size:12px; color:#202124; margin-bottom:4px; }
          .mobile-summary-note span { margin:0; text-transform:none; letter-spacing:0; font-size:11.5px; line-height:1.4; color:#5f6368; font-weight:500; }
        }

        /* V2.1 — alta densidade, fundo único e seções recolhidas */
        .reports-container {
          max-width:none !important;
          margin:0 !important;
          padding:0 18px 28px !important;
          background:#fff !important;
        }
        .compact-report-toolbar {
          position:sticky;
          top:0;
          z-index:45;
          background:rgba(255,255,255,.97);
          backdrop-filter:blur(10px);
          border-bottom:1px solid #e8eaed;
          margin:0 -18px 10px;
          padding:9px 18px;
        }
        .compact-toolbar-main { display:flex; align-items:flex-end; justify-content:space-between; gap:14px; min-height:44px; }
        .compact-period { display:flex; align-items:center; gap:10px; min-width:0; }
        .compact-label { color:#5f6368; font-size:10px; font-weight:800; text-transform:uppercase; letter-spacing:.06em; white-space:nowrap; }
        .compact-date-range { gap:6px; }
        .compact-date-range .input-group { min-height:34px; height:34px; padding:0 9px; border-radius:8px; }
        .compact-date-range input { font-size:12px !important; }
        .compact-toolbar-actions { display:flex; align-items:center; gap:6px; }
        .compact-toggle,.compact-icon-action { height:34px; border:1px solid #dadce0; background:#fff; color:#3c4043; border-radius:8px; padding:0 10px; display:inline-flex; align-items:center; justify-content:center; gap:6px; font-size:11.5px; font-weight:700; cursor:pointer; }
        .compact-toggle svg { width:14px; height:14px; transition:transform .18s ease; }
        .compact-toggle.active { color:#1a73e8; border-color:#aecbfa; background:#f5f9ff; }
        .compact-toggle.active svg { transform:rotate(180deg); }
        .compact-icon-action { width:34px; padding:0; }
        .compact-expand-area { border-top:1px solid #edf0f3; margin-top:9px; padding-top:9px; animation:slideDown .16s ease-out; }
        .compact-report-toolbar .quick-filters { margin-bottom:0; padding-bottom:0; }
        .compact-advanced-filters { display:grid; grid-template-columns:minmax(250px,2fr) repeat(4,minmax(140px,1fr)); gap:8px; }
        .compact-advanced-filters .input-group { min-height:36px; height:36px; border-radius:8px; padding:0 10px; }
        .compact-indicators { margin:0 0 10px !important; padding:10px 0; border-bottom:1px solid #e8eaed; }
        .results-toolbar { padding:8px 0 !important; border:0 !important; border-bottom:1px solid #e8eaed !important; }
        .list-container { border-top:0 !important; }
        .list-header { padding:9px 12px !important; }
        .list-row { padding:10px 12px !important; }
        .loading-compact { border:0 !important; border-radius:0 !important; background:#fff !important; padding:60px 20px !important; }
        @media (max-width:1100px) { .compact-advanced-filters { grid-template-columns:1fr 1fr; } .compact-search { grid-column:1/-1; } }
        @media (max-width:768px) {
          .reports-container { padding:0 0 24px !important; }
          .compact-report-toolbar { margin:0; padding:8px 12px; position:relative; }
          .compact-toolbar-main { align-items:stretch; flex-direction:column; }
          .compact-period { align-items:stretch; flex-direction:column; gap:5px; }
          .compact-date-range { flex-direction:row !important; }
          .compact-toolbar-actions { overflow-x:auto; padding-bottom:2px; }
          .compact-toggle { white-space:nowrap; }
          .compact-advanced-filters { grid-template-columns:1fr; }
          .compact-search { grid-column:auto; }
          .results-toolbar { padding:10px 12px !important; }
          .inline-error { margin:0 12px 10px; }
        }



        /* V2.2 — tipografia nítida, fluxo de rolagem estável e contexto sem duplicação */
        .reports-container {
          color:#17191c !important;
          font-family:Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif !important;
          font-synthesis:none;
          -webkit-font-smoothing:auto !important;
          -moz-osx-font-smoothing:auto !important;
          text-rendering:optimizeLegibility !important;
        }

        /* O contexto já existe no menu lateral; não deve ocupar uma segunda faixa. */
        .module-workspace:has(.reports-container) > .module-workspace-header {
          display:none !important;
        }
        .module-workspace:has(.reports-container) > .module-workspace-body {
          padding-top:0 !important;
          background:#fff !important;
        }
        .module-workspace:has(.reports-container),
        .module-workspace:has(.reports-container) .module-workspace-body {
          background:#fff !important;
        }

        /* Nada fica preso no topo: elimina sobreposição durante o scroll. */
        .compact-report-toolbar {
          position:relative !important;
          top:auto !important;
          z-index:4 !important;
          background:#fff !important;
          backdrop-filter:none !important;
          -webkit-backdrop-filter:none !important;
          box-shadow:none !important;
        }
        .list-header {
          position:relative !important;
          top:auto !important;
          z-index:1 !important;
        }
        .dropdown-menu {
          z-index:80 !important;
        }

        /* Hierarquia textual com contraste real, sem cinza lavado. */
        .compact-label,
        .list-th,
        .kpi-label,
        .info-label,
        .detail-card-title {
          color:#4b5158 !important;
          font-weight:700 !important;
          letter-spacing:.025em !important;
        }
        .text-main-cell,
        .info-val,
        .kpi-val,
        .expanded-header h3,
        .mobile-os-summary strong {
          color:#17191c !important;
          font-weight:650 !important;
          letter-spacing:-.012em !important;
        }
        .text-sub-cell,
        .page-titles p,
        .mobile-summary-note span {
          color:#626970 !important;
          font-weight:500 !important;
        }
        .list-row {
          line-height:1.32 !important;
        }
        .list-th {
          font-size:10.5px !important;
        }
        .text-main-cell {
          font-size:13px;
        }
        .text-sub-cell {
          font-size:11px !important;
          line-height:1.35 !important;
        }

        /* Controles compactos, porém legíveis. */
        .compact-toggle,
        .compact-icon-action,
        .quick-filter-btn,
        .clear-filter-btn,
        .input-group input,
        .input-group select {
          font-weight:600 !important;
          color:#30343a !important;
        }
        .compact-toggle:focus-visible,
        .compact-icon-action:focus-visible,
        .btn-acao:focus-visible,
        .dropdown-item:focus-visible {
          outline:2px solid #1a73e8 !important;
          outline-offset:2px;
        }

        @media (max-width:768px) {
          .module-workspace:has(.reports-container) > .module-workspace-body {
            padding:0 !important;
          }
          .compact-report-toolbar {
            position:relative !important;
          }
        }

        /* V2.5 — Fluxos reforçados */
        .quick-filter-btn.active,.compact-quick-btn.active{background:#111!important;color:#fff!important;border-color:#111!important}
        .quick-filter-btn.active *,.compact-quick-btn.active *{color:#fff!important}
        .dropdown-menu{position:fixed!important;right:auto!important;bottom:auto!important;z-index:2147483000!important;width:236px!important;max-height:min(290px,calc(100vh - 20px))!important;overflow-y:auto!important;background:#fff!important;border:1px solid #dadada!important;border-radius:10px!important;box-shadow:0 18px 45px rgba(0,0,0,.16)!important;animation:none!important}
        .dropdown-item{color:#202020!important}.dropdown-item:hover,.dropdown-item:focus-visible{color:#000!important;background:#f3f3f3!important}
        .modal-overlay{position:fixed!important;inset:0!important;z-index:2147483640!important;display:flex!important;align-items:center!important;justify-content:center!important;padding:20px!important;background:rgba(0,0,0,.56)!important;backdrop-filter:none!important;-webkit-backdrop-filter:none!important;overflow-y:auto!important}
        .modal-content{position:relative!important;z-index:2147483641!important;width:min(100%,480px)!important;max-height:calc(100vh - 40px)!important;overflow-y:auto!important;margin:auto!important;background:#fff!important;border:1px solid #dadada!important;border-radius:12px!important;box-shadow:0 24px 70px rgba(0,0,0,.24)!important;animation:none!important}
        .detail-card{padding:0!important;overflow:hidden}.detail-section-header{display:flex;align-items:center;gap:8px}
        .detail-section-toggle,.item-collapse-toggle{width:100%;min-height:44px;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 12px;border:0;border-bottom:1px solid #e7e7e7;background:#fff;color:#111;font:inherit;font-size:12px;font-weight:800;text-align:left;cursor:pointer}
        .detail-section-toggle span,.item-collapse-toggle span{display:flex;align-items:center;gap:8px}.detail-section-toggle:hover,.item-collapse-toggle:hover{background:#fafafa}
        .detail-section-toggle+*,.detail-section-header+*{padding:12px}.item-collapse-header{display:flex;align-items:center;gap:8px;margin:-12px -12px 0}.item-collapse-toggle{min-height:40px;border-bottom:0;background:#f8f8f8}.item-collapse-header+*{margin-top:12px}.details-grid{align-items:start!important}.list-row-wrapper.menu-open{z-index:auto!important}

    /* =========================================================================
       V2.6 — AÇÕES LATERAIS, DETALHE PANORÂMICO E OBSERVAÇÃO REAL
       ========================================================================= */
    .dropdown-menu {
      position: fixed !important;
      width: 236px !important;
      max-height: min(360px, calc(100dvh - 24px)) !important;
      overflow-y: auto !important;
      z-index: 2147483000 !important;
      transform: none !important;
      margin: 0 !important;
    }

    .expanded-header {
      align-items: center !important;
      gap: 14px !important;
      padding-bottom: 12px !important;
      margin-bottom: 10px !important;
      border-bottom: 1px solid var(--border) !important;
    }

    .expanded-header h3 {
      font-size: 14px !important;
      line-height: 1.25 !important;
      letter-spacing: -.01em !important;
      white-space: nowrap;
    }

    .action-buttons {
      justify-content: flex-end !important;
      gap: 6px !important;
    }

    .action-buttons .btn-sm {
      min-height: 30px !important;
      padding: 5px 9px !important;
      font-size: 10px !important;
      border-radius: 6px !important;
    }

    .nfce-guidance-panel {
      margin-top: 8px !important;
      padding: 10px 0 !important;
      border: 0 !important;
      border-bottom: 1px solid var(--border) !important;
      border-radius: 0 !important;
      background: #fff !important;
      box-shadow: none !important;
    }

    .nfce-copy span,
    .nfce-copy small {
      font-size: 10px !important;
    }

    .nfce-copy strong {
      font-size: 12px !important;
    }

    .detail-accordion,
    .detail-card {
      border-radius: 0 !important;
      box-shadow: none !important;
    }

    .detail-card {
      padding: 12px 0 !important;
      border: 0 !important;
      border-bottom: 1px solid var(--border) !important;
      background: #fff !important;
    }

    .detail-grid,
    .client-grid,
    .financial-grid,
    .info-grid {
      display: grid !important;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)) !important;
      gap: 8px 18px !important;
    }

    .detail-grid label,
    .client-grid label,
    .financial-grid label,
    .info-grid label,
    .detail-field span {
      font-size: 9px !important;
      line-height: 1.2 !important;
      color: #6b7280 !important;
      text-transform: uppercase !important;
      letter-spacing: .045em !important;
    }

    .detail-grid strong,
    .client-grid strong,
    .financial-grid strong,
    .info-grid strong,
    .detail-field strong {
      font-size: 11px !important;
      line-height: 1.35 !important;
      color: #111 !important;
    }

    .sale-observation {
      padding: 12px 0 !important;
    }

    .sale-observation-text {
      font-size: 11px;
      line-height: 1.55;
      color: #202020;
      padding: 10px 12px;
      background: #f7f7f7;
      border-left: 2px solid #111;
      white-space: pre-wrap;
    }

    @media (max-width: 760px) {
      .dropdown-menu {
        width: min(236px, calc(100vw - 24px)) !important;
      }

      .expanded-header {
        align-items: flex-start !important;
      }

      .expanded-header h3 {
        white-space: normal;
      }

      .action-buttons {
        width: 100%;
        justify-content: flex-start !important;
      }
    }


    /* V2.7 — abertura inteligente e ações centralizadas */
    .dropdown-menu.actions-center-menu {
      position: fixed !important;
      top: 50% !important;
      left: 50% !important;
      right: auto !important;
      bottom: auto !important;
      width: min(320px, calc(100vw - 32px)) !important;
      max-height: min(70vh, 520px) !important;
      overflow-y: auto !important;
      transform: translate(-50%, -50%) !important;
      z-index: 10050 !important;
      margin: 0 !important;
      border: 1px solid #D9D9D9 !important;
      border-radius: 12px !important;
      background: #FFFFFF !important;
      box-shadow: 0 18px 60px rgba(0,0,0,.16) !important;
    }

    .dropdown-menu.actions-center-menu .dropdown-item {
      min-height: 42px;
      color: #171717 !important;
      background: #FFFFFF !important;
    }

    .dropdown-menu.actions-center-menu .dropdown-item:hover,
    .dropdown-menu.actions-center-menu .dropdown-item:focus {
      color: #000000 !important;
      background: #F4F4F4 !important;
    }

    @media (max-width: 640px) {
      .dropdown-menu.actions-center-menu {
        width: calc(100vw - 24px) !important;
        max-height: 76vh !important;
      }
    }


        /* V3.0 — Busca determinística + parceria Labolens */
        .list-row-wrapper { scroll-margin-top: 14px; }
        .list-row-wrapper.search-pinned { order:-1; border-top:1px solid #d8dfde; background:#fff; }
        .list-row-wrapper.search-pinned > .list-row { background:#f8faf9 !important; }
        .list-row[role="button"]:focus-visible { outline:2px solid #36564c; outline-offset:-2px; }
        .compact-search:focus-within { border-color:#36564c !important; box-shadow:0 0 0 2px rgba(54,86,76,.08) !important; }
        .detail-error-clean { min-height:120px; display:grid; grid-template-columns:24px minmax(0,1fr) auto; align-items:center; gap:12px; padding:18px 0; color:#8b2f28; }
        .detail-error-clean strong,.detail-error-clean p { display:block; margin:0; }
        .detail-error-clean strong { color:#202124; font-size:12px; font-weight:750; }
        .detail-error-clean p { margin-top:3px; color:#6b7280; font-size:10.5px; line-height:1.45; }
        .detail-error-clean button { min-height:32px; padding:0 10px; border:1px solid #d8dfde; border-radius:6px; background:#fff; color:#36564c; font-size:9px; font-weight:750; cursor:pointer; }

        .labolens-inline-order {
          min-height:37px;
          flex:0 0 auto;
          padding:4px 9px;
          display:inline-flex;
          align-items:center;
          gap:8px;
          border:1px solid #8f969c;
          border-radius:6px;
          background:#a8aeb4;
          color:#202124;
          cursor:pointer;
          box-shadow:none;
          transition:background .14s ease,border-color .14s ease;
        }

        .labolens-inline-order:hover {
          border-color:#7f878d;
          background:#9da4aa;
        }

        .labolens-inline-order:active {
          background:#939aa0;
        }

        .labolens-inline-order span {
          font-size:7px;
          font-weight:850;
          text-transform:uppercase;
          letter-spacing:.055em;
          color:#202124;
        }

        .labolens-inline-order img {
          width:92px;
          height:29px;
          object-fit:contain;
          display:block;
        }

        /* V7 — portal direto no body: modal nunca fica preso atrás ou dentro dos menus. */
        .labolens-portal-overlay {
          position:fixed!important;
          inset:0!important;
          z-index:2147483646!important;
          display:flex!important;
          align-items:center!important;
          justify-content:center!important;
          padding:72px 24px 24px!important;
          overflow:auto!important;
          background:rgba(15,23,42,.42)!important;
          backdrop-filter:blur(2px);
          -webkit-backdrop-filter:blur(2px);
        }

        .labolens-order-modal {
          position:relative!important;
          width:min(860px,calc(100vw - 48px))!important;
          max-width:860px!important;
          max-height:min(720px,calc(100dvh - 96px))!important;
          margin:auto!important;
          display:flex!important;
          flex-direction:column!important;
          overflow:hidden!important;
          border:1px solid #d4d9dd!important;
          border-radius:12px!important;
          background:#fff!important;
          color:#172020!important;
          box-shadow:0 24px 70px rgba(0,0,0,.24)!important;
          transform:none!important;
          animation:none!important;
          outline:none!important;
        }

        .labolens-modal-top {
          min-height:62px;
          flex:0 0 auto;
          padding:9px 12px;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          border-bottom:1px solid #e5e7eb;
          background:#f1f2f2;
        }

        .labolens-modal-brand-box {
          min-width:185px;
          min-height:44px;
          padding:5px 10px;
          display:flex;
          align-items:center;
          gap:9px;
          border:1px solid #8f969c;
          border-radius:7px;
          background:#a8aeb4;
        }

        .labolens-modal-brand-box img {
          width:136px;
          height:40px;
          object-fit:contain;
          display:block;
        }

        .labolens-partner-label {
          color:#202124;
          font-size:7px;
          font-weight:850;
          text-transform:uppercase;
          letter-spacing:.055em;
          white-space:nowrap;
        }

        .labolens-modal-close {
          flex:0 0 auto;
        }

        .labolens-modal-scroll {
          min-height:0;
          flex:1 1 auto;
          overflow-y:auto;
          overscroll-behavior:contain;
          scrollbar-gutter:stable;
        }

        .labolens-modal-heading {
          padding:14px 16px 11px;
          display:flex;
          align-items:flex-end;
          justify-content:space-between;
          gap:16px;
        }

        .labolens-modal-kicker {
          display:block;
          margin-bottom:4px;
          color:#80868b;
          font-size:7px;
          line-height:1;
          font-weight:800;
          text-transform:uppercase;
          letter-spacing:.06em;
        }

        .labolens-modal-heading h3 {
          margin:0;
          color:#172020;
          font-size:18px;
          line-height:1.2;
          font-weight:800;
          letter-spacing:-.025em;
        }

        .labolens-modal-heading > strong {
          color:#36564c;
          font-size:10px;
          font-weight:800;
          white-space:nowrap;
        }

        .labolens-modal-summary {
          margin:0 16px 12px;
          display:grid;
          grid-template-columns:repeat(4,minmax(0,1fr));
          border:1px solid #e5e7eb;
          border-radius:7px;
          overflow:hidden;
        }

        .labolens-modal-summary > div {
          min-width:0;
          padding:9px 10px;
          border-right:1px solid #e5e7eb;
          background:#fff;
        }

        .labolens-modal-summary > div:last-child {
          border-right:0;
        }

        .labolens-modal-summary span,
        .labolens-modal-section-title,
        .labolens-delivery-note > span {
          display:block;
          margin-bottom:4px;
          color:#80868b;
          font-size:7px;
          line-height:1.2;
          font-weight:800;
          text-transform:uppercase;
          letter-spacing:.055em;
        }

        .labolens-modal-summary strong {
          display:block;
          overflow:hidden;
          color:#202124;
          font-size:10px;
          line-height:1.35;
          font-weight:750;
          white-space:nowrap;
          text-overflow:ellipsis;
        }

        .labolens-modal-summary small {
          display:block;
          margin-top:2px;
          color:#8a9290;
          font-size:7px;
          line-height:1.3;
        }

        .labolens-forecast-box {
          background:#eef4f2!important;
        }

        .labolens-forecast-box strong {
          color:#36564c!important;
        }

        .labolens-modal-body-grid {
          margin:0 16px 11px;
          display:grid;
          grid-template-columns:minmax(0,1.45fr) minmax(250px,.75fr);
          gap:10px;
        }

        .labolens-modal-body-grid .labolens-modal-section {
          min-width:0;
          height:100%;
          margin:0;
        }

        .labolens-modal-section {
          padding:11px;
          border:1px solid #e5e7eb;
          border-radius:7px;
          background:#fff;
        }

        .labolens-prescription-table-wrap {
          width:100%;
          overflow-x:auto;
          border:1px solid #e8ecec;
        }

        .labolens-prescription-table {
          width:100%;
          min-width:470px;
          border-collapse:collapse;
        }

        .labolens-prescription-table th,
        .labolens-prescription-table td {
          padding:7px 8px;
          border-right:1px solid #edf0ef;
          border-bottom:1px solid #edf0ef;
          text-align:center;
          font-size:9px;
        }

        .labolens-prescription-table th:last-child,
        .labolens-prescription-table td:last-child {
          border-right:0;
        }

        .labolens-prescription-table tbody tr:last-child td {
          border-bottom:0;
        }

        .labolens-prescription-table th {
          color:#6b7280;
          background:#f7f9f9;
          font-size:7px;
          font-weight:800;
          text-transform:uppercase;
        }

        .labolens-prescription-table td:first-child {
          color:#36564c;
          font-weight:850;
        }

        .labolens-addition {
          margin-top:8px;
          display:flex;
          justify-content:flex-end;
          gap:7px;
          color:#74807d;
          font-size:8px;
        }

        .labolens-addition strong {
          color:#36564c;
        }

        .labolens-measures {
          display:grid;
          grid-template-columns:repeat(2,minmax(0,1fr));
          border-top:1px solid #e8ecec;
          border-left:1px solid #e8ecec;
        }

        .labolens-measures span {
          min-width:0;
          padding:9px 8px;
          border-right:1px solid #e8ecec;
          border-bottom:1px solid #e8ecec;
          color:#74807d;
          text-align:center;
          font-size:7.5px;
        }

        .labolens-measures strong {
          display:block;
          margin-top:2px;
          color:#172020;
          font-size:10px;
        }

        .labolens-delivery-note {
          margin:0 16px 11px;
          padding:8px 10px;
          border:1px solid #e8ecec;
          border-radius:6px;
          background:#f7f9f9;
        }

        .labolens-delivery-note code {
          display:block;
          overflow-wrap:anywhere;
          color:#36564c;
          font-size:8px;
          line-height:1.45;
        }

        .labolens-feedback {
          margin:0 16px 10px;
          padding:9px 10px;
          border:1px solid;
          border-radius:6px;
          font-size:8.5px;
          font-weight:700;
        }

        .labolens-feedback.error {
          color:#9f312a;
          border-color:#ead8d5;
          background:#fff6f5;
        }

        .labolens-feedback.success {
          color:#2f6b57;
          border-color:#dce9e4;
          background:#f2f7f5;
        }

        .labolens-confirm-check {
          width:calc(100% - 32px);
          margin:0 16px 12px;
          padding:10px 12px;
          display:grid;
          grid-template-columns:18px minmax(0,1fr);
          align-items:center;
          gap:9px;
          border:1px solid #d8dfde;
          border-radius:7px;
          background:#f7f9f9;
          color:inherit;
          text-align:left;
          cursor:pointer;
          user-select:none;
          transition:background .14s ease,border-color .14s ease;
        }

        .labolens-confirm-check:hover:not(:disabled) {
          border-color:#aeb8b4;
          background:#f2f6f4;
        }

        .labolens-confirm-check.checked {
          border-color:#8fa89f;
          background:#eaf2ef;
        }

        .labolens-confirm-check:disabled {
          cursor:not-allowed;
          opacity:.62;
        }

        .labolens-confirm-check-box {
          width:18px;
          height:18px;
          display:grid;
          place-items:center;
          border:1px solid #aeb6b3;
          border-radius:4px;
          background:#fff;
          color:#fff;
          font-size:11px;
          font-weight:900;
        }

        .labolens-confirm-check.checked .labolens-confirm-check-box {
          border-color:#36564c;
          background:#36564c;
        }

        .labolens-confirm-check-copy {
          min-width:0;
          display:flex;
          flex-direction:column;
          gap:2px;
        }

        .labolens-confirm-check-copy strong {
          color:#172020;
          font-size:9px;
          font-weight:800;
        }

        .labolens-confirm-check-copy small {
          color:#74807d;
          font-size:7.5px;
          line-height:1.4;
        }

        .labolens-modal-actions {
          min-height:57px;
          flex:0 0 auto;
          padding:10px 16px 12px;
          display:flex;
          align-items:center;
          justify-content:flex-end;
          gap:8px;
          border-top:1px solid #e5e7eb;
          background:#fafafa;
        }

        .labolens-confirm-order {
          min-height:36px;
          min-width:132px;
          padding:0 14px;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          gap:6px;
          border:1px solid #36564c;
          border-radius:6px;
          background:#36564c;
          color:#fff;
          font-size:8.5px;
          font-weight:800;
          cursor:pointer;
          transition:opacity .16s ease,filter .16s ease,background .16s ease;
        }

        .labolens-confirm-order.is-locked,
        .labolens-confirm-order:disabled:not(.is-ready) {
          opacity:.18!important;
          filter:grayscale(1);
          cursor:not-allowed!important;
          pointer-events:none;
        }

        .labolens-confirm-order.is-ready {
          opacity:1!important;
          filter:none;
          pointer-events:auto;
        }

        .labolens-confirm-order.is-ready:hover:not(:disabled) {
          background:#2e4a42;
        }

        .labolens-manufacturing-badge {
          width:max-content;
          max-width:100%;
          min-height:27px;
          margin-top:6px;
          padding:3px 6px 3px 4px;
          display:flex;
          align-items:center;
          gap:5px;
          border:1px solid #9fa6ac;
          border-radius:6px;
          background:#b7bdc2;
          color:#202124;
          box-shadow:none;
        }

        .labolens-manufacturing-badge > img {
          width:43px;
          height:18px;
          flex:0 0 auto;
          object-fit:contain;
        }

        .labolens-verified-mark {
          width:16px;
          height:16px;
          flex:0 0 16px;
          display:grid;
          place-items:center;
          border-radius:50%;
          background:#1a73e8;
          color:#fff;
          font-size:9px;
          line-height:1;
          font-weight:900;
        }

        .labolens-manufacturing-copy {
          min-width:0;
          display:flex;
          flex-direction:column;
          gap:1px;
        }

        .labolens-manufacturing-copy strong {
          color:#202124;
          font-size:7px;
          line-height:1.1;
          font-weight:850;
          white-space:nowrap;
        }

        .labolens-manufacturing-copy small {
          color:#4f575c;
          font-size:6.5px;
          line-height:1.1;
          white-space:nowrap;
        }

        @media(max-width:900px) {
          .labolens-portal-overlay {
            padding:66px 16px 16px!important;
          }

          .labolens-order-modal {
            width:min(760px,calc(100vw - 32px))!important;
            max-height:calc(100dvh - 82px)!important;
          }

          .labolens-modal-body-grid {
            grid-template-columns:1fr;
          }

          .labolens-measures {
            grid-template-columns:repeat(4,minmax(0,1fr));
          }
        }

        @media(max-width:680px) {
          .labolens-portal-overlay {
            padding:58px 10px 10px!important;
          }

          .labolens-order-modal {
            width:calc(100vw - 20px)!important;
            max-width:none!important;
            max-height:calc(100dvh - 68px)!important;
            border-radius:10px!important;
          }

          .labolens-modal-top {
            min-height:54px;
            padding:7px 9px;
          }

          .labolens-modal-brand-box {
            min-width:0;
            min-height:39px;
            padding:4px 7px;
          }

          .labolens-modal-brand-box img {
            width:112px;
            height:34px;
          }

          .labolens-modal-heading {
            padding:12px 12px 9px;
            align-items:flex-start;
            flex-direction:column;
            gap:4px;
          }

          .labolens-modal-heading h3 {
            font-size:16px;
          }

          .labolens-modal-summary {
            margin:0 12px 10px;
            grid-template-columns:repeat(2,minmax(0,1fr));
          }

          .labolens-modal-summary > div:nth-child(2) {
            border-right:0;
          }

          .labolens-modal-summary > div:nth-child(-n+2) {
            border-bottom:1px solid #e5e7eb;
          }

          .labolens-modal-body-grid {
            margin:0 12px 10px;
            grid-template-columns:1fr;
          }

          .labolens-delivery-note {
            margin:0 12px 10px;
          }

          .labolens-feedback {
            margin-left:12px;
            margin-right:12px;
          }

          .labolens-confirm-check {
            width:calc(100% - 24px);
            margin-left:12px;
            margin-right:12px;
          }

          .labolens-measures {
            grid-template-columns:repeat(2,minmax(0,1fr));
          }

          .labolens-modal-actions {
            min-height:54px;
            padding:8px 12px 10px;
          }
        }

        @media(max-width:430px) {
          .labolens-portal-overlay {
            padding:52px 7px 7px!important;
          }

          .labolens-order-modal {
            width:calc(100vw - 14px)!important;
            max-height:calc(100dvh - 59px)!important;
            border-radius:9px!important;
          }

          .labolens-modal-summary {
            grid-template-columns:1fr;
          }

          .labolens-modal-summary > div {
            border-right:0!important;
            border-bottom:1px solid #e5e7eb;
          }

          .labolens-modal-summary > div:last-child {
            border-bottom:0;
          }

          .labolens-modal-actions {
            display:grid;
            grid-template-columns:1fr 1.35fr;
          }

          .labolens-modal-actions > button {
            width:100%;
            justify-content:center;
          }

          .labolens-inline-order {
            width:100%;
            justify-content:space-between;
          }

          .labolens-manufacturing-badge {
            width:100%;
          }
        }
      `}
</style>
      <LabolensPedidoModal
        pedido={labolensPedido}
        onClose={fecharPedidoLabolens}
        onConfirm={enviarPedidoLabolens}
        enviando={labolensEnviando}
        erro={labolensErro}
        sucesso={labolensSucesso}
        conferido={labolensPedidoConferido}
        onToggleConferido={(value) => {
          setLabolensPedidoConferido(value);
          if (value) setLabolensErro('');
        }}
      />

      {/* MODAL DE IMPRESSÃO */}
      {printModalOs && (
        <div className="modal-overlay" onClick={() => setPrintModalOs(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, color: 'var(--text-main)', fontWeight: 800 }}>Opções de Impressão</h3>
              <button onClick={() => setPrintModalOs(null)} className="btn-acao"><Icons.Close /></button>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-sec)', marginBottom: 16 }}>Selecione as vias para a <strong>OS #{printModalOs.numeroOS}</strong>:</p>

            <div className={`checkbox-container ${printOptions.otica ? 'active' : ''}`} onClick={() => setPrintOptions({...printOptions, otica: !printOptions.otica})}>
              {printOptions.otica ? <Icons.CheckSquare /> : <Icons.Square />}
              <span>1. Via da Ótica (Arquivo interno)</span>
            </div>
            <div className={`checkbox-container ${printOptions.lab ? 'active' : ''}`} onClick={() => setPrintOptions({...printOptions, lab: !printOptions.lab})}>
              {printOptions.lab ? <Icons.CheckSquare /> : <Icons.Square />}
              <span>2. Via do Laboratório (Produção/Montagem)</span>
            </div>
            <div className={`checkbox-container ${printOptions.cliente ? 'active' : ''}`} onClick={() => setPrintOptions({...printOptions, cliente: !printOptions.cliente})}>
              {printOptions.cliente ? <Icons.CheckSquare /> : <Icons.Square />}
              <span>3. Via do Cliente (Recibo/Garantia)</span>
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button onClick={() => setPrintModalOs(null)} className="btn-sm btn-cancel" style={{flex: 1, justifyContent: 'center'}}>Cancelar</button>
              <button onClick={confirmGerarPDF} disabled={(!printOptions.otica && !printOptions.lab && !printOptions.cliente) || loadingPdfId} className="btn-sm btn-save" style={{ flex: 2, justifyContent: 'center', opacity: (!printOptions.otica && !printOptions.lab && !printOptions.cliente) ? 0.5 : 1 }}>
                <Icons.Receipt /> {loadingPdfId ? 'Gerando...' : 'Gerar PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE DECISÃO: PRONTO NA LOJA */}
      {statusDecisionModal && (
        <div className="modal-overlay" onClick={() => setStatusDecisionModal(null)}>
          <div className="modal-content smart-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="smart-confirm-head">
              <div className="smart-confirm-icon"><Icons.MessageCircle /></div>
              <div>
                <h3>Marcar como pronto na loja?</h3>
                <p>Escolha se deseja avisar o cliente agora ou apenas atualizar o status da O.S.</p>
              </div>
              <button onClick={() => setStatusDecisionModal(null)} className="btn-acao"><Icons.Close /></button>
            </div>

            <div className="smart-confirm-client">
              <span>Cliente</span>
              <strong>{safeRenderName(statusDecisionModal.os?.cliente) || 'Não informado'}</strong>
              <small>{statusDecisionModal.os?.whatsapp || 'Sem WhatsApp cadastrado'}</small>
            </div>

            <div className="smart-confirm-actions">
              {statusDecisionModal.temWhatsapp && (
                <button type="button" className="smart-action primary" onClick={() => handleMarcarProntoNaLoja('enviar')} disabled={saving}>
                  <Icons.Send /> Avisar cliente e marcar pronto
                </button>
              )}
              <button type="button" className="smart-action secondary" onClick={() => handleMarcarProntoNaLoja('apenas')} disabled={saving}>
                Apenas marcar como pronto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BARRA OPERACIONAL COMPACTA */}
      <section className="compact-report-toolbar" aria-label="Filtros dos relatórios">
        <div className="compact-toolbar-main">
          <div className="compact-period">
            <span className="compact-label">Período</span>
            <div className="date-range compact-date-range">
              <div className="input-group"><Icons.Calendar /><input aria-label="Data inicial" type="date" value={filtros.dataInicio} onChange={e => handleCustomDateChange('dataInicio', e.target.value)} /></div>
              <span>-</span>
              <div className="input-group"><Icons.Calendar /><input aria-label="Data final" type="date" value={filtros.dataFim} onChange={e => handleCustomDateChange('dataFim', e.target.value)} /></div>
            </div>
          </div>

          <div className="compact-toolbar-actions">
            <button type="button" className={`compact-toggle ${atalhosAbertos ? 'active' : ''}`} onClick={() => setAtalhosAbertos((valor) => !valor)} aria-expanded={atalhosAbertos}>
              Atalhos <Icons.ChevronDown />
            </button>
            <button type="button" className={`compact-toggle ${filtrosAvancadosAbertos ? 'active' : ''}`} onClick={() => setFiltrosAvancadosAbertos((valor) => !valor)} aria-expanded={filtrosAvancadosAbertos}>
              Mais filtros <Icons.ChevronDown />
            </button>
            <button type="button" className={`compact-toggle ${indicadoresAbertos ? 'active' : ''}`} onClick={() => setIndicadoresAbertos((valor) => !valor)} aria-expanded={indicadoresAbertos}>
              Indicadores <Icons.ChevronDown />
            </button>
            <button onClick={handleExportPDF} disabled={isExporting} className="compact-icon-action" title="Exportar relatório PDF" aria-label="Exportar relatório PDF">
              <Icons.FileText />
            </button>
          </div>
        </div>

        {atalhosAbertos && (
          <div className="compact-expand-area quick-filters">
            <button className={`quick-filter-btn ${activeQuickFilter === 'hoje' ? 'active' : ''}`} onClick={() => handleQuickFilterClick('hoje')}>Hoje</button>
            <button className={`quick-filter-btn ${activeQuickFilter === 'ontem' ? 'active' : ''}`} onClick={() => handleQuickFilterClick('ontem')}>Ontem</button>
            <button className={`quick-filter-btn ${activeQuickFilter === 'semana' ? 'active' : ''}`} onClick={() => handleQuickFilterClick('semana')}>Esta semana</button>
            <button className={`quick-filter-btn ${activeQuickFilter === 'mes' ? 'active' : ''}`} onClick={() => handleQuickFilterClick('mes')}>Este mês</button>
            <button className={`quick-filter-btn ${activeQuickFilter === 'mes_passado' ? 'active' : ''}`} onClick={() => handleQuickFilterClick('mes_passado')}>Mês passado</button>
            <button className="clear-filter-btn" onClick={handleClearFilters}><Icons.FilterOff /> Limpar</button>
          </div>
        )}

        {filtrosAvancadosAbertos && (
          <div className="compact-expand-area compact-advanced-filters">
            <div className="input-group compact-search">
              <Icons.Search />
              <input
                placeholder="Buscar cliente, OS, CPF, vendedor ou CV..."
                value={filtros.busca}
                onChange={e => setFiltros({...filtros, busca: e.target.value})}
                onKeyDown={handleSearchKeyDown}
                aria-label="Buscar cliente, número da OS, CPF, vendedor ou CV"
              />
            </div>
            <div className="input-group"><Icons.Store />
              <select value={filtros.loja} onChange={e => setFiltros({...filtros, loja: e.target.value})}>
                <option value="Todas">Todas as empresas</option>
                {listaEmpresasFiltro.map((empresa) => (
                  <option key={empresa.id || empresa.codigo} value={empresa.id || empresa.codigo}>
                    {empresa.label}{empresa.fiscalPronta ? '' : ' · fiscal pendente'}
                  </option>
                ))}
                {!listaEmpresasFiltro.length && listaLojas.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div className="input-group"><Icons.User />
              <select value={filtros.vendedor} onChange={e => setFiltros({...filtros, vendedor: e.target.value})}>
                {listaVendedores.map(vend => (<option key={vend} value={vend}>{vend === 'Todos' ? 'Todos os vendedores' : vend}</option>))}
              </select>
            </div>
            <div className="input-group"><Icons.List />
              <select value={filtros.status} onChange={e => setFiltros({...filtros, status: e.target.value})}><option value="Todos">Todos os status</option>{STATUS_PERMITIDOS.map(s => <option key={s} value={s}>{s}</option>)}</select>
            </div>
            <div className="input-group"><Icons.Wallet />
              <select value={filtros.financeiro} onChange={e => setFiltros({...filtros, financeiro: e.target.value})}><option value="Todos">Todos os financeiros</option><option value="Quitado">Quitados</option><option value="Pendente">A receber</option></select>
            </div>
          </div>
        )}
      </section>

      {erroCarregamento && (
        <div className="inline-error">
          <span>{erroCarregamento}</span>
          <button type="button" onClick={() => fetchOrdens()}>Tentar novamente</button>
        </div>
      )}

      {loading ? (
        <div className="loading-compact" style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
           <div style={{width: 40, height: 40, border: '4px solid var(--border)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 1s linear infinite'}}></div>
           <h3 style={{marginTop: 16, fontSize: 16, fontWeight: 700}}>Carregando relatórios...</h3>
        </div>
      ) : (
        <>
          {/* INDICADORES E VENDEDORES — FECHADOS POR PADRÃO */}
          {indicadoresAbertos && (
          <div className="dash-kpi-container compact-indicators">
             <div className="kpi-cards-grid">
                <div className="kpi-card" style={{gridColumn: '1 / -1'}}>
                   <div className="kpi-icon" style={{background: '#EEF2FF', color: '#4F46E5'}}><Icons.Dollar /></div>
                   <div className="kpi-texts"><span className="kpi-label">Faturamento da página</span><span className="kpi-val">{formatCurrency(kpis.totalRevenue)}</span></div>
                </div>
                <div className="kpi-card">
                   <div className="kpi-icon" style={{background: '#FEF2F2', color: '#EF4444'}}><Icons.Alert /></div>
                   <div className="kpi-texts"><span className="kpi-label">A Receber</span><span className="kpi-val" style={{fontSize: 18}}>{formatCurrency(kpis.totalReceber)}</span></div>
                </div>
                <div className="kpi-card">
                   <div className="kpi-icon" style={{background: '#ECFDF5', color: '#10B981'}}><Icons.Receipt /></div>
                   <div className="kpi-texts"><span className="kpi-label">Ticket Médio</span><span className="kpi-val" style={{fontSize: 18}}>{formatCurrency(kpis.avgTicket)}</span></div>
                </div>
             </div>
             
             <div className="ranking-card">
                <h3 className="ranking-title"><Icons.Trophy /> Vendedores desta página</h3>
                <div className="ranking-list custom-scrollbar">
                   {kpis.ranking.length > 0 ? kpis.ranking.map((v, i) => (
                      <div key={i} className="ranking-item">
                         <div style={{display:'flex', alignItems:'center', gap: 12}}>
                            <span style={{background: i===0?'#FEF3C7':(i===1?'#F1F5F9':'#FFEDD5'), color: i===0?'#D97706':(i===1?'#475569':'#C2410C'), padding: '4px 8px', borderRadius: 6, fontWeight: 800, fontSize: 11}}>{i + 1}º</span>
                            <div><span style={{display:'block', fontSize: 13, fontWeight: 700, color: 'var(--text-main)'}}>{v.nome}</span><span style={{fontSize: 10, color: 'var(--text-sec)'}}>{v.qtd} vendas</span></div>
                         </div>
                         <strong style={{fontSize: 13, color: i === 0 ? '#10B981' : 'var(--text-sec)', fontWeight: 800}}>{formatCurrency(v.total)}</strong>
                      </div>
                   )) : <p style={{textAlign:'center', color:'var(--text-muted)', fontSize: 12, marginTop: 20}}>Nenhuma venda.</p>}
                </div>
             </div>
          </div>
          )}

          {/* LISTA DE O.S. (TABELA RESPONSIVA) */}
          <div className="results-toolbar">
            <div className="results-summary">
              <strong>{paginacao.total ? `${paginacao.totalConhecido ? paginacao.total : `${paginacao.total}+`} ordens encontradas` : 'Nenhuma ordem encontrada'}</strong>
              <span>Mostrando {displayData.length} registro(s) nesta página. {filtrosAplicados.busca ? 'Clique em uma O.S. para abrir e fixar no topo.' : 'Os filtros são processados pela API.'}</span>
            </div>
            <label className="page-size-control">
              Exibir
              <select value={itensPorPagina} onChange={(event) => alterarItensPorPagina(event.target.value)}>
                <option value={10}>10 por página</option>
                <option value={20}>20 por página</option>
              </select>
            </label>
          </div>
          <div className="list-container">
             <div className="list-header desktop-only">
                <div className="list-th"></div>
                <div className="list-th">Ordem / OS</div>
                <div className="list-th">Datas</div>
                <div className="list-th">Cliente & CV</div>
                <div className="list-th">Status</div>
                <div className="list-th" style={{textAlign: 'right'}}>Financeiro</div>
                <div className="list-th" style={{textAlign: 'center'}}>Ações</div>
             </div>
             
             <div className="list-body">
                {displayData.length === 0 ? (
                  <div style={{padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontWeight: 600}}>Nenhuma OS encontrada para estes filtros.</div>
                ) : (
                  displayData.map((os) => {
                     const rowId = normalizarId(os?._id || os?.id);
                     const isExpanded = expandedRow === rowId;
                     const isSearchPinned = searchPinnedOsId === rowId && Boolean(String(filtrosAplicados.busca || '').trim());
                     const faltaPagar = calcularRestante(os);
                     const nfeEmitida = isNotaFiscalEmitida(os);
                     const nfeProcessando = isNotaFiscalProcessando(os);
                     const nfeRejeitada = isNotaFiscalRejeitada(os);
                     const notaFiscalLabel = getNotaFiscalLabel(os);
                     const notaFiscalBadgeStyle = getNotaFiscalBadgeStyle(os);
                     const osItems = getOsItems(os);
                     const principalCV = osItems[0]?.produto?.numeroCV || osItems[0]?.produto?.cv || 'S/N';
                     const extraBadge = osItems.length > 1 ? ` (+${osItems.length - 1})` : '';
                     const labolensInfo = getLabolensInfoOS(os, labolensPedidosPorOs[rowId] || []);

                     // Cores do Status Pill
                     let pillBg = '#F1F5F9', pillColor = '#64748B';
                     if (os.status === 'Entregue') { pillBg = '#DCFCE7'; pillColor = '#166534'; }
                     else if (os.status === 'Pronto na loja') { pillBg = '#DBEAFE'; pillColor = '#1E40AF'; }
                     else if (os.status === 'No laboratório') { pillBg = '#FEF9C3'; pillColor = '#854D0E'; }
                     else if (os.status === 'Pedido realizado') { pillBg = '#E8F0FE'; pillColor = '#174EA6'; }
                     else if (os.status === 'Pedido da lente') { pillBg = '#FFEDD5'; pillColor = '#C2410C'; }

                     return (
                     <div
                       key={rowId || os.numeroOS}
                       ref={(node) => {
                         if (!rowId) return;
                         if (node) rowRefs.current.set(rowId, node);
                         else rowRefs.current.delete(rowId);
                       }}
                       className={`list-row-wrapper ${menuAberto === os._id ? 'menu-open' : ''} ${isSearchPinned ? 'search-pinned' : ''}`}
                     >
                        {/* LINHA / CARD */}
                        <div
                          className={`list-row ${isExpanded ? 'is-expanded' : ''}`}
                          onClick={() => handleRowClick(rowId)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleRowClick(rowId);
                            }
                          }}
                        >
                           <div className="col-icon desktop-only"><Icons.ChevronDown /></div>
                           
                           <div className="col-os">
                              <span className="text-main-cell" style={{fontSize: 16}}>#{os.numeroOS || '---'}</span>
                              {(nfeEmitida || nfeProcessando || nfeRejeitada) && (
                                <div className="badge-nfce" style={notaFiscalBadgeStyle}>
                                  {nfeEmitida && <Icons.CheckCircle />}
                                  {notaFiscalLabel}
                                </div>
                              )}
                              {!nfeEmitida && !nfeProcessando && !nfeRejeitada && os.empresaFiscalPronta && (
                                <div className={`badge-nfce ${osEstaEntregue(os) ? 'badge-nfce-ready' : 'badge-nfce-waiting'}`}>
                                  {osEstaEntregue(os) ? 'Fiscal pronto' : 'Entregue para emitir'}
                                </div>
                              )}

                              {labolensInfo.ativo && (
                                <div
                                  className="labolens-manufacturing-badge"
                                  title={labolensInfo.previsao
                                    ? `Labolens · ${labolensInfo.statusLabel} · previsão ${formatDateLocal(labolensInfo.previsao)}`
                                    : `Labolens · ${labolensInfo.statusLabel}`}
                                >
                                  <span className="labolens-verified-mark">✓</span>
                                  <img src={labolensLogoPng} alt="" aria-hidden="true" />
                                  <span className="labolens-manufacturing-copy">
                                    <strong>{labolensInfo.statusLabel}</strong>
                                    {labolensInfo.previsao && (
                                      <small>Prev. {formatDateLocal(labolensInfo.previsao)}</small>
                                    )}
                                  </span>
                                </div>
                              )}
                           </div>
                           
                           <div className="col-data">
                              <div>
                                <span className="text-main-cell" style={{fontSize: 13}}>{formatDateLocal(os.dataCompra)}</span>
                                <span className="text-sub-cell">{os.lojaOficial}</span>
                              </div>
                              <div className="mobile-only col-icon"><Icons.ChevronDown /></div>
                           </div>
                           
                           <div className="col-cliente">
                              <span className="text-main-cell">{safeRenderName(os.cliente)}</span>
                              <span className="cv-badge">CV: {principalCV} {extraBadge}</span>
                           </div>
                           
                           <div className="col-status">
                              <span className="status-pill" style={{background: pillBg, color: pillColor}}>{os.status}</span>
                           </div>
                           
                           <div className="col-valor desktop-only" style={{textAlign: 'right'}}>
                               <span className="text-main-cell" style={{fontSize: 15}}>{formatCurrency(os.valorTotal)}</span>
                               <span style={{fontSize: 11, fontWeight: 800, color: faltaPagar > 0 ? '#EF4444' : '#10B981', marginTop: 4, display: 'block'}}>{faltaPagar > 0 ? `Falta: ${formatCurrency(faltaPagar)}` : 'Quitado ✓'}</span>
                           </div>

                           <div className="col-valor mobile-only">
                               <div><span style={{fontSize: 10, color: 'var(--text-sec)', fontWeight: 700, textTransform: 'uppercase'}}>Total</span><span className="text-main-cell">{formatCurrency(os.valorTotal)}</span></div>
                               <div style={{textAlign: 'right'}}><span style={{fontSize: 10, color: 'var(--text-sec)', fontWeight: 700, textTransform: 'uppercase'}}>Situação</span><span style={{fontSize: 12, fontWeight: 800, color: faltaPagar > 0 ? '#EF4444' : '#10B981', display: 'block'}}>{faltaPagar > 0 ? `Falta: ${formatCurrency(faltaPagar)}` : 'Quitado ✓'}</span></div>
                           </div>
                           
                           <div className="col-actions action-cell">
                              <button
                                type="button"
                                className="btn-acao"
                                aria-label="Abrir ações da OS"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (menuAberto === os._id) {
                                    setMenuAberto(null);
                                    return;
                                  }
                                  setMenuPosition({
                                    top: window.innerHeight / 2,
                                    left: window.innerWidth / 2
                                  });
                                  setMenuAberto(os._id);
                                }}
                              >
                                <Icons.MoreVertical />
                              </button>

                              {menuAberto === os._id && (
                                <div className="dropdown-menu actions-center-menu" style={{ top: menuPosition.top, left: menuPosition.left }} onClick={e => e.stopPropagation()}>
                                  <button onClick={(e) => handleOpenPrintModal(os, e)} className="dropdown-item">
                                    <Icons.Receipt /> Imprimir vias do pedido
                                  </button>
                                  <div className="dropdown-divider" />

                                  <button
                                    onClick={(e) => handleEmitirNFCe(os, e)}
                                    className="dropdown-item primary-action"
                                    disabled={isFiscalActionLoading(os._id) || (!podeEmitirNovaNfce(os))}
                                    title={!podeEmitirNovaNfce(os) ? 'A OS precisa estar Entregue para emitir cupom fiscal.' : ''}
                                  >
                                    {isFiscalActionLoading(os._id, 'visualizar') ? <Icons.Loader /> : <Icons.FileText />}
                                    {getFiscalActionLabel(
                                      os._id,
                                      'visualizar',
                                      nfeEmitida
                                        ? 'Visualizar cupom'
                                        : nfeProcessando
                                          ? 'Consultar NFC-e'
                                          : osEstaEntregue(os)
                                            ? 'Emitir cupom NFC-e'
                                            : 'Entregue para emitir',
                                      nfeEmitida ? 'Abrindo cupom...' : nfeProcessando ? 'Consultando...' : 'Emitindo...'
                                    )}
                                  </button>

                                  <button onClick={(e) => handleBaixarPdfCupom(os, e)} className="dropdown-item" disabled={isFiscalActionLoading(os._id) || (!nfeEmitida && !os?.notaFiscal?.invoiceId)}>
                                    {isFiscalActionLoading(os._id, 'pdf') ? <Icons.Loader /> : <Icons.Download />}
                                    {getFiscalActionLabel(os._id, 'pdf', 'Baixar PDF', 'Baixando PDF...')}
                                  </button>

                                  <button onClick={(e) => handleBaixarXmlCupom(os, e)} className="dropdown-item" disabled={isFiscalActionLoading(os._id) || (!nfeEmitida && !os?.notaFiscal?.invoiceId)}>
                                    {isFiscalActionLoading(os._id, 'xml') ? <Icons.Loader /> : <Icons.Xml />}
                                    {getFiscalActionLabel(os._id, 'xml', 'Baixar XML', 'Baixando XML...')}
                                  </button>
                                </div>
                              )}
                           </div>
                        </div>

                        {/* CONTEÚDO EXPANDIDO (GRID LADO A LADO OTIMIZADO) */}
                        {isExpanded && (
                           <div className="expanded-panel">
                              {loadingDetalhes ? (
                                 <div className="detail-loading-clean">
                                    <span className="mini-loader" />
                                    <strong>Carregando detalhes da O.S.</strong>
                                    <p>Buscando histórico, fotos e informações do cliente.</p>
                                    <div className="detail-loading-lines"><i /><i /><i /></div>
                                 </div>
                              ) : erroDetalhes ? (
                                 <div className="detail-error-clean">
                                   <Icons.Alert />
                                   <div><strong>Não foi possível abrir esta O.S.</strong><p>{erroDetalhes}</p></div>
                                   <button type="button" onClick={() => handleRowClick(rowId, true)}>Tentar novamente</button>
                                 </div>
                              ) : !osDetalhada ? (
                                 <div className="detail-error-clean">
                                   <Icons.Alert />
                                   <div><strong>Detalhe indisponível.</strong><p>Selecione a O.S. novamente.</p></div>
                                 </div>
                              ) : (
                                 <>
                                    <div className="expanded-header">
                                      <h3>Controle da O.S. #{osDetalhada.numeroOS}</h3>
                                      <div className="action-buttons">
                                        {!editMode ? (
                                          <>
                                            <button onClick={handleReenviarBoasVindas} disabled={saving} className="btn-sm btn-wa"><Icons.Refresh /> {saving ? 'Enviando...' : 'Reenviar boas-vindas'}</button>
                                            <button onClick={() => handleDeleteOS(osDetalhada._id)} className="btn-sm btn-del"><Icons.Trash /> Excluir</button>
                                            <button onClick={toggleEditMode} className="btn-sm btn-edit"><Icons.Edit /> Editar OS</button>
                                          </>
                                        ) : (
                                          <>
                                            <button onClick={handleClearForm} className="btn-sm btn-del" style={{marginRight: 'auto'}}><Icons.Refresh /> Limpar</button>
                                            <button onClick={toggleEditMode} className="btn-sm btn-cancel">Cancelar</button>
                                            <button onClick={handleSaveEdit} disabled={saving} className="btn-sm btn-save"><Icons.Save /> {saving ? 'Salvando...' : 'Salvar'}</button>
                                          </>
                                        )}
                                      </div>
                                    </div>

                                    {!editMode && (
                                      <div className="nfce-guidance-panel">
                                        <div className="nfce-copy">
                                          <span>Fiscal</span>
                                          <strong>{getNotaFiscalLabel(osDetalhada)}</strong>
                                          <small>{osEstaEntregue(osDetalhada) ? 'OS entregue. Você já pode emitir ou consultar a NFC-e.' : 'A emissão fica liberada após marcar a OS como Entregue.'}</small>
                                        </div>
                                        <div className="nfce-actions-inline">
                                          <button
                                            type="button"
                                            className="nfce-main-action"
                                            onClick={(e) => handleEmitirNFCe(osDetalhada, e)}
                                            disabled={isFiscalActionLoading(osDetalhada._id) || (!podeEmitirNovaNfce(osDetalhada))}
                                          >
                                            <Icons.Receipt />
                                            {isNotaFiscalEmitida(osDetalhada)
                                              ? getFiscalActionLabel(osDetalhada._id, 'visualizar', 'Abrir NFC-e', 'Abrindo...')
                                              : isNotaFiscalProcessando(osDetalhada)
                                                ? getFiscalActionLabel(osDetalhada._id, 'visualizar', 'Consultar NFC-e', 'Consultando...')
                                                : getFiscalActionLabel(osDetalhada._id, 'visualizar', 'Emitir NFC-e', 'Emitindo...')}
                                          </button>
                                          <button type="button" className="nfce-secondary-action" onClick={(e) => handleBaixarPdfCupom(osDetalhada, e)} disabled={isFiscalActionLoading(osDetalhada._id) || (!isNotaFiscalEmitida(osDetalhada) && !osDetalhada?.notaFiscal?.invoiceId)}>
                                            PDF
                                          </button>
                                          <button type="button" className="nfce-secondary-action" onClick={(e) => handleBaixarXmlCupom(osDetalhada, e)} disabled={isFiscalActionLoading(osDetalhada._id) || (!isNotaFiscalEmitida(osDetalhada) && !osDetalhada?.notaFiscal?.invoiceId)}>
                                            XML
                                          </button>
                                        </div>
                                      </div>
                                    )}

                                    {!editMode && (
                                      <div className="status-quick-bar">
                                         <strong style={{fontSize: 10, color: 'var(--text-sec)', textTransform: 'uppercase', flexShrink: 0}}>Alterar Status Rápidamente:</strong>
                                         <div style={{display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1}}>
                                            {STATUS_PERMITIDOS.map(s => {
                                              const bloqueadoFinanceiro = s === 'Entregue' && calcularRestante(osDetalhada) > 0;

                                              return (
                                                <button
                                                  key={s}
                                                  className={`status-btn ${osDetalhada.status === s ? 'active' : ''} ${bloqueadoFinanceiro ? 'financial-locked' : ''}`}
                                                  title={bloqueadoFinanceiro ? `Quite o saldo de ${formatCurrency(calcularRestante(osDetalhada))} antes de entregar.` : ''}
                                                  onClick={() => handleQuickStatusUpdate(s)}
                                                >
                                                   {s}
                                                </button>
                                              );
                                            })}
                                         </div>
                                      </div>
                                    )}

                                    {/* GRID 3 COLUNAS LADO A LADO NO DESKTOP */}
                                    <div className="details-grid">
                                       
                                       {/* COLUNA 1: CLIENTE E ENDEREÇO */}
                                       <div className="grid-col">
                                          <div className="detail-card">
                                             <button type="button" className="detail-section-toggle" onClick={() => setDetailSections((p) => ({ ...p, cliente: !p.cliente }))}>
                                                <span><Icons.Users /> Dados do Cliente</span><Icons.ChevronDown />
                                             </button>
                                             {detailSections.cliente && (!editMode ? (
                                               <>
                                                 <div className="info-group"><span className="info-label">Nome Completo</span><span className="info-val">{safeRenderName(osDetalhada.cliente)}</span></div>
                                                 <div className="info-row">
                                                     <div className="info-group"><span className="info-label">CPF</span><span className="info-val">{osDetalhada.cliente?.cpf || 'Não informado'}</span></div>
                                                     <div className="info-group"><span className="info-label">Data Nasc.</span><span className="info-val">{formatDateLocal(osDetalhada.cliente?.dataNascimento)}</span></div>
                                                 </div>
                                                 <div className="info-group"><span className="info-label">WhatsApp</span><span className="info-val">{osDetalhada.whatsapp || 'Não informado'}</span></div>
                                                 <div style={{height: 1, background:'var(--border-soft)', margin:'12px 0'}}></div>
                                                 <div className="info-group"><span className="info-label">Endereço</span><span className="info-val">{osDetalhada.endereco ? `${osDetalhada.endereco.logradouro || ''}, ${osDetalhada.endereco.numero || 'S/N'} ${osDetalhada.endereco.complemento ? '('+osDetalhada.endereco.complemento+')' : ''}` : 'Não informado'}</span></div>
                                                 <div className="info-row">
                                                     <div className="info-group"><span className="info-label">Bairro</span><span className="info-val">{osDetalhada.endereco?.bairro || '-'}</span></div>
                                                     <div className="info-group"><span className="info-label">CEP</span><span className="info-val">{osDetalhada.endereco?.cep || '-'}</span></div>
                                                 </div>
                                                 <div className="info-row">
                                                     <div className="info-group"><span className="info-label">Cidade / UF</span><span className="info-val">{osDetalhada.endereco?.cidade || '-'}/{osDetalhada.endereco?.uf || '-'}</span></div>
                                                 </div>
                                               </>
                                             ) : (
                                               <>
                                                 <label className="edit-label">Nome Cliente <input className="edit-input" value={editForm.cliente || ''} onChange={e => setEditForm({...editForm, cliente: e.target.value})} /></label>
                                                 <div style={{display:'flex', gap: 8}}>
                                                     <label className="edit-label" style={{flex:1}}>CPF <input className="edit-input" value={editForm.cpf || ''} onChange={e => setEditForm({...editForm, cpf: e.target.value})} /></label>
                                                     <label className="edit-label" style={{flex:1}}>Nascimento <input type="date" className="edit-input" value={editForm.dataNascimento || ''} onChange={e => setEditForm({...editForm, dataNascimento: e.target.value})} /></label>
                                                 </div>
                                                 <label className="edit-label">WhatsApp <input className="edit-input" value={editForm.whatsapp || ''} onChange={e => setEditForm({...editForm, whatsapp: e.target.value})} /></label>
                                                 <div style={{height: 1, background:'var(--border-soft)', margin:'12px 0'}}></div>
                                                 <div style={{display:'flex', gap: 8}}>
                                                     <label className="edit-label" style={{width: 80}}>CEP <input className="edit-input" value={editForm.cep || ''} onChange={e => setEditForm({...editForm, cep: e.target.value})} /></label>
                                                     <label className="edit-label" style={{flex:1}}>Logradouro <input className="edit-input" value={editForm.endereco || ''} onChange={e => setEditForm({...editForm, endereco: e.target.value})} /></label>
                                                 </div>
                                                 <div style={{display:'flex', gap: 8}}>
                                                     <label className="edit-label" style={{flex:1}}>Número <input className="edit-input" value={editForm.numero || ''} onChange={e => setEditForm({...editForm, numero: e.target.value})} /></label>
                                                     <label className="edit-label" style={{flex:2}}>Complemento <input className="edit-input" value={editForm.complemento || ''} onChange={e => setEditForm({...editForm, complemento: e.target.value})} /></label>
                                                 </div>
                                                 <div style={{display:'flex', gap: 8}}>
                                                    <label className="edit-label" style={{flex:1}}>Bairro <input className="edit-input" value={editForm.bairro || ''} onChange={e => setEditForm({...editForm, bairro: e.target.value})} /></label>
                                                    <label className="edit-label" style={{flex:1}}>Cidade <input className="edit-input" value={editForm.cidade || ''} onChange={e => setEditForm({...editForm, cidade: e.target.value})} /></label>
                                                    <label className="edit-label" style={{width: 50}}>UF <input className="edit-input" value={editForm.uf || ''} onChange={e => setEditForm({...editForm, uf: e.target.value})} /></label>
                                                 </div>
                                               </>
                                             ))}
                                          </div>
                                       </div>

                                       {/* COLUNA 2: PRODUTOS E RECEITUÁRIO (COLUNA DO MEIO MAIS LARGA) */}
                                       <div className="grid-col">
                                          <div className="detail-card">
                                             <div className="detail-section-header">
                                               <button type="button" className="detail-section-toggle" onClick={() => setDetailSections((p) => ({ ...p, produtos: !p.produtos }))}>
                                                 <span><Icons.Eye /> Produtos & Receituário</span><Icons.ChevronDown />
                                               </button>
                                               {editMode && detailSections.produtos && <button onClick={handleAddItem} className="btn-sm btn-wa" style={{padding: '4px 8px'}}><Icons.Plus /> Adicionar</button>}
                                             </div>
                                             {detailSections.produtos && (<>
                                             <div style={{display:'flex', gap: 10, marginBottom: 16, background: 'var(--surface-hover)', padding: 12, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', flexWrap: 'wrap'}}>
                                                 {!editMode ? (
                                                   <>
                                                     <div style={{flex: 1, minWidth: 80}}><span className="info-label">Loja</span><span className="info-val" style={{fontSize:11}}>{os.lojaOficial}</span></div>
                                                     <div style={{flex: 1, minWidth: 100}}><span className="info-label">Vendedor</span><span className="info-val" style={{fontSize:11}}>{os.vendedorOficial}</span></div>
                                                     <div style={{flex: 1, minWidth: 80}}><span className="info-label">Compra</span><span className="info-val" style={{fontSize:11}}>{formatDateLocal(osDetalhada.dataCompra)}</span></div>
                                                     <div style={{flex: 1, minWidth: 80}}><span className="info-label">Entrega</span><span className="info-val" style={{fontSize:11}}>{formatDateLocal(osDetalhada.dataEntrega)}</span></div>
                                                   </>
                                                 ) : (
                                                   <>
                                                     <label className="edit-label" style={{flex:1, minWidth: 140}}>Empresa <select className="edit-input" value={editForm.empresaId || editForm.loja} onChange={e => handleEmpresaEditChange(e.target.value)}>
                                                       <option value="">Selecione a empresa</option>
                                                       {listaEmpresasFiltro.map((empresa) => (
                                                         <option key={empresa.id || empresa.codigo} value={empresa.id || empresa.codigo}>
                                                           {empresa.label}{empresa.fiscalPronta ? '' : ' · fiscal pendente'}
                                                         </option>
                                                       ))}
                                                       {!listaEmpresasFiltro.length && listaLojas.map(l=><option key={l} value={l}>{l}</option>)}
                                                     </select></label>
                                                     <label className="edit-label" style={{flex:1, minWidth: 100}}>Vendedor <select className="edit-input" value={editForm.vendedor} onChange={e => setEditForm({...editForm, vendedor: e.target.value})}>{listaVendedoresEdit.map(v=><option key={v} value={v}>{v}</option>)}</select></label>
                                                     <label className="edit-label" style={{flex:1, minWidth: 100}}>Compra <input type="date" className="edit-input" value={editForm.dataCompra || ''} onChange={e => setEditForm({...editForm, dataCompra: e.target.value})} /></label>
                                                     <label className="edit-label" style={{flex:1, minWidth: 100}}>Entrega <input type="date" className="edit-input" value={editForm.dataEntrega || ''} onChange={e => setEditForm({...editForm, dataEntrega: e.target.value})} /></label>
                                                     <label className="edit-label" style={{flex:1, minWidth: 100}}>Status <select className="edit-input" value={editForm.status} onChange={e => setEditForm({...editForm, status: e.target.value})}>{STATUS_PERMITIDOS.map(s=><option key={s} value={s}>{s}</option>)}</select></label>
                                                   </>
                                                 )}
                                             </div>

                                             <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
                                               {(!editMode ? getOsItems(osDetalhada) : editForm.itens).map((item, index) => {
                                                 const detalheOsId = normalizarId(osDetalhada?._id || osDetalhada?.id);
                                                 const pedidoLabolensItem = getLabolensPedidoItem(
                                                   osDetalhada,
                                                   index,
                                                   labolensPedidosPorOs[detalheOsId] || []
                                                 );
                                                 const statusLabolensItem = pedidoLabolensItem
                                                   ? getLabolensStatusLabel(pedidoLabolensItem.status)
                                                   : '';

                                                 return (
                                                 <div key={index} style={{border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 12, background: 'var(--surface)'}}>
                                                   <div className="item-collapse-header">
                                                     <button type="button" className="item-collapse-toggle" onClick={() => setExpandedItems((p) => ({ ...p, [index]: !p[index] }))}>
                                                       <span>Óculos #{index + 1}</span><Icons.ChevronDown />
                                                     </button>
                                                     {!editMode && (
                                                       <button
                                                         type="button"
                                                         className="labolens-inline-order"
                                                         onClick={(event) => {
                                                           event.stopPropagation();
                                                           abrirPedidoLabolens(osDetalhada, item, index);
                                                         }}
                                                         title="Fazer pedido da lente com a Labolens"
                                                       >
                                                         <span>
                                                           {statusLabolensItem || 'Parceria'}
                                                         </span>
                                                         <img src={labolensLogoPng} alt="Labolens" />
                                                       </button>
                                                     )}
                                                     {editMode && <button onClick={() => handleRemoveItem(index)} className="btn-sm btn-del" style={{padding: '2px 6px'}}><Icons.Trash /> Remover</button>}
                                                   </div>
                                                   {expandedItems[index] && (!editMode ? (
                                                     <>
                                                       <div className="info-row" style={{marginBottom: 8}}>
                                                         <div className="info-group"><span className="info-label">Paciente</span><span className="info-val">{safeRenderName(item.paciente)}</span></div>
                                                         <div className="info-group"><span className="info-label">Código CV</span><span className="info-val" style={{color: 'var(--primary)'}}>{item.produto?.numeroCV || item.produto?.cv || item.produto?.codigoCV || '-'}</span></div>
                                                       </div>
                                                       <div className="info-row" style={{marginBottom: 8}}>
                                                         <div className="info-group"><span className="info-label">Lente</span><span className="info-val">{item.produto?.lente || '-'}</span></div>
                                                         <div className="info-group"><span className="info-label">Armação</span><span className="info-val">{item.produto?.armacao || 'Própria / Cliente'}</span></div>
                                                       </div>
                                                       
                                                       {/* NOVOS DADOS DA ARMAÇÃO NO VIEW MODE */}
                                                       <div className="info-row" style={{marginBottom: 12, background: 'var(--surface-hover)', padding: 8, borderRadius: 8}}>
                                                         <div className="info-group" style={{marginBottom: 0}}><span className="info-label">Logística / Origem</span><span className="info-val">{item.produto?.origemArmacao || 'Ótica'} • {item.produto?.envioArmacao || 'Vai no malote'}</span></div>
                                                         <div className="info-group" style={{marginBottom: 0, display: 'flex', flexDirection: 'column'}}>
                                                            <span className="info-label">Formato</span>
                                                            <span className="info-val" style={{display: 'flex', alignItems: 'center', gap: 6}}>
                                                               {renderShapeIcon(item.produto?.formatoArmacao)} {item.produto?.formatoArmacao || 'N/A'}
                                                            </span>
                                                         </div>
                                                       </div>
                                                       
                                                       <div className="medidas-box" style={{marginBottom: 12}}>
                                                          <span style={{textTransform: 'uppercase', marginRight: 8}}>Medidas (mm):</span>
                                                          <div className="medida-item">Aro: <span className="medida-val">{item.produto?.medidasArmacao?.aro || '-'}</span></div> |
                                                          <div className="medida-item">Ponte: <span className="medida-val">{item.produto?.medidasArmacao?.ponte || '-'}</span></div> |
                                                          <div className="medida-item">Alt: <span className="medida-val">{item.produto?.medidasArmacao?.altura || '-'}</span></div> |
                                                          <div className="medida-item">Diag: <span className="medida-val">{item.produto?.medidasArmacao?.diagonalMaior || '-'}</span></div>
                                                       </div>

                                                       <div style={{marginTop: 12, overflowX: 'auto'}}>
                                                         <table className="grau-table">
                                                             <thead><tr><th>Olho</th><th>Esf</th><th>Cil</th><th>Eixo</th><th>DNP</th><th>Alt</th></tr></thead>
                                                             <tbody>
                                                               <tr><td style={{color:'var(--primary)', fontWeight:800, textAlign:'center', fontSize: 10}}>OD</td><td align="center" style={{fontSize:11}}>{item.receita?.od?.esf||'-'}</td><td align="center" style={{fontSize:11}}>{item.receita?.od?.cil||'-'}</td><td align="center" style={{fontSize:11}}>{item.receita?.od?.eixo||'-'}</td><td align="center" style={{fontSize:11}}>{item.receita?.od?.dnp||'-'}</td><td align="center" style={{fontSize:11}}>{item.receita?.od?.aco||'-'}</td></tr>
                                                               <tr><td style={{color:'#10b981', fontWeight:800, textAlign:'center', fontSize: 10}}>OE</td><td align="center" style={{fontSize:11}}>{item.receita?.oe?.esf||'-'}</td><td align="center" style={{fontSize:11}}>{item.receita?.oe?.cil||'-'}</td><td align="center" style={{fontSize:11}}>{item.receita?.oe?.eixo||'-'}</td><td align="center" style={{fontSize:11}}>{item.receita?.oe?.dnp||'-'}</td><td align="center" style={{fontSize:11}}>{item.receita?.oe?.aco||'-'}</td></tr>
                                                             </tbody>
                                                         </table>
                                                       </div>
                                                       <div style={{marginTop: 10, fontSize: 11, fontWeight: 800, color: 'var(--text-main)', background: 'var(--surface-hover)', padding: '6px 10px', borderRadius: 6, display: 'inline-block'}}>Adição (+): <span style={{color: 'var(--primary)'}}>{item.receita?.adicao || '-'}</span></div>
                                                     </>
                                                   ) : (
                                                     <>
                                                       {/* EDIT MODE: NOVOS CAMPOS */}
                                                       <div style={{display:'flex', gap: 8, flexWrap: 'wrap'}}>
                                                         <label className="edit-label" style={{flex:2, minWidth: 150}}>Paciente <input className="edit-input" value={item.paciente || ''} onChange={e => updateItemField(index, 'paciente', e.target.value)} /></label>
                                                         <label className="edit-label" style={{flex:1, minWidth: 80}}>Cód CV <input className="edit-input" value={item.numeroCV || ''} onChange={e => updateItemField(index, 'numeroCV', e.target.value)} /></label>
                                                       </div>
                                                       <div style={{display:'flex', gap: 8, flexWrap: 'wrap'}}>
                                                         <label className="edit-label" style={{flex:1, minWidth: 120}}>Lente <input className="edit-input" value={item.lente || ''} onChange={e => updateItemField(index, 'lente', e.target.value)} /></label>
                                                         <label className="edit-label" style={{flex:1, minWidth: 120}}>Armação <input className="edit-input" value={item.armacao || ''} onChange={e => updateItemField(index, 'armacao', e.target.value)} /></label>
                                                       </div>

                                                       <div style={{display:'flex', gap: 8, flexWrap: 'wrap'}}>
                                                         <label className="edit-label" style={{flex:1}}>Origem
                                                            <select className="edit-input" value={item.origemArmacao || 'Ótica'} onChange={e => updateItemField(index, 'origemArmacao', e.target.value)}>
                                                               <option value="Ótica">Ótica</option><option value="Própria do Cliente">Própria do Cliente</option>
                                                            </select>
                                                         </label>
                                                         <label className="edit-label" style={{flex:1}}>Logística
                                                            <select className="edit-input" value={item.envioArmacao || 'Vai no malote'} onChange={e => updateItemField(index, 'envioArmacao', e.target.value)}>
                                                               <option value="Vai no malote">Vai no malote</option><option value="Fica na loja (Já na óptica)">Fica na loja</option><option value="Não enviar para lab (Apenas Lente)">Não enviar para lab</option>
                                                            </select>
                                                         </label>
                                                         <label className="edit-label" style={{flex:1}}>Formato
                                                            <select className="edit-input" value={item.formatoArmacao || ''} onChange={e => updateItemField(index, 'formatoArmacao', e.target.value)}>
                                                               <option value="">Selecione...</option>
                                                               {FORMATOS_ARMACAO.map(f => <option key={f} value={f}>{f}</option>)}
                                                            </select>
                                                         </label>
                                                       </div>

                                                       <div style={{display:'flex', gap: 8, flexWrap: 'wrap', background: 'var(--surface-hover)', padding: '10px', borderRadius: 8, border: '1px solid var(--border)', marginTop: 4, marginBottom: 8}}>
                                                         <label className="edit-label" style={{marginBottom: 0, flex: 1}}>Aro<input className="edit-input" value={item.medidasArmacao?.aro || ''} onChange={e => updateMedidaField(index, 'aro', e.target.value)}/></label>
                                                         <label className="edit-label" style={{marginBottom: 0, flex: 1}}>Ponte<input className="edit-input" value={item.medidasArmacao?.ponte || ''} onChange={e => updateMedidaField(index, 'ponte', e.target.value)}/></label>
                                                         <label className="edit-label" style={{marginBottom: 0, flex: 1}}>Altura<input className="edit-input" value={item.medidasArmacao?.altura || ''} onChange={e => updateMedidaField(index, 'altura', e.target.value)}/></label>
                                                         <label className="edit-label" style={{marginBottom: 0, flex: 1}}>Diagonal<input className="edit-input" value={item.medidasArmacao?.diagonalMaior || ''} onChange={e => updateMedidaField(index, 'diagonalMaior', e.target.value)}/></label>
                                                       </div>
                                                       
                                                       <div style={{marginTop: 12, overflowX: 'auto'}}>
                                                         <label className="edit-label">Prescrição (Grau)</label>
                                                         <table className="grau-table">
                                                           <thead><tr><th>Olho</th><th>Esf</th><th>Cil</th><th>Eixo</th><th>DNP</th><th>Alt</th></tr></thead>
                                                           <tbody>
                                                               <tr>
                                                                 <td align="center" style={{fontWeight: 800, color: 'var(--primary)', fontSize: 10}}>OD</td>
                                                                 <td><input className="grau-input" placeholder="0.00" value={item.receita?.od?.esf||''} onChange={e=>updateReceita(index,'od','esf',e.target.value)} /></td>
                                                                 <td><input className="grau-input" placeholder="0.00" value={item.receita?.od?.cil||''} onChange={e=>updateReceita(index,'od','cil',e.target.value)} /></td>
                                                                 <td><input className="grau-input" placeholder="0º" value={item.receita?.od?.eixo||''} onChange={e=>updateReceita(index,'od','eixo',e.target.value)} /></td>
                                                                 <td><input className="grau-input" placeholder="-" value={item.receita?.od?.dnp||''} onChange={e=>updateReceita(index,'od','dnp',e.target.value)} /></td>
                                                                 <td><input className="grau-input" placeholder="-" value={item.receita?.od?.aco||''} onChange={e=>updateReceita(index,'od','aco',e.target.value)} /></td>
                                                               </tr>
                                                               <tr>
                                                                 <td align="center" style={{fontWeight: 800, color: '#10b981', fontSize: 10}}>OE</td>
                                                                 <td><input className="grau-input" placeholder="0.00" value={item.receita?.oe?.esf||''} onChange={e=>updateReceita(index,'oe','esf',e.target.value)} /></td>
                                                                 <td><input className="grau-input" placeholder="0.00" value={item.receita?.oe?.cil||''} onChange={e=>updateReceita(index,'oe','cil',e.target.value)} /></td>
                                                                 <td><input className="grau-input" placeholder="0º" value={item.receita?.oe?.eixo||''} onChange={e=>updateReceita(index,'oe','eixo',e.target.value)} /></td>
                                                                 <td><input className="grau-input" placeholder="-" value={item.receita?.oe?.dnp||''} onChange={e=>updateReceita(index,'oe','dnp',e.target.value)} /></td>
                                                                 <td><input className="grau-input" placeholder="-" value={item.receita?.oe?.aco||''} onChange={e=>updateReceita(index,'oe','aco',e.target.value)} /></td>
                                                               </tr>
                                                           </tbody>
                                                         </table>
                                                         <div style={{marginTop: 8, display: 'flex', alignItems: 'center', gap: 8}}>
                                                             <strong style={{fontSize: 9, color: 'var(--text-sec)', textTransform: 'uppercase'}}>Adição (+):</strong>
                                                             <input className="edit-input" style={{width: 80, marginTop: 0, padding: 6}} placeholder="+0.00" value={item.receita?.adicao||''} onChange={e=>updateAdicao(index, e.target.value)} />
                                                         </div>
                                                       </div>
                                                     </>
                                                   ))}
                                                 </div>
                                                 );
                                               })}
                                             </div>
                                             </>)}
                                          </div>
                                       </div>

                                       {/* COLUNA 3: FINANCEIRO E LOG */}
                                       <div className="grid-col">
                                          <div className="detail-card">
                                             <button type="button" className="detail-section-toggle" onClick={() => setDetailSections((p) => ({ ...p, financeiro: !p.financeiro }))}>
                                                <span><Icons.Dollar /> Financeiro</span><Icons.ChevronDown />
                                             </button>
                                             {detailSections.financeiro && (!editMode ? (
                                               <div className="finance-box">
                                                  <div className="finance-line">
                                                     <span style={{color: 'var(--text-sec)', fontSize: 11, fontWeight: 600}}>Valor Total:</span> 
                                                     <span className="finance-val" style={{fontSize: 14}}>{formatCurrency(osDetalhada.valorTotal)}</span>
                                                  </div>
                                                  <div className="finance-line">
                                                     <span style={{color: '#059669', fontSize: 11, fontWeight: 600}}>Total Pago:</span> 
                                                     <span className="finance-val" style={{color: '#059669', fontSize: 14}}>{formatCurrency(calcularTotalPago(osDetalhada))}</span>
                                                  </div>
                                                  <div style={{height: 1, background:'var(--border)', margin:'10px 0'}}></div>
                                                  <div className="finance-line" style={{color: calcularRestante(osDetalhada) > 0 ? '#EF4444' : '#10B981'}}>
                                                     <span style={{fontSize: 12, fontWeight: 800}}>{calcularRestante(osDetalhada) > 0 ? 'Falta Pagar:' : 'Situação:'}</span> 
                                                     <span className="finance-val">{calcularRestante(osDetalhada) > 0 ? formatCurrency(calcularRestante(osDetalhada)) : 'QUITADO ✓'}</span>
                                                  </div>

                                                  {osDetalhada.pagamentos && osDetalhada.pagamentos.length > 0 && (
                                                     <div style={{marginTop: 12, borderTop: '1px dashed var(--border)', paddingTop: 12}}>
                                                        <span style={{display: 'block', fontSize: 9, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6}}>Histórico Pagamentos</span>
                                                        {osDetalhada.pagamentos.map((pag, idx) => (
                                                          <div key={idx} className="pag-history-item">
                                                             <div>
                                                                <strong style={{fontSize: 11, color: 'var(--text-main)', display: 'block'}}>{pag.metodo} {pag.parcelas > 1 ? `(${pag.parcelas}x)` : ''}</strong>
                                                                <span style={{fontSize: 9, color: 'var(--text-sec)'}}>{formatDateLocal(pag.data)}</span>
                                                             </div>
                                                             <strong style={{fontSize: 12, color: '#059669'}}>{formatCurrency(pag.valor)}</strong>
                                                          </div>
                                                        ))}
                                                     </div>
                                                  )}

                                                  {calcularRestante(osDetalhada) > 0 && (
                                                    <div className="payment-entry-box">
                                                      <span className="payment-entry-title">Adicionar entrada</span>

                                                      <div className="payment-entry-grid">
                                                        <select
                                                          className="edit-input"
                                                          value={novoPagamento.metodo}
                                                          onChange={(e) => setNovoPagamento((prev) => ({ ...prev, metodo: e.target.value }))}
                                                        >
                                                          <option value="PIX">PIX</option>
                                                          <option value="Dinheiro">Dinheiro</option>
                                                          <option value="Débito">Débito</option>
                                                          <option value="Crédito">Crédito</option>
                                                          <option value="Boleto">Boleto</option>
                                                        </select>

                                                        <input
                                                          className="edit-input"
                                                          type="number"
                                                          min="0"
                                                          step="0.01"
                                                          placeholder="Valor"
                                                          value={novoPagamento.valor}
                                                          onChange={(e) => setNovoPagamento((prev) => ({ ...prev, valor: e.target.value }))}
                                                        />

                                                        <input
                                                          className="edit-input"
                                                          type="number"
                                                          min="1"
                                                          step="1"
                                                          placeholder="Parcelas"
                                                          value={novoPagamento.parcelas}
                                                          onChange={(e) => setNovoPagamento((prev) => ({ ...prev, parcelas: e.target.value }))}
                                                        />
                                                      </div>

                                                      <button
                                                        type="button"
                                                        className="btn-add-payment"
                                                        onClick={handleAdicionarPagamento}
                                                        disabled={adicionandoPagamento}
                                                      >
                                                        <Icons.Plus /> {adicionandoPagamento ? 'Salvando entrada...' : `Adicionar até ${formatCurrency(calcularRestante(osDetalhada))}`}
                                                      </button>
                                                    </div>
                                                  )}
                                               </div>
                                             ) : (
                                               <>
                                                  <label className="edit-label">
                                                    Valor Total (R$)
                                                    <input type="number" className="edit-input" value={editForm.valorTotal || ''} onChange={e => setEditForm({...editForm, valorTotal: e.target.value})} style={{fontWeight: 800, fontSize: 18, color: 'var(--primary)'}} />
                                                  </label>
                                                  

                                               </>
                                             ))}
                                          </div>
                                          
                                          {/* Exibe somente a observação digitada durante a criação/edição da venda. */}
                                          {extractObservations(osDetalhada) && (
                                            <div className="detail-card sale-observation">
                                               <h4 className="detail-card-title"><Icons.FileText /> Observação da venda</h4>
                                               <div className="sale-observation-text">
                                                 {extractObservations(osDetalhada)}
                                               </div>
                                            </div>
                                          )}

                                          <div className="detail-card">
                                             <h4 className="detail-card-title"><Icons.List /> Registro de Atividades</h4>
                                             <div className="log-container custom-scrollbar">
                                                {osDetalhada.registros && osDetalhada.registros.length > 0 ? (
                                                   osDetalhada.registros.slice().reverse().map((reg, idx) => (
                                                      <div key={idx} className="log-item">
                                                         <div className="log-date">{new Date(reg.createdAt).toLocaleString('pt-BR')}</div>
                                                         <div className="log-text">{reg.texto}</div>
                                                      </div>
                                                   ))
                                                ) : <div style={{fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic', textAlign: 'center', padding: 16}}>Nenhum registro encontrado.</div>}
                                             </div>
                                          </div>
                                       </div>

                                    </div>

                                    {/* LINHA DE ANEXOS FICA EMBAIXO E PEGA TODA A LARGURA */}
                                    <div className="detail-card" style={{marginTop: '16px'}}>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 12}}>
                                            <h4 className="detail-card-title" style={{margin: 0, border: 'none', padding: 0}}><Icons.Camera /> Galeria de Anexos</h4>
                                            {editMode && (
                                            <div>
                                                <input type="file" id="upload-foto" multiple accept="image/*" style={{display: 'none'}} onChange={handleUploadFotos} disabled={uploadingFotos} />
                                                <label htmlFor="upload-foto" className="btn-sm btn-edit" style={{cursor: uploadingFotos ? 'wait' : 'pointer', opacity: uploadingFotos ? 0.7 : 1}}><Icons.UploadCloud /> {uploadingFotos ? 'Enviando...' : 'Adicionar Fotos'}</label>
                                            </div>
                                            )}
                                        </div>
                                        {uploadingFotos && (
                                          <div style={{ marginBottom: 12, padding: 10, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--surface-hover)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6, fontSize: 11, fontWeight: 800, color: 'var(--text-sec)' }}>
                                              <span>{uploadFotosMensagem || 'Enviando imagens...'}</span>
                                              <span style={{ color: 'var(--primary)' }}>{uploadFotosProgress}%</span>
                                            </div>
                                            <div style={{ height: 6, borderRadius: 999, overflow: 'hidden', background: '#e2e8f0' }}>
                                              <div style={{ width: `${uploadFotosProgress}%`, height: '100%', borderRadius: 999, background: 'linear-gradient(90deg, var(--primary), #22c55e)', transition: 'width .2s ease' }} />
                                            </div>
                                          </div>
                                        )}
                                        <div className="gallery-grid">
                                            {osDetalhada.anexos && osDetalhada.anexos.length > 0 ? osDetalhada.anexos.map((url, i) => (
                                            <div key={i} className="photo-thumb" onClick={(e) => { e.stopPropagation(); setFotoExpandida(url); }}>
                                                {editMode && <button className="btn-remove-foto" onClick={(e) => handleDeleteFoto(e, i)}><Icons.Close /></button>}
                                                <ProtectedImage src={url} alt={`Anexo ${i}`} />
                                            </div>
                                            )) : <div style={{gridColumn:'1/-1', textAlign:'center', color:'var(--text-muted)', padding: '20px', border:'1px dashed var(--border)', borderRadius: 'var(--radius-md)', fontSize: 11}}>Sem documentos ou imagens em anexo.</div>}
                                        </div>
                                    </div>
                                 </>
                              )}
                           </div>
                        )}
                     </div>
                     );
                  })
                )}
             </div>
          </div>

          <div className="pagination-bar">
            <span className="pagination-info">
              Página {paginacao.pagina || paginaAtual} de {Math.max(1, paginacao.totalPaginas)}
            </span>
            <div className="pagination-actions">
              <button
                type="button"
                className="pagination-button"
                onClick={() => irParaPagina(paginaAtual - 1)}
                disabled={!paginacao.temAnterior || loading}
              >
                Anterior
              </button>
              <span className="pagination-current">{paginaAtual} / {Math.max(1, paginacao.totalPaginas)}</span>
              <button
                type="button"
                className="pagination-button"
                onClick={() => irParaPagina(paginaAtual + 1)}
                disabled={!paginacao.temProxima || loading}
              >
                Próxima
              </button>
            </div>
          </div>
        </>
      )}

      {/* ZOOM DE FOTO */}
      {fotoExpandida && (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.95)', zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)'}} onClick={() => setFotoExpandida(null)}>
           <ProtectedImage src={fotoExpandida} style={{maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', objectFit: 'contain'}} alt="Zoom" />
           <div style={{position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.1)', color: '#fff', padding: '10px 16px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 13, border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', backdropFilter: 'blur(4px)'}}><Icons.Close /> Fechar</div>
        </div>
      )}
    </div>
  );
}

// ==================================================================================
// GERADOR DE PDF PARA LISTA/RELATÓRIO DE GESTÃO (SUBSTITUI EXCEL)
// ==================================================================================
const reportStyles = StyleSheet.create({
  page: { padding: 25, fontFamily: 'Helvetica', backgroundColor: '#ffffff' },
  header: { flexDirection: 'row', justifyContent: 'space-between', borderBottomWidth: 1, borderBottomColor: '#cbd5e1', paddingBottom: 15, marginBottom: 20 },
  headerTitle: { fontSize: 16, fontFamily: 'Helvetica-Bold', color: '#0f172a' },
  headerSub: { fontSize: 9, color: '#64748b', marginTop: 4 },
  kpiRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 20, gap: 10 },
  kpiBox: { flex: 1, padding: 10, backgroundColor: '#f8fafc', borderRadius: 4, borderWidth: 1, borderColor: '#e2e8f0' },
  kpiLabel: { fontSize: 8, color: '#64748b', fontFamily: 'Helvetica-Bold', textTransform: 'uppercase' },
  kpiVal: { fontSize: 14, color: '#4f46e5', fontFamily: 'Helvetica-Bold', marginTop: 4 },
  table: { width: '100%', borderWidth: 1, borderColor: '#e2e8f0' },
  tableHeader: { flexDirection: 'row', backgroundColor: '#f1f5f9', borderBottomWidth: 1, borderColor: '#e2e8f0', paddingVertical: 6, paddingHorizontal: 4 },
  tableRow: { flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0', paddingVertical: 6, paddingHorizontal: 4 },
  colOS: { width: '10%' },
  colData: { width: '12%' },
  colCliente: { width: '23%' },
  colVendedor: { width: '15%' },
  colCV: { width: '15%' },
  colValor: { width: '15%', textAlign: 'right' },
  colStatus: { width: '10%', textAlign: 'right' },
  textHeader: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#475569' },
  textRow: { fontSize: 8, color: '#0f172a' },
  footer: { marginTop: 20, fontSize: 8, color: '#94a3b8', textAlign: 'center' }
});

const ReportPdfDocument = ({ data, kpis, periodo }) => (
  <Document>
    <Page size="A4" style={reportStyles.page} orientation="landscape">
      <View style={reportStyles.header}>
        <View>
          <Text style={reportStyles.headerTitle}>Relatório Gerencial de O.S.</Text>
          <Text style={reportStyles.headerSub}>Período filtrado: {periodo}</Text>
        </View>
        <View style={{ alignItems: 'flex-end' }}>
           <Text style={reportStyles.headerSub}>Total de O.S.: {data.length}</Text>
           <Text style={reportStyles.headerSub}>Gerado em: {new Date().toLocaleString('pt-BR')}</Text>
        </View>
      </View>

      <View style={reportStyles.kpiRow}>
        <View style={reportStyles.kpiBox}>
          <Text style={reportStyles.kpiLabel}>Faturamento Total</Text>
          <Text style={reportStyles.kpiVal}>{formatCurrency(kpis.totalRevenue)}</Text>
        </View>
        <View style={reportStyles.kpiBox}>
          <Text style={reportStyles.kpiLabel}>A Receber (Pendentes)</Text>
          <Text style={[reportStyles.kpiVal, { color: '#ef4444' }]}>{formatCurrency(kpis.totalReceber)}</Text>
        </View>
        <View style={reportStyles.kpiBox}>
          <Text style={reportStyles.kpiLabel}>Ticket Médio</Text>
          <Text style={[reportStyles.kpiVal, { color: '#d97706' }]}>{formatCurrency(kpis.avgTicket)}</Text>
        </View>
      </View>

      <View style={reportStyles.table}>
        <View style={reportStyles.tableHeader}>
          <Text style={[reportStyles.textHeader, reportStyles.colOS]}>OS</Text>
          <Text style={[reportStyles.textHeader, reportStyles.colData]}>DATA</Text>
          <Text style={[reportStyles.textHeader, reportStyles.colCliente]}>CLIENTE</Text>
          <Text style={[reportStyles.textHeader, reportStyles.colVendedor]}>VENDEDOR</Text>
          <Text style={[reportStyles.textHeader, reportStyles.colCV]}>CÓDIGO CV</Text>
          <Text style={[reportStyles.textHeader, reportStyles.colValor]}>VALOR TOTAL</Text>
          <Text style={[reportStyles.textHeader, reportStyles.colStatus]}>STATUS</Text>
        </View>
        {data.map((os, i) => {
          const items = getOsItems(os);
          const cvs = items.map(item => item.produto?.numeroCV || item.produto?.cv || item.produto?.codigoCV).filter(Boolean).join(' / ') || 'S/N';
          return (
            <View key={i} style={reportStyles.tableRow}>
              <Text style={[reportStyles.textRow, reportStyles.colOS]}>#{os.numeroOS}</Text>
              <Text style={[reportStyles.textRow, reportStyles.colData]}>{formatDateLocal(os.dataCompra)}</Text>
              <Text style={[reportStyles.textRow, reportStyles.colCliente]}>{safeRenderName(os.cliente)}</Text>
              <Text style={[reportStyles.textRow, reportStyles.colVendedor]}>{os.vendedorOficial || '---'}</Text>
              <Text style={[reportStyles.textRow, reportStyles.colCV]}>{cvs}</Text>
              <Text style={[reportStyles.textRow, reportStyles.colValor]}>{formatCurrency(os.valorTotal)}</Text>
              <Text style={[reportStyles.textRow, reportStyles.colStatus]}>{os.status}</Text>
            </View>
          );
        })}
      </View>
      
      <Text style={reportStyles.footer}>Documento gerado automaticamente pelo Sistema Gerencial Ótica Vizum</Text>
    </Page>
  </Document>
);

// ==================================================================================
// GERADOR DE PDF PROFISSIONAL DAS VIAS
// ==================================================================================

const PdfShapeIcon = ({ shape }) => {
  const color = "#4f46e5";
  const strW = 2.3;

  switch (shape) {
    case 'Redondo':
      return (
        <Svg viewBox="0 0 40 20" width={16} height={8}>
          <Circle cx={10} cy={10} r={7} fill="none" stroke={color} strokeWidth={strW} />
          <Circle cx={30} cy={10} r={7} fill="none" stroke={color} strokeWidth={strW} />
          <Path d="M17 10h6" stroke={color} strokeWidth={strW} />
        </Svg>
      );
    case 'Quadrado':
      return (
        <Svg viewBox="0 0 40 20" width={16} height={8}>
          <Rect x={3} y={3} width={14} height={14} rx={2} fill="none" stroke={color} strokeWidth={strW} />
          <Rect x={23} y={3} width={14} height={14} rx={2} fill="none" stroke={color} strokeWidth={strW} />
          <Path d="M17 10h6" stroke={color} strokeWidth={strW} />
        </Svg>
      );
    case 'Retangular':
      return (
        <Svg viewBox="0 0 40 20" width={16} height={8}>
          <Rect x={2} y={5} width={16} height={10} rx={2} fill="none" stroke={color} strokeWidth={strW} />
          <Rect x={22} y={5} width={16} height={10} rx={2} fill="none" stroke={color} strokeWidth={strW} />
          <Path d="M18 10h4" stroke={color} strokeWidth={strW} />
        </Svg>
      );
    case 'Oval':
      return (
        <Svg viewBox="0 0 40 20" width={16} height={8}>
          <Ellipse cx={10} cy={10} rx={8} ry={6} fill="none" stroke={color} strokeWidth={strW} />
          <Ellipse cx={30} cy={10} rx={8} ry={6} fill="none" stroke={color} strokeWidth={strW} />
          <Path d="M18 10h4" stroke={color} strokeWidth={strW} />
        </Svg>
      );
    case 'Aviador':
      return (
        <Svg viewBox="0 0 40 20" width={16} height={8}>
          <Path d="M3 8c0-3 3-5 7-5 5 0 7 5 7 9 0 4-4 5-7 5-4 0-7-2-7-9z" fill="none" stroke={color} strokeWidth={strW} />
          <Path d="M23 8c0-3 3-5 7-5 5 0 7 5 7 9 0 4-4 5-7 5-4 0-7-2-7-9z" fill="none" stroke={color} strokeWidth={strW} />
          <Path d="M17 6h6" stroke={color} strokeWidth={strW} />
        </Svg>
      );
    case 'Gatinho':
      return (
        <Svg viewBox="0 0 40 20" width={16} height={8}>
          <Path d="M2 6l5-4c4 0 9 4 9 9 0 4-4 5-7 5-4 0-7-3-7-10z" fill="none" stroke={color} strokeWidth={strW} />
          <Path d="M22 6l5-4c4 0 9 4 9 9 0 4-4 5-7 5-4 0-7-3-7-10z" fill="none" stroke={color} strokeWidth={strW} />
          <Path d="M16 8h8" stroke={color} strokeWidth={strW} />
        </Svg>
      );
    default:
      return null;
  }
};

const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};


// Tamanho A4 em pontos PDF: 595.28 x 841.89.
// Usar array evita o visualizador inferir/alternar orientação quando existe apenas uma via.
const PDF_A4_PORTRAIT = [595.28, 841.89];
const VIAS_POR_FOLHA = 3;

// Mantém o mesmo layout original, mas quando a OS tem mais de um produto,
// a via da ótica e a via do cliente viram um resumo simples,
// sem receita: cliente, paciente, produto/lente e armação.
const limitarTextoPdf = (texto = '', max = 320) => {
  const valor = String(texto || '').trim();
  if (valor.length <= max) return valor;
  return `${valor.substring(0, max).trim()}...`;
};

const getViaContainerStyle = (quantidadeViasNaFolha) => {
  if (quantidadeViasNaFolha === 1) {
    return [pdfStyles.viaContainer, pdfStyles.viaContainerUnica];
  }

  if (quantidadeViasNaFolha === 2) {
    return [pdfStyles.viaContainer, pdfStyles.viaContainerDuasVias];
  }

  return pdfStyles.viaContainer;
};

const pdfStyles = StyleSheet.create({
  page: {
    width: PDF_A4_PORTRAIT[0],
    height: PDF_A4_PORTRAIT[1],
    paddingHorizontal: 10,
    paddingTop: 8,
    paddingBottom: 12,
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
    position: 'relative'
  },

  // Área útil da folha A4. Mantém o PDF sempre em retrato e
  // joga as vias dentro da mesma página, sem deixar o conteúdo definir
  // um tamanho estranho para o visualizador de impressão.
  pageContent: {
    width: '100%',
    height: PDF_A4_PORTRAIT[1] - 20,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start'
  },

  viaContainer: {
    paddingTop: 0,
    paddingBottom: 0,
    marginTop: 0,
    marginBottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start'
  },

  // Quando imprimir somente uma via, a folha continua A4 retrato,
  // mas o conteúdo fica concentrado no topo, sem ocupar altura forçada.
  // Assim valor, status e entrega prevista permanecem juntos da via.
  viaContainerUnica: {
    justifyContent: 'flex-start'
  },

  // Quando selecionar duas vias, não força metade da página.
  // As vias ficam empilhadas e coladas, sem espaço vazio entre elas.
  viaContainerDuasVias: {
    justifyContent: 'flex-start'
  },

  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    paddingBottom: 3,
    marginBottom: 3
  },

  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '50%'
  },

  logo: {
    width: 36,
    height: 36,
    objectFit: 'contain',
    marginRight: 6
  },

  companyName: {
    fontSize: 10.5,
    fontFamily: 'Helvetica-Bold',
    color: '#0f172a'
  },

  companyInfoText: {
    fontSize: 6.1,
    color: '#64748b',
    marginTop: 0.5,
    lineHeight: 1.1
  },

  badgeContainer: {
    backgroundColor: '#e2e8f0',
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 4
  },

  badgeText: {
    fontSize: 8.2,
    fontFamily: 'Helvetica-Bold',
    color: '#1e293b',
    textTransform: 'uppercase'
  },

  osNumberBox: {
    alignItems: 'flex-end',
    width: '25%'
  },

  osLabel: {
    fontSize: 6.7,
    color: '#64748b',
    textTransform: 'uppercase',
    marginBottom: 1
  },

  osValue: {
    fontSize: 11.5,
    fontFamily: 'Helvetica-Bold',
    color: '#4f46e5'
  },

  sectionTitle: {
    fontSize: 7.2,
    fontFamily: 'Helvetica-Bold',
    color: '#64748b',
    textTransform: 'uppercase',
    marginBottom: 2,
    marginTop: 2
  },

  gridRow: {
    flexDirection: 'row',
    marginBottom: 2
  },

  label: {
    fontSize: 6.2,
    color: '#64748b',
    marginBottom: 1,
    textTransform: 'uppercase'
  },

  value: {
    fontSize: 7.8,
    fontFamily: 'Helvetica-Bold',
    color: '#0f172a'
  },

  identityHighlight: {
    flexDirection: 'row',
    marginBottom: 3,
    paddingVertical: 3,
    paddingHorizontal: 5,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 4,
    backgroundColor: '#f8fafc'
  },

  identityLabel: {
    fontSize: 6.2,
    color: '#64748b',
    textTransform: 'uppercase',
    marginBottom: 1
  },

  identityValue: {
    fontSize: 8.4,
    fontFamily: 'Helvetica-Bold',
    color: '#0f172a'
  },

  tableContainer: {
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 2
  },

  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0'
  },

  tableHeaderCol: {
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: '#e2e8f0',
    paddingVertical: 1.5,
    textAlign: 'center',
    fontSize: 6.2,
    fontFamily: 'Helvetica-Bold',
    backgroundColor: '#f8fafc',
    color: '#475569'
  },

  tableCol: {
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: '#e2e8f0',
    paddingVertical: 2,
    textAlign: 'center',
    fontSize: 7.2,
    fontFamily: 'Helvetica-Bold',
    color: '#0f172a'
  },

  medidasContainer: {
    flexDirection: 'row',
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 4,
    padding: 3,
    marginTop: 1,
    alignItems: 'center'
  },

  medidasText: {
    fontSize: 6.6,
    fontFamily: 'Helvetica-Bold',
    color: '#475569',
    marginRight: 5
  },

  medidaVal: {
    fontSize: 7.1,
    fontFamily: 'Helvetica-Bold',
    color: '#4f46e5',
    marginRight: 4
  },

  shapeBox: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 'auto',
    paddingLeft: 4,
    borderLeftWidth: 1,
    borderLeftColor: '#cbd5e1'
  },

  obsBox: {
    marginTop: 2,
    padding: 4,
    backgroundColor: '#FEF9C3',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#FEF08A'
  },

  obsText: {
    fontSize: 6.6,
    color: '#854D0E',
    fontStyle: 'italic',
    lineHeight: 1.15
  },

  footerWrapper: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 3,
    marginTop: 2,
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },

  financeBlock: {
    width: '30%'
  },

  financeLabel: {
    fontSize: 6.4,
    color: '#64748b',
    textTransform: 'uppercase'
  },

  financeValue: {
    fontSize: 9.3,
    fontFamily: 'Helvetica-Bold',
    color: '#0f172a'
  },

  financeAlert: {
    fontSize: 7.9,
    fontFamily: 'Helvetica-Bold',
    color: '#ef4444'
  },

  financeSuccess: {
    fontSize: 7.9,
    fontFamily: 'Helvetica-Bold',
    color: '#10b981'
  },

  termsBlock: {
    width: '40%',
    paddingHorizontal: 5
  },

  whatsappText: {
    fontSize: 5.8,
    color: '#475569',
    textAlign: 'justify',
    lineHeight: 1.05
  },

  deliveryText: {
    fontSize: 7.1,
    fontFamily: 'Helvetica-Bold',
    color: '#4f46e5',
    marginTop: 2
  },

  signatureBlock: {
    width: '30%',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingTop: 6
  },

  signatureLine: {
    borderTopWidth: 1,
    borderTopColor: '#0f172a',
    width: '100%',
    marginBottom: 2
  },

  signatureText: {
    fontSize: 6.4,
    color: '#475569'
  },

  cutLineContainer: {
    marginTop: 0,
    marginBottom: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexDirection: 'row',
    alignItems: 'center'
  },

  dashedLine: {
    flex: 1,
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    borderStyle: 'dashed'
  },

  scissorsIcon: {
    marginHorizontal: 4,
    fontSize: 7,
    color: '#94a3b8'
  },

  dataImpressaoRodape: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 2,
    textAlign: 'center',
    fontSize: 4.5,
    color: '#f8fafc'
  }
});

const OsPdfDocument = ({ os, viasConfig }) => {
  const dataFormatada = formatDateLocal(os.dataCompra);
  const dataEntregaFormatada = formatDateLocal(os.dataEntrega);
  const dataImpressaoFormatada = new Date().toLocaleDateString('pt-BR');
  const faltaPagar = calcularRestante(os);
  const observacoes = limitarTextoPdf(extractObservations(os));

  const cpfFormatado = os.cliente?.cpf ? os.cliente.cpf : '---';
  const dataNascFormatada = os.cliente?.dataNascimento ? formatDateLocal(os.cliente.dataNascimento) : '---';

  const end = os.endereco || {};
  const logradouroFull = `${end.logradouro || ''}, ${end.numero || 'S/N'}${end.complemento ? ' - ' + end.complemento : ''}`.trim();
  const bairroStr = end.bairro || '---';
  const cidadeUf = `${end.cidade || '---'}/${end.uf || '-'}`;
  const cepStr = end.cep || '---';

  const emp = os.empresaId || {};
  const logoUrl = emp.logoUrl ? formatImageUrl(emp.logoUrl) : null;
  const nomeEmpresa = emp.nome || 'ÓTICA VIZUM';
  const unidadeEmpresa = emp.unidade || os.lojaOficial || 'Matriz';
  const cnpjEmpresa = emp.cnpj ? `CNPJ: ${emp.cnpj}` : '';
  const enderecoEmpresa = emp.endereco || '';

  const contatosAr = [];
  if (emp.telefone) contatosAr.push(`Tel: ${emp.telefone}`);
  if (emp.whatsapp) contatosAr.push(`Zap: ${emp.whatsapp}`);
  if (emp.email) contatosAr.push(`E-mail: ${emp.email}`);
  const linhaContatos = contatosAr.join(' | ');

  const itensVenda = getOsItems(os);

  const viasToRender = [];

  viasConfig.forEach((via) => {
    const isViaLaboratorio = via.titulo.includes('LABORATÓRIO');

    // A via do laboratório continua exatamente no padrão original: 1 óculos por via.
    if (isViaLaboratorio) {
      if (itensVenda.length === 0) {
        viasToRender.push({
          ...via,
          singleItem: null,
          itensParte: [],
          itemStartIndex: 0,
          parteIndex: 0,
          totalPartes: 1
        });
        return;
      }

      itensVenda.forEach((item, idx) => {
        viasToRender.push({
          ...via,
          titulo: itensVenda.length > 1
            ? `VIA DO LAB (Óculos ${idx + 1}/${itensVenda.length})`
            : 'VIA DO LABORATÓRIO',
          singleItem: item,
          singleItemIndex: idx,
          itensParte: [item],
          itemStartIndex: idx,
          parteIndex: idx,
          totalPartes: itensVenda.length
        });
      });

      return;
    }

    // Via da ótica e via do cliente:
    // Se tiver mais de um produto, mantém tudo em uma única via-resumo,
    // exibindo somente cliente, paciente, produto/lente e armação.
    // A via do laboratório continua separada por óculos, no padrão original.
    const partes = itensVenda.length > 0 ? [itensVenda] : [[]];

    partes.forEach((itensParte, parteIndex) => {
      viasToRender.push({
        ...via,
        singleItem: null,
        itensParte,
        itemStartIndex: 0,
        parteIndex,
        totalPartes: partes.length
      });
    });
  });

  const pages = chunkArray(viasToRender, VIAS_POR_FOLHA);

  return (
    <Document>
      {pages.map((pageVias, pageIndex) => (
        <Page key={pageIndex} size={PDF_A4_PORTRAIT} orientation="portrait" style={pdfStyles.page} wrap={false}>
          <View style={pdfStyles.pageContent} wrap={false}>
            {pageVias.map((via, i) => {
            const itensParaRenderizar = via.singleItem ? [via.singleItem] : (via.itensParte || itensVenda);
            const isViaLaboratorioRender = via.titulo.includes('LAB');
            const isViaResumoMultiproduto = !isViaLaboratorioRender && itensVenda.length > 1;
            const deveExibirReceita = isViaLaboratorioRender;

            const getProdutoResumoPdf = (item = {}) => (
              item.produto?.lente ||
              item.produto?.produto ||
              item.produto?.nome ||
              item.produto?.descricao ||
              '---'
            );

            const getItemLabel = (idx) => {
              if (itensVenda.length <= 1) return '';
              const numeroItem = via.singleItem
                ? (via.singleItemIndex || 0) + 1
                : (via.itemStartIndex || 0) + idx + 1;
              return `(#${numeroItem})`;
            };

            return (
              <View key={i} style={getViaContainerStyle(pageVias.length)} wrap={false}>
                <View>
                  {/* CABEÇALHO */}
                  <View style={pdfStyles.headerRow}>
                    <View style={pdfStyles.headerLeft}>
                      {logoUrl && <Image src={logoUrl} style={pdfStyles.logo} />}
                      <View style={{ flex: 1 }}>
                        <Text style={pdfStyles.companyName}>{nomeEmpresa}</Text>
                        <Text style={[pdfStyles.companyInfoText, { fontFamily: 'Helvetica-Bold' }]}>
                          Unidade: {unidadeEmpresa}
                        </Text>
                        {cnpjEmpresa && <Text style={pdfStyles.companyInfoText}>{cnpjEmpresa}</Text>}
                        {enderecoEmpresa && <Text style={pdfStyles.companyInfoText}>{enderecoEmpresa}</Text>}
                        {linhaContatos && <Text style={pdfStyles.companyInfoText}>{linhaContatos}</Text>}
                      </View>
                    </View>

                    <View style={{ width: '25%', alignItems: 'center' }}>
                      <View style={pdfStyles.badgeContainer}>
                        <Text style={pdfStyles.badgeText}>{via.titulo}</Text>
                      </View>
                    </View>

                    <View style={pdfStyles.osNumberBox}>
                      <Text style={pdfStyles.osLabel}>Ordem de Serviço</Text>
                      <Text style={pdfStyles.osValue}>#{os.numeroOS || 'S/N'}</Text>
                      <Text style={[pdfStyles.companyInfoText, { marginTop: 3, fontFamily: 'Helvetica-Bold' }]}>
                        Data: {dataFormatada}
                      </Text>
                      {via.totalPartes > 1 && !via.titulo.includes('LAB') && (
                        <Text style={[pdfStyles.companyInfoText, { marginTop: 1, color: '#4f46e5', fontFamily: 'Helvetica-Bold' }]}>
                          Parte {via.parteIndex + 1}/{via.totalPartes}
                        </Text>
                      )}
                    </View>
                  </View>

                  {/* DADOS CLIENTE + PRODUTO */}
                  <View style={pdfStyles.gridRow}>
                    {isViaResumoMultiproduto ? (
                      <>
                        <View style={{ width: '42%', paddingRight: 6 }}>
                          <Text style={pdfStyles.sectionTitle}>Dados do Cliente / Comprador</Text>

                          <View style={pdfStyles.gridRow}>
                            <View style={{ flex: 1 }}>
                              <Text style={pdfStyles.label}>Nome do Cliente</Text>
                              <Text style={pdfStyles.value}>{safeRenderName(os.cliente) || '---'}</Text>
                            </View>
                          </View>

                          <View style={pdfStyles.gridRow}>
                            <View style={{ flex: 1 }}>
                              <Text style={pdfStyles.label}>Endereço</Text>
                              <Text style={pdfStyles.value}>{logradouroFull || '---'}</Text>
                            </View>
                          </View>

                          <View style={pdfStyles.gridRow}>
                            <View style={{ flex: 1 }}>
                              <Text style={pdfStyles.label}>Bairro</Text>
                              <Text style={pdfStyles.value}>{bairroStr}</Text>
                            </View>
                            <View style={{ flex: 1 }}>
                              <Text style={pdfStyles.label}>WhatsApp</Text>
                              <Text style={pdfStyles.value}>{os.whatsapp || '---'}</Text>
                            </View>
                          </View>

                          <View style={pdfStyles.gridRow}>
                            <View style={{ flex: 1 }}>
                              <Text style={pdfStyles.label}>Total de Produtos</Text>
                              <Text style={pdfStyles.value}>{itensParaRenderizar.length} óculos</Text>
                            </View>
                          </View>
                        </View>

                        <View style={{ width: '58%', borderLeftWidth: 1, borderLeftColor: '#e2e8f0', paddingLeft: 6 }}>
                          <Text style={pdfStyles.sectionTitle}>Produtos Escolhidos</Text>

                          {itensParaRenderizar.length === 0 && (
                            <Text style={pdfStyles.value}>Nenhum item informado.</Text>
                          )}

                          {itensParaRenderizar.map((item, idx) => (
                            <View
                              key={`resumo-produto-${idx}`}
                              style={{
                                marginBottom: idx < itensParaRenderizar.length - 1 ? 5 : 0,
                                paddingBottom: idx < itensParaRenderizar.length - 1 ? 4 : 0,
                                borderBottomWidth: idx < itensParaRenderizar.length - 1 ? 1 : 0,
                                borderBottomColor: '#e2e8f0'
                              }}
                            >
                              <Text style={[pdfStyles.sectionTitle, { marginBottom: 2 }]}>
                                Óculos {idx + 1}
                              </Text>

                              <View style={pdfStyles.gridRow}>
                                <View style={{ flex: 1 }}>
                                  <Text style={pdfStyles.label}>Paciente</Text>
                                  <Text style={pdfStyles.value}>{safeRenderName(item.paciente) || safeRenderName(os.cliente) || '---'}</Text>
                                </View>
                                <View style={{ flex: 1.25 }}>
                                  <Text style={pdfStyles.label}>Produto / Lente</Text>
                                  <Text style={pdfStyles.value}>{getProdutoResumoPdf(item)}</Text>
                                </View>
                                <View style={{ flex: 1 }}>
                                  <Text style={pdfStyles.label}>Armação</Text>
                                  <Text style={pdfStyles.value}>{item.produto?.armacao || 'Própria'}</Text>
                                </View>
                              </View>
                            </View>
                          ))}
                        </View>
                      </>
                    ) : (
                      <>
                        <View style={{ width: '55%', paddingRight: 6 }}>
                          <Text style={pdfStyles.sectionTitle}>Dados do Cliente / Comprador</Text>

                          <View style={pdfStyles.gridRow}>
                            <View style={{ flex: 2 }}>
                              <Text style={pdfStyles.label}>Nome Completo</Text>
                              <Text style={pdfStyles.value}>{safeRenderName(os.cliente)}</Text>
                            </View>
                            <View style={{ flex: 1 }}>
                              <Text style={pdfStyles.label}>CPF</Text>
                              <Text style={pdfStyles.value}>{cpfFormatado}</Text>
                            </View>
                            <View style={{ flex: 1 }}>
                              <Text style={pdfStyles.label}>Data Nasc.</Text>
                              <Text style={pdfStyles.value}>{dataNascFormatada}</Text>
                            </View>
                          </View>

                          <View style={pdfStyles.gridRow}>
                            <View style={{ flex: 1 }}>
                              <Text style={pdfStyles.label}>Endereço (Logradouro, Nº, Comp)</Text>
                              <Text style={pdfStyles.value}>{logradouroFull || '---'}</Text>
                            </View>
                          </View>

                          <View style={pdfStyles.gridRow}>
                            <View style={{ flex: 1.5 }}>
                              <Text style={pdfStyles.label}>Bairro</Text>
                              <Text style={pdfStyles.value}>{bairroStr}</Text>
                            </View>
                            <View style={{ flex: 1.5 }}>
                              <Text style={pdfStyles.label}>Cidade/UF</Text>
                              <Text style={pdfStyles.value}>{cidadeUf}</Text>
                            </View>
                            <View style={{ flex: 1 }}>
                              <Text style={pdfStyles.label}>CEP</Text>
                              <Text style={pdfStyles.value}>{cepStr}</Text>
                            </View>
                          </View>

                          <View style={pdfStyles.gridRow}>
                            <View style={{ flex: 1 }}>
                              <Text style={pdfStyles.label}>WhatsApp</Text>
                              <Text style={pdfStyles.value}>{os.whatsapp || '---'}</Text>
                            </View>
                            <View style={{ flex: 1 }}>
                              <Text style={pdfStyles.label}>Vendedor Responsável</Text>
                              <Text style={pdfStyles.value}>{os.vendedorOficial || '---'}</Text>
                            </View>
                          </View>
                        </View>

                        <View style={{ width: '45%', borderLeftWidth: 1, borderLeftColor: '#e2e8f0', paddingLeft: 6 }}>
                          {itensParaRenderizar.length === 0 && (
                            <>
                              <Text style={pdfStyles.sectionTitle}>Produto Escolhido</Text>
                              <Text style={pdfStyles.value}>Nenhum item informado.</Text>
                            </>
                          )}

                          {itensParaRenderizar.map((item, idx) => (
                            <React.Fragment key={idx}>
                              <Text style={pdfStyles.sectionTitle}>
                                Produto Escolhido {getItemLabel(idx)}
                              </Text>

                              <View style={pdfStyles.identityHighlight}>
                                <View style={{ flex: 1 }}>
                                  <Text style={pdfStyles.identityLabel}>Cliente / Comprador</Text>
                                  <Text style={pdfStyles.identityValue}>
                                    {safeRenderName(os.cliente) || '---'}
                                  </Text>
                                </View>

                                <View style={{ flex: 1, borderLeftWidth: 1, borderLeftColor: '#cbd5e1', paddingLeft: 6 }}>
                                  <Text style={pdfStyles.identityLabel}>Paciente</Text>
                                  <Text style={pdfStyles.identityValue}>
                                    {safeRenderName(item.paciente) || safeRenderName(os.cliente) || '---'}
                                  </Text>
                                </View>
                              </View>

                              <View style={pdfStyles.gridRow}>
                                <View style={{ flex: 1 }}>
                                  <Text style={pdfStyles.label}>Lente</Text>
                                  <Text style={pdfStyles.value}>{item.produto?.lente || '---'}</Text>
                                </View>
                                <View style={{ flex: 1 }}>
                                  <Text style={pdfStyles.label}>Cód CV</Text>
                                  <Text style={[pdfStyles.value, { color: '#4f46e5' }]}>{item.produto?.numeroCV || item.produto?.cv || item.produto?.codigoCV || '---'}</Text>
                                </View>
                              </View>

                              <View style={pdfStyles.gridRow}>
                                <View style={{ flex: 1 }}>
                                  <Text style={pdfStyles.label}>Armação</Text>
                                  <Text style={pdfStyles.value}>{item.produto?.armacao || 'Própria'}</Text>
                                </View>
                                <View style={{ flex: 1 }}>
                                  <Text style={pdfStyles.label}>Origem / Envio</Text>
                                  <Text style={pdfStyles.value}>
                                    {item.produto?.origemArmacao || 'Ótica'} • {item.produto?.envioArmacao === 'Vai no malote' ? 'Malote' : 'Loja'}
                                  </Text>
                                </View>
                              </View>

                              <View style={pdfStyles.medidasContainer}>
                                <Text style={pdfStyles.medidasText}>MEDIDAS:</Text>
                                <Text style={pdfStyles.label}>Aro: </Text>
                                <Text style={pdfStyles.medidaVal}>{item.produto?.medidasArmacao?.aro || '-'}</Text>

                                <Text style={pdfStyles.label}>Pte: </Text>
                                <Text style={pdfStyles.medidaVal}>{item.produto?.medidasArmacao?.ponte || '-'}</Text>

                                <Text style={pdfStyles.label}>Alt: </Text>
                                <Text style={pdfStyles.medidaVal}>{item.produto?.medidasArmacao?.altura || '-'}</Text>

                                <Text style={pdfStyles.label}>Diag: </Text>
                                <Text style={pdfStyles.medidaVal}>{item.produto?.medidasArmacao?.diagonalMaior || '-'}</Text>

                                <View style={pdfStyles.shapeBox}>
                                  <PdfShapeIcon shape={item.produto?.formatoArmacao} />
                                  <Text style={[pdfStyles.label, { marginLeft: 4 }]}>{item.produto?.formatoArmacao || 'S/N'}</Text>
                                </View>
                              </View>
                            </React.Fragment>
                          ))}
                        </View>
                      </>
                    )}
                  </View>

                  {/* RECEITA */}
                  {deveExibirReceita && itensParaRenderizar.map((item, idx) => (
                    <View key={`rec-${idx}`}>
                      <Text style={pdfStyles.sectionTitle}>
                        Prescrição Médica (Receita) {getItemLabel(idx)}
                      </Text>

                      <View style={pdfStyles.tableContainer}>
                        <View style={pdfStyles.tableRow}>
                          <Text style={pdfStyles.tableHeaderCol}>OLHO</Text>
                          <Text style={pdfStyles.tableHeaderCol}>ESFÉRICO</Text>
                          <Text style={pdfStyles.tableHeaderCol}>CILÍNDRICO</Text>
                          <Text style={pdfStyles.tableHeaderCol}>EIXO</Text>
                          <Text style={pdfStyles.tableHeaderCol}>DNP</Text>
                          <Text style={[pdfStyles.tableHeaderCol, { borderRightWidth: 0 }]}>ALTURA</Text>
                        </View>

                        <View style={pdfStyles.tableRow}>
                          <Text style={[pdfStyles.tableCol, { color: '#4f46e5' }]}>OD</Text>
                          <Text style={pdfStyles.tableCol}>{item.receita?.od?.esf || '-'}</Text>
                          <Text style={pdfStyles.tableCol}>{item.receita?.od?.cil || '-'}</Text>
                          <Text style={pdfStyles.tableCol}>{item.receita?.od?.eixo || '-'}</Text>
                          <Text style={pdfStyles.tableCol}>{item.receita?.od?.dnp || '-'}</Text>
                          <Text style={[pdfStyles.tableCol, { borderRightWidth: 0 }]}>{item.receita?.od?.aco || '-'}</Text>
                        </View>

                        <View style={[pdfStyles.tableRow, { borderBottomWidth: 0 }]}>
                          <Text style={[pdfStyles.tableCol, { color: '#10b981' }]}>OE</Text>
                          <Text style={pdfStyles.tableCol}>{item.receita?.oe?.esf || '-'}</Text>
                          <Text style={pdfStyles.tableCol}>{item.receita?.oe?.cil || '-'}</Text>
                          <Text style={pdfStyles.tableCol}>{item.receita?.oe?.eixo || '-'}</Text>
                          <Text style={pdfStyles.tableCol}>{item.receita?.oe?.dnp || '-'}</Text>
                          <Text style={[pdfStyles.tableCol, { borderRightWidth: 0 }]}>{item.receita?.oe?.aco || '-'}</Text>
                        </View>
                      </View>

                      <Text style={{ fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#0f172a' }}>
                        ADIÇÃO: <Text style={{ fontFamily: 'Helvetica', color: '#4f46e5' }}>{item.receita?.adicao || '-'}</Text>
                      </Text>
                    </View>
                  ))}

                  {/* OBSERVAÇÕES */}
                  <View style={pdfStyles.obsBox}>
                    <Text style={{ fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#ca8a04', marginBottom: 2 }}>
                      OBSERVAÇÕES DA VENDA:
                    </Text>
                    <Text style={pdfStyles.obsText}>{observacoes}</Text>
                  </View>
                </View>

                {/* RODAPÉ */}
                <View style={pdfStyles.footerWrapper}>
                  <View style={pdfStyles.financeBlock}>
                    <View style={{ marginBottom: 3 }}>
                      <Text style={pdfStyles.financeLabel}>Valor da Compra</Text>
                      <Text style={pdfStyles.financeValue}>{formatCurrency(os.valorTotal)}</Text>
                    </View>

                    <View>
                      <Text style={pdfStyles.financeLabel}>Status Financeiro</Text>
                      {faltaPagar > 0 ? (
                        <Text style={pdfStyles.financeAlert}>FALTA: {formatCurrency(faltaPagar)}</Text>
                      ) : (
                        <Text style={pdfStyles.financeSuccess}>PAGO / QUITADO</Text>
                      )}
                    </View>
                  </View>

                  <View style={pdfStyles.termsBlock}>
                    <Text style={pdfStyles.whatsappText}>
                      Declaro estarem corretos os dados de identificação e graduação acima, autorizando a confecção dos óculos.
                      <Text style={{ fontFamily: 'Helvetica-Bold' }}> Autorizo o envio de notificações de status e promoções via WhatsApp.</Text>
                    </Text>
                    <Text style={pdfStyles.deliveryText}>ENTREGA PREVISTA: {dataEntregaFormatada}</Text>
                  </View>

                  <View style={pdfStyles.signatureBlock}>
                    <View style={pdfStyles.signatureLine}></View>
                    <Text style={pdfStyles.signatureText}>{via.signText}</Text>
                  </View>
                </View>

                {i < pageVias.length - 1 && (
                  <View style={pdfStyles.cutLineContainer}>
                    <View style={pdfStyles.dashedLine}></View>
                    <Text style={pdfStyles.scissorsIcon}>✂</Text>
                    <View style={pdfStyles.dashedLine}></View>
                  </View>
                )}
              </View>
            );
            })}
          </View>

          <Text style={pdfStyles.dataImpressaoRodape}>{dataImpressaoFormatada}</Text>
        </Page>
      ))}
    </Document>
  );
};
